import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import useSWR from 'swr';
import { useAuth, API } from "@/App";
import { triggerGlobalSync, useSyncListener } from "@/hooks/useUserData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import PhygitalIcon, { PoweredByPhygital, PhygitalLogo } from "@/components/PhygitalIcon";
import PageMeta from "@/components/PageMeta";
import UnifiedPreferenceCard from "@/components/UnifiedPreferenceCard";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import {
  Calendar,
  Clock,
  MapPin,
  Sparkles,
  ShoppingBag,
  Paintbrush,
  Bookmark,
  Music,
  Heart,
  Check,
  ChevronRight,
  ArrowRight,
  ArrowUp,
  Clock3,
  Wind,
  Brain,
  Activity,
  AlertCircle,
  Coffee,
  Zap,
  Users,
  Eye,
  Smile,
  Accessibility,
  Dog,
  Volume2,
  HandHeart,
  Headphones,
  ListMusic,
  Play,
  Wallet,
  QrCode,
  Download,
  ExternalLink,
  Building2,
  Link2,
  Copy,
  CalendarPlus,
  LayoutDashboard,
  PartyPopper,
  Edit3,
  Utensils,
  X,
  Info,
  Mail,
  Leaf,
  Star,
  Car,
  Train,
  Bike,
  CheckCircle2,
  Plus,
  ChefHat,
  Upload,
  Image,
  Monitor,
  Captions,
  Type,
  HandMetal,
  Briefcase,
  Camera,
  Globe,
  Trash2,
  RotateCcw,
  XCircle,
  Lock,
  GraduationCap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Helper to format time as 12-hour (e.g., "7:00 PM" instead of "19:00")
const formatTime12Hour = (time, timezone) => {
  if (!time) return "";
  
  // If already in 12-hour format with AM/PM, return as-is
  if (time.toUpperCase().includes('AM') || time.toUpperCase().includes('PM')) {
    // Append user's local timezone abbreviation if no timezone in string
    if (timezone) return `${time} ${timezone}`;
    return time;
  }
  
  let hours, minutes;
  if (time.includes(':')) {
    [hours, minutes] = time.split(':').map(Number);
    // Check for NaN
    if (isNaN(hours) || isNaN(minutes)) {
      return time; // Return original if parsing failed
    }
  } else {
    return time;
  }
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const formatted = `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  if (timezone) return `${formatted} ${timezone}`;
  return formatted;
};

// Safe date formatter - never returns "Invalid Date"
const formatSafeDate = (dateStr, opts = { month: "short", day: "numeric", year: "numeric" }) => {
  if (!dateStr) return null;
  const raw = String(dateStr).split('T')[0];
  const [year, month, day] = raw.split('-');
  if (!year || !month || !day || isNaN(Number(year))) return null;
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', opts);
};

// Get user's local timezone abbreviation (e.g., "PST", "EST", "GMT")
const getLocalTimezoneAbbr = () => {
  try {
    return new Date().toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
  } catch { return ''; }
};

// Convert timezone name to abbreviation (e.g., "Pacific Time" → "PST", "America/Los_Angeles" → "PST")
const getTimezoneAbbr = (tzNameOrId) => {
  if (!tzNameOrId) return getLocalTimezoneAbbr();
  // Already an abbreviation (3-4 chars like PST, EST, GMT, AEST)
  if (/^[A-Z]{2,5}$/.test(tzNameOrId)) return tzNameOrId;
  try {
    // Try using Intl to convert IANA timezone to abbreviation
    const abbr = new Date().toLocaleTimeString('en-US', { timeZone: tzNameOrId, timeZoneName: 'short' }).split(' ').pop();
    if (abbr && /^[A-Z]/.test(abbr)) return abbr;
  } catch {}
  // Fallback: common timezone name mappings
  const map = {
    'Pacific Time': 'PT', 'Eastern Time': 'ET', 'Central Time': 'CT', 'Mountain Time': 'MT',
    'Pacific Standard Time': 'PST', 'Eastern Standard Time': 'EST', 'Central Standard Time': 'CST',
    'Pacific Daylight Time': 'PDT', 'Eastern Daylight Time': 'EDT',
    'GMT': 'GMT', 'UTC': 'UTC', 'IST': 'IST', 'BST': 'BST', 'CET': 'CET', 'AEST': 'AEST'
  };
  return map[tzNameOrId] || tzNameOrId;
};

// Dietary options
// Dietary options with icons and colors (matches DefaultsTab styling)
const DIETARY_OPTIONS = [
  { id: "vegetarian", label: "Vegetarian", desc: "No meat or fish", icon: Leaf, color: "bg-green-100 text-green-600" },
  { id: "vegan", label: "Vegan", desc: "No animal products", icon: Leaf, color: "bg-emerald-100 text-emerald-600" },
  { id: "gluten_free", label: "Gluten-free", desc: "No wheat, barley, rye", icon: Utensils, color: "bg-amber-100 text-amber-600" },
  { id: "dairy_free", label: "Dairy-free", desc: "No milk products", icon: Coffee, color: "bg-blue-100 text-blue-600" },
  { id: "halal", label: "Halal", desc: "Islamic dietary requirements", icon: Star, color: "bg-purple-100 text-purple-600" },
  { id: "kosher", label: "Kosher", desc: "Jewish dietary requirements", icon: Star, color: "bg-indigo-100 text-indigo-600" },
  { id: "pescatarian", label: "Pescatarian", desc: "Fish but no meat", icon: Utensils, color: "bg-cyan-100 text-cyan-600" },
  { id: "keto", label: "Keto/Low-carb", desc: "Low carbohydrate diet", icon: Zap, color: "bg-orange-100 text-orange-600" }
];

// Meat preference options (multi-select)
const MEAT_OPTIONS = [
  { id: "chicken", label: "Chicken", icon: Utensils, color: "bg-yellow-100 text-yellow-600" },
  { id: "beef", label: "Beef", icon: Utensils, color: "bg-red-100 text-red-600" },
  { id: "pork", label: "Pork", icon: Utensils, color: "bg-pink-100 text-pink-600" },
  { id: "lamb", label: "Lamb", icon: Utensils, color: "bg-rose-100 text-rose-600" },
  { id: "fish", label: "Fish", icon: Utensils, color: "bg-blue-100 text-blue-600" },
  { id: "shellfish", label: "Shellfish", icon: Utensils, color: "bg-teal-100 text-teal-600" }
];

// Allergy options with icons
const ALLERGY_OPTIONS = [
  { id: "nuts", label: "Tree Nuts", icon: AlertCircle, color: "bg-red-100 text-red-600" },
  { id: "peanuts", label: "Peanuts", icon: AlertCircle, color: "bg-red-100 text-red-600" },
  { id: "shellfish", label: "Shellfish", icon: AlertCircle, color: "bg-red-100 text-red-600" },
  { id: "dairy", label: "Dairy", icon: AlertCircle, color: "bg-orange-100 text-orange-600" },
  { id: "eggs", label: "Eggs", icon: AlertCircle, color: "bg-yellow-100 text-yellow-600" },
  { id: "wheat", label: "Wheat/Gluten", icon: AlertCircle, color: "bg-amber-100 text-amber-600" },
  { id: "soy", label: "Soy", icon: AlertCircle, color: "bg-green-100 text-green-600" },
  { id: "sesame", label: "Sesame", icon: AlertCircle, color: "bg-lime-100 text-lime-600" }
];

// Accessibility options with icons and colors
const ACCESSIBILITY_OPTIONS = [
  { id: "wheelchair", label: "Wheelchair accessible", desc: "Need wheelchair-accessible space", icon: Accessibility, color: "bg-blue-100 text-blue-600" },
  { id: "extra_time", label: "Extra time needed", desc: "May need more time between activities", icon: Clock3, color: "bg-purple-100 text-purple-600" },
  { id: "hearing", label: "Hearing assistance", desc: "May need visual cues or written communication", icon: Headphones, color: "bg-teal-100 text-teal-600" },
  { id: "service_animal", label: "Service animal", desc: "Will have a service animal present", icon: Dog, color: "bg-amber-100 text-amber-600" },
  { id: "fragrance_free", label: "Fragrance-free", desc: "Sensitive to scents and fragrances", icon: Wind, color: "bg-green-100 text-green-600" },
  { id: "limited_mobility", label: "Limited mobility", desc: "May need assistance with positioning", icon: HandHeart, color: "bg-rose-100 text-rose-600" }
];

// Digital accessibility for VIRTUAL events (per Master Plan Section 9: Default 1: ACCESSIBILITY)
const DIGITAL_ACCESSIBILITY_OPTIONS = [
  { id: "screen_reader", label: "Screen Reader", desc: "Using assistive technology for navigation", icon: Monitor, color: "bg-blue-100 text-blue-600" },
  { id: "captions", label: "Captions/Subtitles", desc: "Need captions for audio content", icon: Captions, color: "bg-purple-100 text-purple-600" },
  { id: "color_blind", label: "Color Blindness", desc: "Need color-friendly visuals", icon: Eye, color: "bg-teal-100 text-teal-600" },
  { id: "large_text", label: "Large Text", desc: "Prefer larger font sizes", icon: Type, color: "bg-amber-100 text-amber-600" },
  { id: "adhd_focus", label: "ADHD/Focus Support", desc: "May need breaks or structured content", icon: Brain, color: "bg-green-100 text-green-600" },
  { id: "sign_language", label: "Sign Language", desc: "ASL interpretation needed", icon: HandMetal, color: "bg-rose-100 text-rose-600" }
];

// Invisible disabilities with detailed explanations
const INVISIBLE_DISABILITIES = [
  { id: "vocal_tics", label: "Vocal tics", desc: "I may make sudden sounds or verbal expressions - please continue normally", icon: Activity, color: "bg-violet-100 text-violet-600" },
  { id: "motor_tics", label: "Motor tics", desc: "I may have sudden movements or gestures - please continue normally", icon: Zap, color: "bg-orange-100 text-orange-600" },
  { id: "tourettes", label: "Tourette's Syndrome", desc: "I have a combination of vocal and motor tics - please continue normally", icon: Activity, color: "bg-pink-100 text-pink-600" },
  { id: "anxiety", label: "Anxiety", desc: "I may need extra patience, breaks, or reassurance", icon: Heart, color: "bg-red-100 text-red-600" },
  { id: "chronic_pain", label: "Chronic pain", desc: "I may need to adjust my position frequently", icon: AlertCircle, color: "bg-amber-100 text-amber-600" },
  { id: "chronic_fatigue", label: "Chronic fatigue", desc: "I may need rest breaks during the event", icon: Coffee, color: "bg-cyan-100 text-cyan-600" },
  { id: "adhd", label: "ADHD", desc: "I may need reminders or have difficulty with long sessions", icon: Brain, color: "bg-indigo-100 text-indigo-600" },
  { id: "autism", label: "Autism spectrum", desc: "I may have specific sensory or communication preferences", icon: Sparkles, color: "bg-emerald-100 text-emerald-600" },
  { id: "epilepsy", label: "Epilepsy", desc: "Please be aware of potential seizures - I will share emergency info", icon: Zap, color: "bg-yellow-100 text-yellow-600" }
];

// Interest tags - Dynamic based on event pilot_category
// Per Master Plan Section 15: Default 10: INTERESTS = industries[], passions[], networking_goals[]

// Default interests for Events (HTHF-style)
const EVENT_INTEREST_OPTIONS = [
  "AI and Machine Learning",
  "Fashion Tech",
  "Sustainable Fashion",
  "Wearable Tech",
  "Digital Fashion",
  "Networking",
  "Investing",
  "Art and Design",
  "Storytelling",
  "Entrepreneurship"
];

// Interests for Instructor/Builder Pilot (per Master Plan Section 25)
const INSTRUCTOR_INTEREST_OPTIONS = [
  "Product Development",
  "Fundraising & Investors",
  "Go-to-Market Strategy",
  "Engineering & Development",
  "Design & UX",
  "Community Building",
  "Content & Marketing",
  "Operations & Scaling",
  "AI & Automation",
  "Leadership & Team Building"
];

// Helper to get interests based on event pilot_category
const getInterestOptions = (event) => {
  if (!event) return EVENT_INTEREST_OPTIONS;
  
  // First check for event-specific interest_categories (admin-configured)
  if (event.intake_settings?.interest_categories?.length > 0) {
    return event.intake_settings.interest_categories;
  }
  
  // Then check pilot_category to determine which defaults to use
  const pilotCategory = event.pilot_category?.toLowerCase();
  if (pilotCategory === 'instructor' || pilotCategory === 'instructors' || pilotCategory === 'workshop' || pilotCategory === 'builder') {
    return INSTRUCTOR_INTEREST_OPTIONS;
  }
  
  // Default to event interests
  return EVENT_INTEREST_OPTIONS;
};

// Legacy constant for backwards compatibility (prefer getInterestOptions)
const INTEREST_OPTIONS = EVENT_INTEREST_OPTIONS;

// Vibe options
const VIBE_OPTIONS = [
  { id: "social", label: "Very social", desc: "Ready to meet everyone", icon: Users, color: "bg-pink-100 text-pink-600" },
  { id: "selective", label: "Selectively social", desc: "Quality conversations", icon: Smile, color: "bg-purple-100 text-purple-600" },
  { id: "observing", label: "Observing", desc: "Taking it all in", icon: Eye, color: "bg-blue-100 text-blue-600" },
  { id: "chill", label: "Just vibing", desc: "Here for the experience", icon: Sparkles, color: "bg-amber-100 text-amber-600" }
];

// Helper function to ensure URLs are absolute (have protocol)
const ensureAbsoluteUrl = (url) => {
  if (!url) return null;
  // If it already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }
  // Otherwise, add https://
  return `https://${url}`;
};

// Ways to make your experience yours - Preview cards
import { TAP_IN_COVER, TAP_IN_LOGO, getTapInConfig } from "@/lib/integrationConfig";
// Tap In cover and logo centralized in integrationConfig.js
const NOTION_COVER = "https://customer-assets.emergentagent.com/job_profile-defaults/artifacts/hirdla0h_notioncover.png";
const CANVA_COVER = "https://customer-assets.emergentagent.com/job_profile-defaults/artifacts/09sdvm5g_canvacover.png";
const PARTIFUL_COVER = "https://customer-assets.emergentagent.com/job_profile-defaults/artifacts/1wplgxxu_partifulcover.png";
const POSH_VIP_COVER = "https://customer-assets.emergentagent.com/job_profile-defaults/artifacts/vuaay600_poshvipcover.png";

// Role options for attendees
const ROLE_OPTIONS = [
  { id: "entrepreneur", label: "Entrepreneur" },
  { id: "investor", label: "Investor" },
  { id: "executive", label: "Executive" },
  { id: "fashion_designer", label: "Fashion Designer" },
  { id: "creative_director", label: "Creative Director" },
  { id: "artist", label: "Artist" },
  { id: "engineer", label: "Engineer" },
  { id: "marketing", label: "Marketing" },
  { id: "pr_media", label: "PR/Media" },
  { id: "content_creator", label: "Content Creator" },
  { id: "student", label: "Student" },
  { id: "other", label: "Other" }
];

const EXPERIENCE_PREVIEWS = [
  { 
    id: "access", 
    name: "Accessibility", 
    desc: "Accommodations and needs",
    icon: Accessibility,
    color: "from-blue-400 to-blue-600"
  },
  { 
    id: "dietary", 
    name: "Dietary", 
    desc: "Food preferences",
    icon: Coffee,
    color: "from-green-400 to-emerald-500"
  },
  { 
    id: "tap_in", 
    name: "Tap In", 
    desc: "Add a song to the session playlist",
    image: TAP_IN_COVER,
    color: "from-purple-500 to-pink-500"
  },
  { 
    id: "interests", 
    name: "Interests", 
    desc: "What brings you here",
    icon: Heart,
    color: "from-rose-400 to-pink-500"
  },
  { 
    id: "vibe", 
    name: "Vibe", 
    desc: "Your energy tonight",
    icon: Sparkles,
    color: "from-amber-400 to-orange-500"
  }
];

// Full experience apps for step 3
const EXPERIENCE_APPS = [
  { 
    id: "tap_in", 
    name: "Tap In", 
    desc: "Add a song to the session playlist",
    icon: TAP_IN_COVER,
    color: "from-blue-500 to-purple-600",
    hasPlaylist: true
  },
  { 
    id: "vip_intro", 
    name: "VIP Intros", 
    desc: "Get connected with people who share your interests",
    subDesc: "Industry, goals, and networking preferences",
    iconComponent: Users,
    color: "from-amber-400 to-orange-500"
  }
];


// Pre-Invite Form Component
const PreInviteForm = ({ event, user }) => {
  // Iter 9.47.k - copy sourced from Airtable `surface_waitlist`.
  const { slotMap: w } = useUiSurface('surface_waitlist');
  // State for fetching user defaults
  const [userDefaults, setUserDefaults] = useState(null);
  
  // Location dropdown state
  const [configuredLocs, setConfiguredLocs] = useState([]);
  const [locSearch, setLocSearch] = useState("");
  const [showLocDropdown, setShowLocDropdown] = useState(false);
  const [apiCityResults, setApiCityResults] = useState([]);
  
  // Fetch user defaults and configured locations on mount
  useEffect(() => {
    const fetchDefaults = async () => {
      if (!user?.user_id) return;
      try {
        const response = await fetch(`${API}/user/defaults`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUserDefaults(data.defaults);
        }
      } catch (err) {
        console.log("Could not fetch user defaults");
      }
    };
    fetchDefaults();
    
    // Fetch configured locations if collect_location is enabled
    if (event?.intake_settings?.collect_location) {
      const fetchLocs = async () => {
        try {
          const res = await fetch(`${API}/configured-locations`);
          if (res.ok) {
            const data = await res.json();
            setConfiguredLocs(data.locations || []);
          }
        } catch (err) {
          console.log("Could not fetch locations");
        }
      };
      fetchLocs();
    }
  }, [user?.user_id, event?.intake_settings?.collect_location]);
  
  // Close dropdown on outside click
  useEffect(() => {
    if (!showLocDropdown) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="pre-invite-location-wrap"]')) {
        setShowLocDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLocDropdown]);
  
  // Prefill from logged-in user's profile + saved defaults
  const [formData, setFormData] = useState({
    first_name: user?.name?.split(' ')[0] || '',
    last_name: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    company: user?.company || '',
    title: user?.title || '',
    city: user?.city || '',
    interests: []
  });
  
  // Update interests when userDefaults loads
  useEffect(() => {
    if (userDefaults?.events?.interests?.length > 0) {
      setFormData(prev => ({
        ...prev,
        interests: userDefaults.events.interests
      }));
    }
  }, [userDefaults]);
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const preInviteConfig = event?.pre_invite_mode || {};
  const fields = preInviteConfig.fields || {
    first_name: { enabled: true, required: true, label: "First Name" },
    last_name: { enabled: true, required: true, label: "Last Name" },
    email: { enabled: true, required: true, label: "Email" },
    interests: { enabled: true, required: false, label: "What interests you?" },
    company: { enabled: true, required: false, label: "Company" },
    title: { enabled: true, required: false, label: "Title" }
  };
  
  // Get interests from event's configured interest_categories (from host settings)
  // Priority: intake_settings.interest_categories > interest_categories > categories > defaults
  const interestOptions = event?.intake_settings?.interest_categories || 
                          event?.interest_categories?.map(c => c.name || c) || 
                          event?.interests_options || 
                          event?.categories || 
                          ["Fashion", "Technology", "Art & Design", "Business", "Music", "Investing"];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/inner-circle/pre-invite/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pilot_id: event.slug || event.event_id,
          email: formData.email,
          name: `${formData.first_name} ${formData.last_name}`.trim(),
          company: formData.company || null,
          title: formData.title || null,
          city: formData.city || null,
          interests: formData.interests || [],
          source: 'direct'
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        const successMsg = typeof data.message === 'string' ? data.message : 
                          (preInviteConfig.success_message || "You're on the list!");
        toast.success(successMsg);
      } else {
        const errorMsg = typeof data.detail === 'string' ? data.detail : "Something went wrong. Please try again.";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Pre-invite signup error:", error);
      toast.error("Unable to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  const toggleInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };
  
  if (submitted) {
    return (
      <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-testid="pre-invite-joined-title">{slotText(w, 'joined_title', "You're on the list!")}</h3>
        <p className="text-sm text-gray-600" data-testid="pre-invite-joined-body">
          {preInviteConfig.success_message || slotText(w, 'pre_invite_success_default', "We'll notify you when RSVPs open.")}
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 p-5" data-testid="pre-invite-form">
      <div className="text-center mb-4">
        <h3 className="text-base font-semibold text-gray-900" data-testid="pre-invite-cta-title">
          {preInviteConfig.cta_text || slotText(w, 'pre_invite_default_cta', "Get Early Access")}
        </h3>
        <p className="text-xs text-gray-500 mt-1" data-testid="pre-invite-subtitle">{slotText(w, 'pre_invite_subtitle', 'Be the first to know when RSVPs open')}</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Name Fields */}
        {(fields.first_name?.enabled || fields.last_name?.enabled) && (
          <div className="grid grid-cols-2 gap-2">
            {fields.first_name?.enabled && (
              <div>
                <Input
                  type="text"
                  placeholder={fields.first_name.label || "First Name"}
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  required={fields.first_name.required}
                  className="text-sm"
                  data-testid="pre-invite-first-name"
                />
              </div>
            )}
            {fields.last_name?.enabled && (
              <div>
                <Input
                  type="text"
                  placeholder={fields.last_name.label || "Last Name"}
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  required={fields.last_name.required}
                  className="text-sm"
                  data-testid="pre-invite-last-name"
                />
              </div>
            )}
          </div>
        )}
        
        {/* Email */}
        {fields.email?.enabled && (
          <Input
            type="email"
            placeholder={fields.email.label || "Email"}
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            required={fields.email.required}
            className="text-sm"
            data-testid="pre-invite-email"
          />
        )}
        
        {/* Company & Title */}
        {(fields.company?.enabled || fields.title?.enabled) && (
          <div className="grid grid-cols-2 gap-2">
            {fields.company?.enabled && (
              <Input
                type="text"
                placeholder={fields.company.label || "Company"}
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                required={fields.company.required}
                className="text-sm"
                data-testid="pre-invite-company"
              />
            )}
            {fields.title?.enabled && (
              <Input
                type="text"
                placeholder={fields.title.label || "Title"}
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required={fields.title.required}
                className="text-sm"
                data-testid="pre-invite-title"
              />
            )}
          </div>
        )}
        
        {/* Location - searchable dropdown with all cities */}
        {event?.intake_settings?.collect_location && (
          <div className="relative" data-testid="pre-invite-location-wrap">
            <div className="relative">
              <MapPin className="w-3.5 h-3.5 text-blue-500 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
              <input
                type="text"
                value={locSearch || formData.city}
                onChange={(e) => {
                  const val = e.target.value;
                  setLocSearch(val);
                  setFormData(prev => ({ ...prev, city: val }));
                  setShowLocDropdown(true);
                  // Fetch cities from API when 2+ chars
                  if (val.length >= 2) {
                    fetch(`${API}/cities/search?q=${encodeURIComponent(val)}`)
                      .then(r => r.json())
                      .then(d => setApiCityResults(d.cities || []))
                      .catch(() => setApiCityResults([]));
                  } else {
                    setApiCityResults([]);
                  }
                }}
                onFocus={() => setShowLocDropdown(true)}
                placeholder="City"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300"
                data-testid="pre-invite-city"
              />
            </div>
            {showLocDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {/* GTM Target Cities - shown first as suggested when no search */}
                {!locSearch && configuredLocs.filter(l => l.status === 'active').length > 0 && (
                  <div className="px-3 py-1.5 bg-blue-50 border-b border-blue-100">
                    <span className="text-[10px] font-medium text-blue-600 uppercase tracking-wider">Recommended</span>
                  </div>
                )}
                {!locSearch && configuredLocs
                  .filter(loc => loc.status === 'active')
                  .map((loc, idx) => (
                    <button
                      key={`gtm-${idx}`}
                      type="button"
                      onClick={() => {
                        const val = loc.country !== "USA" ? loc.full_label : loc.label;
                        setFormData(prev => ({ ...prev, city: val }));
                        setLocSearch(val);
                        setShowLocDropdown(false);
                        setApiCityResults([]);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between bg-blue-50/30"
                    >
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 flex-shrink-0 text-blue-500" />
                        <span>{loc.label}</span>
                      </span>
                      <span className="text-[10px] text-blue-500">Recommended</span>
                    </button>
                  ))
                }
                {/* API city results when searching */}
                {locSearch && apiCityResults.length > 0 && apiCityResults.map((city, idx) => (
                  <button
                    key={`api-${idx}`}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, city: city.label }));
                      setLocSearch(city.label);
                      setShowLocDropdown(false);
                      setApiCityResults([]);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <MapPin className="w-3 h-3 flex-shrink-0 text-gray-400" />
                      <span>{city.label}</span>
                    </span>
                    {city.country !== "US" && (
                      <span className="text-[10px] text-gray-400">{city.country}</span>
                    )}
                  </button>
                ))}
                {/* Fallback: configured locs matching search */}
                {locSearch && apiCityResults.length === 0 && configuredLocs
                  .filter(loc => {
                    const q = locSearch.toLowerCase();
                    return loc.city.toLowerCase().includes(q) || loc.state?.toLowerCase().includes(q) || loc.country?.toLowerCase().includes(q);
                  })
                  .map((loc, idx) => (
                    <button
                      key={`cfg-${idx}`}
                      type="button"
                      onClick={() => {
                        const val = loc.country !== "USA" ? loc.full_label : loc.label;
                        setFormData(prev => ({ ...prev, city: val }));
                        setLocSearch(val);
                        setShowLocDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 flex-shrink-0 text-gray-400" />
                        <span>{loc.label}</span>
                      </span>
                    </button>
                  ))
                }
                {locSearch && apiCityResults.length === 0 && configuredLocs.filter(loc => {
                  const q = locSearch.toLowerCase();
                  return loc.city.toLowerCase().includes(q) || loc.state?.toLowerCase().includes(q) || loc.country?.toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-400 text-center">
                    No matches found
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Interests */}
        {fields.interests?.enabled && (
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">{fields.interests.label || "What interests you?"}</p>
            <div className="flex flex-wrap gap-1.5">
              {interestOptions.slice(0, 8).map((interest) => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => toggleInterest(interest)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                    formData.interests.includes(interest)
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {interest}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-gray-900 hover:bg-gray-800 text-white py-2.5 text-sm rounded-full"
          data-testid="pre-invite-submit"
        >
          {submitting ? "Submitting..." : preInviteConfig.cta_text || "Get Early Access"}
        </Button>
      </form>
      
      <p className="text-[10px] text-gray-400 text-center mt-3">
        Your profile stays with you on Phygital
      </p>
    </div>
  );
};

// Fallback logo for events without custom logo
const DEFAULT_EVENT_LOGO = "https://customer-assets.emergentagent.com/job_expflow-1/artifacts/psk240kv_4.png";

// Helper to get full image URL
const getImageUrl = (path, fallback = DEFAULT_EVENT_LOGO) => {
  if (!path) return fallback;
  if (path.startsWith('http')) return path;
  // Path already includes /api/ prefix, use BACKEND_URL directly
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  return `${baseUrl}${path}`;
};

// Dynamic Event Header that uses event data
// Logo is rendered WITHOUT a black border/box - uses transparent background by default
const EventHeader = ({ event, showDescription = false }) => {
  const logoUrl = getImageUrl(event?.logo_image || event?.cover_image);
  // Only apply background color if explicitly set by admin (not default black)
  const hasCustomBg = event?.logo_background_color && event.logo_background_color !== "#000000";
  const bgColor = hasCustomBg ? event.logo_background_color : "transparent";
  const logoPadding = event?.logo_padding || "0px";
  
  return (
    <div className="text-center mb-2">
      <div 
        className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center mb-1"
        style={{ backgroundColor: bgColor, padding: logoPadding }}
      >
        <img 
          src={logoUrl}
          alt={event?.title || event?.name || ""}
          className="w-full h-full object-contain"
        />
      </div>
      <h1 className="text-base font-semibold text-gray-900">{event?.title || event?.name || ""}</h1>
      <p className="text-[11px] text-gray-600">
        {event?.date_tba ? "Date TBA" : (formatSafeDate(event?.date) || "")}
        {event?.timezone_display && ` • ${event.timezone_display}`}
        {(event?.city || event?.location) && ` • ${event.city || event.location}`}
      </p>
      {showDescription && event?.description && (
        <p 
          className="text-gray-600 mt-2 max-w-xs mx-auto leading-relaxed"
          style={{ fontSize: `${parseInt(event?.display_settings?.description_font_size) || 12}px` }}
        >
          {event.description}
        </p>
      )}
    </div>
  );
};

const EventPageElegant = ({ instructorMode = false }) => {
  const { eventSlug, eventId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  // Iter 9.47.l - public Event-page banner copy from Airtable.
  const { slotMap: eventPageSlots } = useUiSurface('surface_event_page');
  
  // Instructor invite code for instructor intake flow
  const instructorInviteCode = searchParams.get('code');
  const isInstructorIntake = instructorMode || !!instructorInviteCode;
  
  // Check for VIP invite link - support all VIP code formats
  const vipCode = searchParams.get('vip');
  const isVipInvite = vipCode && (
    vipCode.startsWith('gi_') ||  // Guest invite
    vipCode.startsWith('p2_') ||  // Plus two
    vipCode.startsWith('lh_') ||  // List host
    vipCode.startsWith('In_') ||  // Inner circle / legacy format
    vipCode.length >= 6           // Any code with 6+ chars could be valid
  );
  
  // Check for filtered view mode (shareable links for specific intake sections)
  // e.g., ?view=dietary, ?view=accessibility, ?view=interests
  const viewFilter = searchParams.get('view');
  const validViewFilters = ['dietary', 'accessibility', 'interests', 'speaker', 'instructor', 'all'];
  const activeViewFilter = validViewFilters.includes(viewFilter) ? viewFilter : null;
  
  // Listen for sync events to refresh data
  useSyncListener();
  
  // SWR fetcher for event data with auto-revalidation
  const fetcher = (url) => axios.get(url).then(res => res.data);
  const effectiveEventSlug = eventSlug || eventId; // Support both /e/:slug and /instructor-intake/:eventId
  const { data: swrEvent, error: swrError, mutate: mutateEvent } = useSWR(
    effectiveEventSlug ? `${API}/events/${effectiveEventSlug}` : null,
    fetcher,
    { 
      refreshInterval: 10000, // Refresh every 10 seconds for real-time sync
      revalidateOnFocus: true,
      onError: (err) => {
        console.error("Event fetch error:", err);
        setLoading(false);
      }
    }
  );
  
  const [event, setEvent] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnvelope, setShowEnvelope] = useState(true);
  const [envelopeEnabled, setEnvelopeEnabled] = useState(true); // From event settings
  const [envelopeOpened, setEnvelopeOpened] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [existingRsvp, setExistingRsvp] = useState(null);
  const [cancelledRsvp, setCancelledRsvp] = useState(null); // For Re-RSVP button
  const [isEventHost, setIsEventHost] = useState(false); // Is user a host/cohost for this event?
  const [dynamicBranding, setDynamicBranding] = useState(null); // Dynamic branding from template/pilot/event
  const [dynamicContent, setDynamicContent] = useState(null); // Dynamic content from template/pilot (intake, welcome, confirmation)
  const [categoryPublicFeatures, setCategoryPublicFeatures] = useState(["rsvp"]); // R-public-features gate
  const [hostInvite, setHostInvite] = useState(null); // Amayah-style native invite when ?invite_token=...
  const [rsvpPrefill, setRsvpPrefill] = useState(null); // /e/{slug}?invite=inv_* prefill (Acuity / host invite-client)
  const [selectedInviteSlot, setSelectedInviteSlot] = useState(null);
  const [requestAlternateTime, setRequestAlternateTime] = useState(false);
  const [preferences, setPreferences] = useState({
    accessibility_needs: [],
    invisible_disabilities: [],
    disability_notes: "",
    dietary_restrictions: [],
    meat_preferences: [],
    allergies: [],
    dietary_notes: "",
    experience_apps: [],
    interests: [],
    vibe: "",
    additional_notes: "",
    transportation: "",  // "rideshare", "driving", "public_transit", "other"
    transportation_notes: "",
    question_for_instructor: "",  // For instructor pilot question bank
    question_recipient: "instructor",  // Who should see: 'instructor', 'emergent', or 'both'
    // 3 separate question fields per Master Plan Section 15
    question_1: "",
    question_1_recipient: "instructor",
    question_2: "",
    question_2_recipient: "platform",
    question_3: "",
    question_3_recipient: "both",
    // Provider-specific preferences
    inspo: [],  // Array of inspo objects with source tracking
    skin_type: "",
    skin_concerns: [],
    makeup_allergies: [],
    special_requests: "",
    occasion: "",
    guest_count: "",
    // Instructor/Workshop - Audience Preferences (per Master Plan Section 15: Default 11: AUDIENCE)
    audience_experience_level: "",  // beginner, intermediate, advanced
    audience_learning_style: "",    // visual, auditory, hands_on, reading
    audience_pace_preference: "",   // fast, moderate, slow
    audience_interaction_pref: "",  // questions, listen, small_groups, chat
    audience_group_pref: "",        // solo, small group, large group, any
    audience_goals: [],             // what they want to learn
    // Instructor/Workshop - Builder Profile (per Master Plan Section 25)
    builder_role: "",               // founder, engineer, designer, etc.
    builder_stage: "",              // exploring, idea, building, launched, scaling
    builder_current_build: "",      // what they're building
    builder_project_url: "",        // link to their project
    builder_biggest_blocker: ""     // biggest challenge
  });
  const [hostCuratedInspo, setHostCuratedInspo] = useState([]); // Inspo the host has curated for this client
  const [loadingHostInspo, setLoadingHostInspo] = useState(false);
  // Acuity-bound private host inspo (visible if rsvp email matches a host's Acuity client)
  const [acuityBoundInspo, setAcuityBoundInspo] = useState([]);
  // Iter 9.40 Pass C - dynamic intake_order from db.categories (admin-controlled)
  // Map: { [category_id]: { intake_order: [...], universal_defaults_enabled: [...] } }
  const [dynamicIntakeMeta, setDynamicIntakeMeta] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/preferences/schema`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const map = {};
        (data.categories || []).forEach((c) => {
          map[c.category_id] = {
            intake_order: c.intake_order || [],
            universal_defaults_enabled: c.universal_defaults_enabled || [],
          };
        });
        setDynamicIntakeMeta(map);
      } catch { /* silent - fallback to hardcoded CATEGORY_DEFAULTS */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [isAcuityMatch, setIsAcuityMatch] = useState(false);
  const [loadingAcuityInspo, setLoadingAcuityInspo] = useState(false);
  const [selectedService, setSelectedService] = useState(null); // Service chosen by client
  const [availableServices, setAvailableServices] = useState([]); // Host's services list
  const [serviceSettings, setServiceSettings] = useState({ show_price: true, location_visibility: "show" });
  const [linkedExperiences, setLinkedExperiences] = useState([]); // Hair, nails, outfit links
  const [newLinkedExp, setNewLinkedExp] = useState({ category: "", provider_name: "", notes: "" });
  const [expandedCategory, setExpandedCategory] = useState(null); // For Full Picture dropdown
  // Iter 9.47.v - Wedding intake conditional rendering
  const [weddingIntake, setWeddingIntake] = useState({ applies: false, fields: [] });
  const [weddingAnswers, setWeddingAnswers] = useState({});
  const [weddingExpanded, setWeddingExpanded] = useState(true);
  // Iter 9.48d - Service tag conditional intake (bridal / instructor / travel)
  const [tagIntake, setTagIntake] = useState({ applies: false, groups: [] });
  const [tagAnswers, setTagAnswers] = useState({});
  const [accountData, setAccountData] = useState({ 
    first_name: "",
    last_name: "",
    name: "", // computed from first + last
    email: "", 
    phone: "",
    city: "",
    company: "",
    title: "",
    roles: [],
    other_role: "",
    instagram: "",
    tiktok: "",
    linkedin: "",
    website: "",  // Per Master Plan: social links include website
    add_to_list: false,
    beehiiv_consent: false,
    headshot_url: "",  // Per Master Plan: Personal info includes headshot
    bio: ""  // Per Master Plan: Personal info includes bio
  });
  
  // Instructor-specific data (for instructor intake flow) - Per Master Plan Section 25
  const [instructorData, setInstructorData] = useState({
    session_title: "",
    session_description: "",
    session_topics: [],
    session_format: "Workshop",
    session_length: "1hr",
    key_learnings: "",
    questions_for_audience: "",
    resources: [],
    social_links: {
      linkedin: "",
      twitter: "",
      website: ""
    },
    project_name: "",
    project_description: "",
    project_url: "",
    project_stage: ""
  });
  const [saving, setSaving] = useState(false);
  const [showAccessibilityExpanded, setShowAccessibilityExpanded] = useState(false);
  const [showTapInPreview, setShowTapInPreview] = useState(false);
  const [emailManuallyChanged, setEmailManuallyChanged] = useState(false); // Track if user manually changed email
  const [prefillSource, setPrefillSource] = useState(null); // 'acuity', 'host', or null (blank)
  const [digitalPass, setDigitalPass] = useState(null);
  const [generatingPass, setGeneratingPass] = useState(false);
  const [showVenuePreview, setShowVenuePreview] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const [showPreSubmitPreview, setShowPreSubmitPreview] = useState(false);
  
  // View filter mode - for shareable intake links
  // Maps view param to step number: dietary=2, accessibility=1, interests=3
  const VIEW_FILTER_STEPS = {
    'accessibility': 1,
    'dietary': 2, 
    'interests': 3
  };
  const isFilteredView = activeViewFilter && VIEW_FILTER_STEPS[activeViewFilter];
  const filteredStep = VIEW_FILTER_STEPS[activeViewFilter] || null;
  
  // VIP List Capacity Modal state
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [showPreferenceCard, setShowPreferenceCard] = useState(false);
  const [capacityError, setCapacityError] = useState(null);
  const [requestingExtraInvites, setRequestingExtraInvites] = useState(false);
  const [loadError, setLoadError] = useState(null);  // Track load errors
  
  // Location dropdown state - for welcome step location toggle
  const [configuredLocations, setConfiguredLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState("");
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  
  // Event Resources state
  const [publicResources, setPublicResources] = useState([]);
  const [resourceAvailability, setResourceAvailability] = useState({});
  
  // Re-RSVP handler for users who previously cancelled
  const handleReRsvp = async () => {
    try {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await axios.post(`${API}/events/${event.event_id}/rsvp/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        toast.success("Welcome back! Your RSVP has been reactivated.");
        setCancelledRsvp(null);
        // Reload the RSVP data
        const rsvpRes = await axios.get(`${API}/events/${event.event_id}/my-rsvp`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (rsvpRes.data?.rsvp_id) {
          setExistingRsvp(rsvpRes.data);
          navigate(`/confirmation/${rsvpRes.data.rsvp_id}`);
        }
      }
    } catch (err) {
      console.error("Re-RSVP error:", err);
      toast.error("Failed to reactivate RSVP");
    }
  };
  
  // Fetch public resources when confirmation page is shown
  useEffect(() => {
    if (currentStep !== 5 || !event?.event_id) return;
    const fetchResources = async () => {
      try {
        const res = await fetch(`${API}/events/${event.event_id}/resources/public?survey_completed=false`);
        if (res.ok) {
          const data = await res.json();
          setPublicResources(data.resources || []);
          setResourceAvailability(data.availability || {});
        }
      } catch (err) {
        console.log("Could not fetch resources");
      }
    };
    fetchResources();
  }, [currentStep, event?.event_id]);
  
  // Close location dropdown when clicking outside
  useEffect(() => {
    if (!showLocationDropdown) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-testid="welcome-location-selector"]') && 
          !e.target.closest('[data-testid="instructor-location-selector"]')) {
        setShowLocationDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLocationDropdown]);

  // Detect instructor pilot flow for restructured RSVP
  const isInstructorPilotFlow = event?.pilot_category?.toLowerCase() === 'instructor' || 
                                 event?.pilot_category?.toLowerCase() === 'workshop' || 
                                 event?.pilot_category?.toLowerCase() === 'builder';

  // Smart step navigation - skips steps that aren't enabled based on intake_settings
  // INSTRUCTOR PILOT FLOW (restructured per Master Plan Section 15):
  //   Step 0: Welcome + Name/Email lock-in
  //   Step 1: Builder Profile (photo, socials, company, builder type/project/stage)
  //   Step 2: Audience + Accessibility (combined)
  //   Step 3: Topics + 3 Questions + Submit
  //
  // STANDARD FLOW:
  //   Step 0: Welcome (always shown)
  //   Step 1: Accessibility (if collect_accessibility is true)
  //   Step 2: Dietary (if collect_dietary is true)
  //   Step 3: Interests/Experience (if collect_interests or requested_defaults has items)
  //   Step 4: Account creation (always shown)
  //   Step 5: Confirmation (always shown)
  
  // Category defaults - determines which intake steps show per category
  const CATEGORY_DEFAULTS = {
    makeup:     { collect_accessibility: true,  collect_dietary: false },
    hair:       { collect_accessibility: true,  collect_dietary: false },
    culinary:   { collect_accessibility: false, collect_dietary: true  },
    nails:      { collect_accessibility: true,  collect_dietary: false },
    lash:       { collect_accessibility: true,  collect_dietary: false },
    wellness:   { collect_accessibility: true,  collect_dietary: true  },
    events:     { collect_accessibility: true,  collect_dietary: true  },
    instructor: { collect_accessibility: true,  collect_dietary: false },
  };
  
  const isProviderFlow = event?.event_type === "service_provider";
  
  const getEnabledSteps = () => {
    if (isInstructorPilotFlow) {
      return [0, 1, 2, 3]; // Instructor flow: Welcome→Builder→Audience+Access→Topics+Questions
    }
    
    const category = event?.pilot_category?.toLowerCase() || "events";
    const catDefaults = CATEGORY_DEFAULTS[category] || CATEGORY_DEFAULTS.events;
    // Iter 9.40 - admin-controlled override via db.categories.universal_defaults_enabled
    const dyn = dynamicIntakeMeta[category];
    const dynDefaults = dyn ? {
      collect_accessibility: dyn.universal_defaults_enabled.includes("accessibility"),
      collect_dietary: dyn.universal_defaults_enabled.includes("dietary"),
    } : null;
    const settings = event?.intake_settings || dynDefaults || catDefaults;
    
    if (isProviderFlow) {
      // Iter 9.40 Slice I.g - RSVP physical reorder.
      // New order: 0 Welcome → 1 Service Select → 6 Payment (Acuity, only if connected)
      //           → 2 Preferences+Inspo → 3 Defaults (folded; same step) → 4 Your Info → 5 Confirm.
      // Payment step is dynamically inserted between Service Select (1) and Inspo (2).
      const acuityConnected = !!(event?.acuity_connected || event?.tech_stack?.acuity || techStack?.acuity?.enabled);
      if (acuityConnected) return [0, 1, 6, 2, 3, 4];  // payment included
      return [0, 1, 2, 3, 4];                           // no payment step (info-only methods)
    }
    
    // Standard event flow
    const enabled = [0]; // Welcome always enabled
    
    // Step 1: Accessibility
    const showAccessibility = settings.collect_accessibility !== undefined 
      ? settings.collect_accessibility === true 
      : catDefaults.collect_accessibility;
    if (showAccessibility) {
      enabled.push(1);
    }
    
    // Step 2: Dietary
    const showDietary = settings.collect_dietary !== undefined 
      ? settings.collect_dietary === true 
      : catDefaults.collect_dietary;
    if (showDietary) {
      enabled.push(2);
    }
    
    // Step 3: Interests/Experience - enable if there's anything to show
    const hasInterests = settings.collect_interests !== false;
    const hasInspoOrDefaults = settings.requested_defaults?.length > 0 || settings.inspo_folders?.length > 0;
    const hasQuestionBank = settings.question_bank_enabled;
    if (hasInterests || hasInspoOrDefaults || hasQuestionBank) {
      enabled.push(3);
    }
    
    // Step 4: Your Info - always enabled
    enabled.push(4);
    
    // Step 5: Confirmation - always the last step for events
    enabled.push(5);
    
    return enabled;
  };
  
  const getNextStep = (currentStep) => {
    const enabled = getEnabledSteps();
    const currentIndex = enabled.indexOf(currentStep);
    if (currentIndex === -1 || currentIndex >= enabled.length - 1) {
      return enabled[enabled.length - 1]; // Last step
    }
    return enabled[currentIndex + 1];
  };
  
  const getPrevStep = (currentStep) => {
    const enabled = getEnabledSteps();
    const currentIndex = enabled.indexOf(currentStep);
    if (currentIndex <= 0) {
      return enabled[0]; // First step
    }
    return enabled[currentIndex - 1];
  };
  
  // Get current step number out of total enabled steps (for progress display)
  const getStepProgress = (currentStep) => {
    const enabled = getEnabledSteps();
    // Don't count welcome (0) or final step in progress
    const lastStep = enabled[enabled.length - 1];
    const progressSteps = enabled.filter(s => s >= 1 && s < lastStep);
    const progressIndex = progressSteps.indexOf(currentStep);
    return { current: Math.max(0, progressIndex) + 1, total: progressSteps.length };
  };

  // Timeout fallback - stop loading after 10 seconds and auto-open envelope after 8s if stuck
  useEffect(() => {
    // Timeout for loading state - stop after 10 seconds
    const loadTimeout = setTimeout(() => {
      if (loading && !event) {
        console.error("Event loading timeout - stopping after 10 seconds");
        setLoading(false);
        setShowEnvelope(false);
        setEnvelopeEnabled(false);
        setLoadError("Event could not be loaded. Please try refreshing the page.");
      }
    }, 10000);
    
    // Auto-open envelope after 8 seconds if still showing (user might not know to tap)
    const envelopeTimeout = setTimeout(() => {
      if (showEnvelope && envelopeEnabled && !envelopeOpened) {
        console.log("Auto-opening envelope after 8 seconds");
        setEnvelopeOpened(true);
        setTimeout(() => setShowEnvelope(false), 800);
      }
    }, 8000);
    
    return () => {
      clearTimeout(loadTimeout);
      clearTimeout(envelopeTimeout);
    };
  }, [loading, event, showEnvelope, envelopeEnabled, envelopeOpened]);

  // Handle filtered view mode - jump directly to the specified step
  useEffect(() => {
    if (filteredStep && event && !loading) {
      // Skip envelope animation for filtered views
      setShowEnvelope(false);
      setEnvelopeEnabled(false);
      setEnvelopeOpened(true);
      // Jump to the filtered step
      setCurrentStep(filteredStep);
    }
  }, [filteredStep, event, loading]);

  // Update local event state when SWR data changes (for real-time sync)
  useEffect(() => {
    if (swrEvent) {
      setEvent(swrEvent);
      setLoading(false);
      
      // Fetch dynamic branding and content from page-content API
      const fetchDynamicContent = async () => {
        try {
          const res = await fetch(`${API}/events/${swrEvent.event_id}/page-content?page=all`);
          if (res.ok) {
            const data = await res.json();
            if (data.branding) {
              setDynamicBranding(data.branding);
            }
            // Store all dynamic content (welcome, intake, confirmation)
            if (data.all_content) {
              setDynamicContent(data.all_content);
            }
          }
        } catch (err) {
          console.log("Could not fetch dynamic content");
        }
      };
      fetchDynamicContent();

      // Fetch category public_features (gate "brand stuff" until pilot-tested)
      const fetchCategoryFeatures = async () => {
        try {
          const cat = (swrEvent.pilot_category || swrEvent.category || "").toLowerCase();
          if (!cat) return;
          const res = await fetch(`${API}/category-features/${cat}`);
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.public_features)) {
              setCategoryPublicFeatures(data.public_features);
            }
          }
        } catch (err) {
          console.log("Could not fetch category features");
        }
      };
      fetchCategoryFeatures();

      // Cache category branding (icon + light/dark hex) so the Welcome cover
      // can render the canonical category default when no host override.
      const fetchCategoryBranding = async () => {
        try {
          if (window.__categoryBrandingCache) return;
          const r = await fetch(`${API}/categories/branding`);
          if (r.ok) window.__categoryBrandingCache = await r.json();
        } catch (_e) {
          /* offline-safe */
        }
      };
      fetchCategoryBranding();

      // Fetch host_invite payload when ?invite_token=... is present (Amayah-style)
      const inviteToken = new URLSearchParams(window.location.search).get("invite_token");
      if (inviteToken) {
        const fetchInvite = async () => {
          try {
            const res = await fetch(`${API}/rsvp/invite/${inviteToken}`);
            if (res.ok) {
              const data = await res.json();
              setHostInvite(data);
              if (data.slots?.length === 1) setSelectedInviteSlot(data.slots[0].slot_id);
              // Apply host pre-fill (editable, just a head start)
              const pf = data.prefill || {};
              if (pf.first_name || pf.last_name || pf.email || pf.phone) {
                setAccountData(prev => ({
                  ...prev,
                  first_name: pf.first_name || prev.first_name,
                  last_name: pf.last_name || prev.last_name,
                  email: pf.email || prev.email,
                  phone: pf.phone || prev.phone,
                }));
              }
              if (pf.preferences && typeof pf.preferences === "object") {
                setPreferences(prev => ({ ...prev, ...pf.preferences }));
              }
            }
          } catch (_e) {
            // silent
          }
        };
        fetchInvite();
      }

      // Fetch RSVP prefill when ?invite=inv_* is present (host invite-client / Acuity prefilled)
      const rsvpInvite = new URLSearchParams(window.location.search).get("invite");
      if (rsvpInvite) {
        const fetchPrefill = async () => {
          try {
            const res = await fetch(`${API}/rsvp/prefill/${rsvpInvite}`);
            if (res.ok) {
              const data = await res.json();
              setRsvpPrefill(data);
              const pf = data.prefill || {};
              if (pf.first_name || pf.last_name || pf.email || pf.phone) {
                setAccountData(prev => ({
                  ...prev,
                  first_name: pf.first_name || prev.first_name,
                  last_name: pf.last_name || prev.last_name,
                  email: pf.email || prev.email,
                  phone: pf.phone || prev.phone,
                }));
              }
              if (pf.preferences && typeof pf.preferences === "object" && Object.keys(pf.preferences).length > 0) {
                setPreferences(prev => ({ ...prev, ...pf.preferences }));
              }
            }
          } catch (_e) { /* silent */ }
        };
        fetchPrefill();
      }
      
      // Fetch configured locations if collect_location is enabled
      if (swrEvent?.intake_settings?.collect_location) {
        const fetchLocations = async () => {
          try {
            const res = await fetch(`${API}/configured-locations`);
            if (res.ok) {
              const data = await res.json();
              // Use event-specific location_options if set, otherwise use all configured locations
              const eventLocations = swrEvent?.intake_settings?.location_options;
              if (eventLocations && eventLocations.length > 0) {
                setConfiguredLocations(data.locations.filter(l => eventLocations.includes(l.label) || eventLocations.includes(l.full_label)));
              } else {
                setConfiguredLocations(data.locations);
              }
            }
          } catch (err) {
            console.log("Could not fetch configured locations");
          }
        };
        fetchLocations();
      }
      
      // Check if envelope animation is enabled based on event settings
      // Only enable if show_envelope is explicitly true or undefined (default to true)
      // But if envelope was already opened or disabled, don't re-enable
      if (swrEvent.show_envelope === false) {
        setShowEnvelope(false);
        setEnvelopeEnabled(false);
      }
      // Note: We don't re-enable envelope here - it stays in whatever state it's in
      // The initial state handles showing the envelope for first-time visitors
      
      // Update partners from team data and host_orgs - combine both.
      // Stricter dedupe: by canonical org_id when present (host_orgs always
      // wins over team); fall back to lower-cased name otherwise.
      const allHostOrgs = [];
      const seenIds = new Set();
      const seenNames = new Set();

      // First add from host_orgs field (canonical)
      if (swrEvent.host_orgs && swrEvent.host_orgs.length > 0) {
        swrEvent.host_orgs.forEach((org, idx) => {
          const name = (org.name || org || "").toString().trim();
          const orgId = org.org_id || org.organization_id || null;
          const dedupeKey = orgId || name.toLowerCase();
          if (!dedupeKey) return;
          if (orgId && seenIds.has(orgId)) return;
          if (!orgId && seenNames.has(name.toLowerCase())) return;
          if (org.show_in_hosted_by === false) return;
          if (orgId) seenIds.add(orgId);
          seenNames.add(name.toLowerCase());
          allHostOrgs.push({
            partner_id: orgId || `host_org_${idx}`,
            name: name,
            featured: true,
            logo_url: org.logo || org.logo_url || null,
            cta_url: org.website || null,
            description: org.description || null,
            is_host: true,
            type: 'host',
            show_in_hosted_by: org.show_in_hosted_by !== false
          });
        });
      }
      
      // Then add team members with label "host" that aren't already included
      const team = swrEvent.team || [];
      team
        .filter(m => m.label === "host" || m.role === "host")
        .filter(m => m.org_name)
        .forEach((m, idx) => {
          const nm = (m.org_name || "").toString().trim();
          const oid = m.org_id || m.organization_id || null;
          if (oid && seenIds.has(oid)) return;
          if (!oid && seenNames.has(nm.toLowerCase())) return;
          if (oid) seenIds.add(oid);
          seenNames.add(nm.toLowerCase());
          allHostOrgs.push({
            partner_id: oid || `team_org_${idx}`,
            name: nm,
            featured: true,
            logo_url: m.org_logo || null,
            cta_url: null,
            is_host: true,
            type: 'host',
            show_in_hosted_by: true
          });
        });
      
      if (allHostOrgs.length > 0) {
        setPartners(allHostOrgs);
      } else if (swrEvent.partners && swrEvent.partners.length > 0) {
        // Also use partners array from event data
        setPartners(swrEvent.partners.map((p, idx) => ({
          partner_id: p.partner_id || `partner_${idx}`,
          name: p.name,
          featured: p.featured !== false,
          logo_url: p.logo_url || p.logo || null,
          cta_url: p.cta_url || p.website || null,
          is_host: p.is_host || false,
          type: p.type || null
        })));
      } else if (swrEvent.org_name) {
        // Fallback: use event's org_name as the host (with enriched org data)
        setPartners([{
          partner_id: 'event_org',
          name: swrEvent.org_name,
          featured: true,
          logo_url: swrEvent.org_logo || null,
          cta_url: swrEvent.org_website || null,
          description: swrEvent.org_description || null,
          is_host: true,
          type: 'host'
        }]);
      }
    }
  }, [swrEvent, envelopeOpened]);

  // Handle SWR errors - stop loading on error and show event not found
  useEffect(() => {
    if (swrError) {
      console.error("Event fetch error:", swrError);
      setLoading(false);
      setEvent(null);  // Will trigger "Event not found" display
    }
  }, [swrError]);

  // Check if user is a host/cohost for this event
  useEffect(() => {
    if (!event || !user) {
      setIsEventHost(false);
      return;
    }
    
    // Check if user is the main host
    const isMainHost = event.host_user_id === user.user_id;
    
    // Check if user is in co_hosts array
    const isCohost = (event.co_hosts || []).some(ch => 
      ch.user_id === user.user_id || 
      (ch.email && user.email && ch.email.toLowerCase() === user.email.toLowerCase())
    );
    
    // Check if user is in team with host/admin role
    const isTeamHost = (event.team || []).some(m => 
      (m.user_id === user.user_id || (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase())) &&
      (m.role === 'host' || m.role === 'admin' || m.role === 'owner' || m.label === 'host')
    );
    
    setIsEventHost(isMainHost || isCohost || isTeamHost);
  }, [event, user]);

  useEffect(() => {
    fetchEvent();
    // Envelope now requires user tap to open - no auto-open
  }, [eventSlug]);

  // Pre-fill states: ?prefill=acuity | ?prefill=host | (blank = empty form)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const prefillMode = urlParams.get('prefill');
    if (prefillMode === 'acuity') {
      setPrefillSource('acuity');
      setAccountData(prev => ({
        ...prev,
        first_name: "Brianna",
        last_name: "Davis",
        name: "Brianna Davis",
        email: "brianna.d@example.com",
        phone: "(310) 555-0147",
        city: "Los Angeles, CA",
      }));
    } else if (prefillMode === 'host') {
      setPrefillSource('host');
      setAccountData(prev => ({
        ...prev,
        first_name: "Brianna",
        last_name: "Davis",
        name: "Brianna Davis",
        email: "brianna.d@example.com",
        city: "Los Angeles, CA",
      }));
    } else {
      setPrefillSource(null);
    }
  }, []);

  // Check for ?pass=true URL param to skip to confirmation for existing RSVPs
  // AND prefill from user defaults for new RSVPs
  // No auto-redirect - signed-in users can view the event page and see buttons for Dashboard/Confirmation
  useEffect(() => {
    const checkExistingRsvpAndPrefill = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const showPass = urlParams.get('pass') === 'true';
      const vipParam = urlParams.get('vip');
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      
      if (!event?.event_id) return;
      
      try {
        if (token) {
          // Check for existing RSVP for this user and event
          try {
            const res = await axios.get(`${API}/events/${event.event_id}/my-rsvp`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.data && res.data.rsvp_id) {
              // Store the existing RSVP for reference (so we can show a link to confirmation)
              setExistingRsvp(res.data);
              
              // Only auto-redirect if ?pass=true is explicitly set
              if (showPass) {
                navigate(`/confirmation/${res.data.rsvp_id}`, { replace: true });
                return;
              }
              // Otherwise, let them view the page with buttons to Dashboard/Confirmation
            }
          } catch (rsvpErr) {
            // 404 means no active RSVP - check for cancelled one
            if (rsvpErr.response?.status === 404) {
              try {
                const cancelledRes = await axios.get(`${API}/events/${event.event_id}/cancelled-rsvp`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (cancelledRes.data?.has_cancelled_rsvp) {
                  setCancelledRsvp(cancelledRes.data);
                }
              } catch (e) {
                // No cancelled RSVP either
              }
            }
          }
        }
        
        // No existing RSVP - prefill from user defaults
        if (token) {
          try {
            const defaultsRes = await axios.get(`${API}/user/defaults`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (defaultsRes.data?.defaults) {
              const defaults = defaultsRes.data.defaults;
              
              // Prefill accessibility from defaults
              if (defaults.accessibility) {
                // Map database accessibility names to form IDs
                const accessibilityMap = {
                  'wheelchair_access': 'wheelchair',
                  'extra_time': 'extra_time',
                  'hearing_assistance': 'hearing',
                  'service_animal': 'service_animal',
                  'fragrance_free': 'fragrance_free',
                  'limited_mobility': 'limited_mobility',
                  'mobility_aid': 'limited_mobility',
                  'sensory_sensitivity': 'hearing'
                };
                
                const mappedNeeds = (defaults.accessibility.needs || []).map(
                  need => accessibilityMap[need] || need
                );
                
                setPreferences(prev => ({
                  ...prev,
                  accessibility_needs: mappedNeeds.length > 0 ? mappedNeeds : prev.accessibility_needs,
                  invisible_disabilities: defaults.accessibility.invisible_disabilities || prev.invisible_disabilities,
                  disability_notes: defaults.accessibility.notes || prev.disability_notes
                }));
              }
              
              // Prefill dietary from defaults
              if (defaults.dietary) {
                setPreferences(prev => ({
                  ...prev,
                  dietary_restrictions: defaults.dietary.restrictions || prev.dietary_restrictions,
                  dietary_notes: defaults.dietary.notes || prev.dietary_notes
                }));
              }
              
              // Prefill interests from defaults
              if (defaults.interests && defaults.interests.length > 0) {
                setPreferences(prev => ({
                  ...prev,
                  interests: defaults.interests
                }));
              }
            }
          } catch (prefillErr) {
            console.log("Could not load user defaults for prefill:", prefillErr.message);
          }
        }
      } catch (err) {
        console.log("No existing RSVP found:", err.message);
        // Still try to prefill from defaults for new RSVPs
        if (token) {
          try {
            const defaultsRes = await axios.get(`${API}/user/defaults`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (defaultsRes.data?.defaults) {
              const defaults = defaultsRes.data.defaults;
              
              if (defaults.accessibility) {
                // Map database accessibility names to form IDs
                const accessibilityMap = {
                  'wheelchair_access': 'wheelchair',
                  'extra_time': 'extra_time',
                  'hearing_assistance': 'hearing',
                  'service_animal': 'service_animal',
                  'fragrance_free': 'fragrance_free',
                  'limited_mobility': 'limited_mobility',
                  'mobility_aid': 'limited_mobility',
                  'sensory_sensitivity': 'hearing'
                };
                
                const mappedNeeds = (defaults.accessibility.needs || []).map(
                  need => accessibilityMap[need] || need
                );
                
                setPreferences(prev => ({
                  ...prev,
                  accessibility_needs: mappedNeeds.length > 0 ? mappedNeeds : prev.accessibility_needs,
                  invisible_disabilities: defaults.accessibility.invisible_disabilities || prev.invisible_disabilities,
                  disability_notes: defaults.accessibility.notes || prev.disability_notes
                }));
              }
              
              if (defaults.dietary) {
                setPreferences(prev => ({
                  ...prev,
                  dietary_restrictions: defaults.dietary.restrictions || prev.dietary_restrictions,
                  dietary_notes: defaults.dietary.notes || prev.dietary_notes
                }));
              }
              
              if (defaults.interests && defaults.interests.length > 0) {
                setPreferences(prev => ({
                  ...prev,
                  interests: defaults.interests
                }));
              }
            }
          } catch (prefillErr) {
            console.log("Could not load user defaults:", prefillErr.message);
          }
        }
      }
    };
    
    if (event) {
      checkExistingRsvpAndPrefill();
    }
  }, [event]);

  // Update page title when event loads
  useEffect(() => {
    if (event?.name) {
      document.title = `phygital - ${event.name}`;
    }
  }, [event]);
  
  // Fetch host-curated inspo when entering step 3 (Experience step) for provider pilots
  useEffect(() => {
    const fetchHostCuratedInspo = async () => {
      if (currentStep !== 3) return;
      if (!event?.event_id) return;
      
      const pilotCategory = event?.pilot_category?.toLowerCase();
      const isProviderPilot = ['makeup', 'hair', 'nails', 'nail', 'lash', 'culinary', 'wellness', 'style', 'travel', 'dispensary', 'style'].includes(pilotCategory);
      
      if (!isProviderPilot) return;
      
      setLoadingHostInspo(true);
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const res = await fetch(`${API}/rsvp/${event.event_id}/host-inspo`, {
          headers,
          credentials: 'include'
        });
        
        if (res.ok) {
          const data = await res.json();
          setHostCuratedInspo(data || []);
        }
      } catch (err) {
        console.log("Could not fetch host curated inspo:", err.message);
      } finally {
        setLoadingHostInspo(false);
      }
    };
    
    fetchHostCuratedInspo();
  }, [currentStep, event?.event_id, event?.pilot_category]);

  // Iter 9.47.v - fetch wedding-specific intake fields (returns applies:false
  // for non-wedding dashboards so this is a safe always-on call).
  useEffect(() => {
    if (!event?.event_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/events/${event.event_id}/wedding-intake-fields`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setWeddingIntake({ applies: !!data.applies, fields: data.fields || [] });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [event?.event_id]);

  // Iter 9.48d - fetch service-tag conditional intake (bridal / instructor / travel).
  // Returns empty groups if the selected service has no tags.
  useEffect(() => {
    const svcId = selectedService?.service_id;
    if (!svcId) {
      setTagIntake({ applies: false, groups: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/services/${svcId}/tag-intake-fields`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setTagIntake({ applies: !!data.applies, groups: data.groups || [] });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [selectedService?.service_id]);
  
  // Fetch Acuity-bound host private inspo when rsvp email is captured (provider pilots)
  // Surfaces tier-2 private inspo only if email matches one of the host's Acuity clients
  useEffect(() => {
    const fetchAcuityBoundInspo = async () => {
      const email = accountData?.email?.trim();
      if (!email || !event?.event_id) return;
      // Only fetch on inspo step (2) or experience step (3) for provider pilots
      if (currentStep !== 2 && currentStep !== 3) return;
      const pilotCategory = event?.pilot_category?.toLowerCase();
      const isProviderPilot = ['makeup', 'hair', 'nails', 'nail', 'lash', 'lashes', 'culinary', 'wellness', 'style', 'travel', 'dispensary', 'style'].includes(pilotCategory);
      if (!isProviderPilot) return;
      
      setLoadingAcuityInspo(true);
      try {
        const res = await fetch(`${API}/public/host-inspo-for-rsvp/${event.event_id}?email=${encodeURIComponent(email)}`);
        if (res.ok) {
          const data = await res.json();
          setAcuityBoundInspo(data.inspo || []);
          setIsAcuityMatch(!!data.is_acuity_match);
        }
      } catch (err) {
        console.log("Acuity inspo fetch skipped:", err.message);
      } finally {
        setLoadingAcuityInspo(false);
      }
    };
    fetchAcuityBoundInspo();
  }, [currentStep, event?.event_id, event?.pilot_category, accountData?.email]);
  
  // Handle mobile keyboard - scroll focused input into view
  useEffect(() => {
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        // Wait for keyboard to appear
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    };
    
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);

  // Guard against infinite updates when user data changes
  const userNameRef = useRef(null);
  const userEmailRef = useRef(null);
  const userPhoneRef = useRef(null);
  
  useEffect(() => {
    if (user) {
      // Only update if actual values changed
      const newName = user.name || "";
      const newEmail = user.email || "";
      const newPhone = user.phone || "";
      
      if (newName !== userNameRef.current || newEmail !== userEmailRef.current || newPhone !== userPhoneRef.current) {
        userNameRef.current = newName;
        userEmailRef.current = newEmail;
        userPhoneRef.current = newPhone;
        
        setAccountData(prev => ({
          ...prev,
          name: newName,
          email: newEmail,
          phone: newPhone
        }));
      }
    }
  }, [user?.name, user?.email, user?.phone]); // Depend on specific fields, not whole user object

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventSlug}`);
      setEvent(res.data);
      
      // Check if envelope animation is enabled for this event
      if (res.data.show_envelope === false) {
        setShowEnvelope(false);
        setEnvelopeEnabled(false);
      }
      
      // Get hosting organizations - combine host_orgs field with team members labeled as "host"
      // Priority: host_orgs field (canonical source), then supplement with team hosts
      const allHostOrgs = [];
      const seenNames = new Set();
      
      // First add from host_orgs field (canonical)
      if (res.data.host_orgs && res.data.host_orgs.length > 0) {
        res.data.host_orgs.forEach((org, idx) => {
          const name = org.name || org;
          if (!seenNames.has(name)) {
            seenNames.add(name);
            allHostOrgs.push({
              partner_id: `host_org_${idx}`,
              name: name,
              featured: true,
              logo_url: org.logo || null,
              cta_url: org.website || null
            });
          }
        });
      }
      
      // Then add team members with label "host" that aren't already included
      const team = res.data.team || [];
      team
        .filter(m => m.label === "host" || m.role === "host")
        .filter(m => m.org_name)
        .forEach((m, idx) => {
          if (!seenNames.has(m.org_name)) {
            seenNames.add(m.org_name);
            allHostOrgs.push({
              partner_id: `team_org_${idx}`,
              name: m.org_name,
              featured: true,
              logo_url: null,
              cta_url: null
            });
          }
        });
      
      // Fallback to hosts field if nothing else
      if (allHostOrgs.length > 0) {
        setPartners(allHostOrgs);
      } else if (res.data.hosts && res.data.hosts.length > 0) {
        setPartners(res.data.hosts.map((host, idx) => ({
          partner_id: `host_${idx}`,
          name: typeof host === 'string' ? host : host.name,
          featured: true,
          logo_url: null,
          cta_url: null
        })));
      }
      // If still no hosts, leave empty - nothing hardcoded
      
      // Check if user has already RSVPd and has a pass
      const token = localStorage.getItem("session_token");
      if (token && res.data.event_id) {
        try {
          const passRes = await axios.get(`${API}/events/${res.data.event_id}/my-pass`, { 
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true 
          });
          if (passRes.data && passRes.data.pass_token) {
            setDigitalPass(passRes.data);
            setCurrentStep(6); // Skip to confirmation/pass screen
            setShowEnvelope(false);
          }
        } catch (passError) {
          // User doesn't have a pass yet, continue normally
          console.log("No existing pass found");
        }
      }
      
      // Fetch provider services for service selection step
      if (res.data.event_type === "service_provider") {
        try {
          const svcRes = await fetch(`${API}/events/${res.data.event_id}/public-services`);
          if (svcRes.ok) {
            const svcData = await svcRes.json();
            setAvailableServices(svcData.services || []);
          }
        } catch (e) { console.log("Services fetch:", e); }
        
        // Fetch service display settings
        try {
          const ssRes = await fetch(`${API}/events/${res.data.event_id}/service-settings`);
          if (ssRes.ok) {
            const ssData = await ssRes.json();
            setServiceSettings(ssData);
          }
        } catch (e) { console.log("Service settings fetch:", e); }
        
        // Fetch host's curated looks
        try {
          const looksRes = await fetch(`${API}/events/${res.data.event_id}/host-looks`);
          if (looksRes.ok) {
            const looksData = await looksRes.json();
            setHostCuratedInspo(looksData.public_looks || []);
          }
        } catch (e) { console.log("Host looks fetch:", e); }
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      setEvent(null);  // Explicitly set null to show "Event not found"
    } finally {
      setLoading(false);
    }
  };

  const generateDigitalPass = async () => {
    if (!event) return;
    setGeneratingPass(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/generate-pass`, {}, { withCredentials: true });
      setDigitalPass(res.data);
      toast.success("Your digital pass is ready!");
    } catch (error) {
      console.error("Error generating pass:", error);
      toast.error("Could not generate pass. Please try again.");
    } finally {
      setGeneratingPass(false);
    }
  };

  const copyPassLink = () => {
    if (digitalPass?.pass_url) {
      navigator.clipboard.writeText(digitalPass.pass_url);
      setLinkCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const sharePass = async () => {
    if (!digitalPass) return;
    const customTexts = event?.custom_texts || {};
    const shareData = {
      title: customTexts.share_title || `${event?.name || 'Event'} - Event Pass`,
      text: customTexts.share_text || `My pass for ${event?.name || 'this event'}`,
      url: digitalPass.pass_url
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        copyPassLink();
      }
    } else {
      copyPassLink();
    }
  };

  // Helper to format date for ICS (YYYYMMDDTHHMMSS format)
  const formatICSDate = (dateStr, timeStr) => {
    if (!dateStr) return null;
    // Parse date (YYYY-MM-DD format)
    const dateParts = dateStr.split('-');
    const date = dateParts.join(''); // YYYYMMDD
    
    // Parse time (HH:MM or HH:MM:SS format)
    let time = '000000';
    if (timeStr) {
      const timeParts = timeStr.replace(/[^0-9:]/g, '').split(':');
      const hours = (timeParts[0] || '00').padStart(2, '0');
      const mins = (timeParts[1] || '00').padStart(2, '0');
      const secs = (timeParts[2] || '00').padStart(2, '0');
      time = `${hours}${mins}${secs}`;
    }
    
    return `${date}T${time}`;
  };

  // Generate Google Calendar URL
  const getGoogleCalendarUrl = () => {
    if (!event) return '';
    const title = encodeURIComponent(event.name || 'Event');
    const location = encodeURIComponent(event.venue_name || event.location || 'TBA');
    const description = encodeURIComponent(event.description || '');
    
    // Format dates for Google Calendar (YYYYMMDDTHHMMSS)
    const startDate = formatICSDate(event.date, event.start_time);
    const endDate = formatICSDate(event.date, event.end_time);
    
    if (!startDate || !endDate) return '';
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&location=${location}&details=${description}`;
  };

  // Generate Outlook Calendar URL
  const getOutlookCalendarUrl = () => {
    if (!event) return '';
    const title = encodeURIComponent(event.name || 'Event');
    const location = encodeURIComponent(event.venue_name || event.location || 'TBA');
    const description = encodeURIComponent(event.description || '');
    
    // Format for Outlook (ISO format)
    const startISO = event.date && event.start_time 
      ? `${event.date}T${event.start_time}:00` 
      : event.date;
    const endISO = event.date && event.end_time 
      ? `${event.date}T${event.end_time}:00` 
      : event.date;
    
    return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&startdt=${startISO}&enddt=${endISO}&location=${location}&body=${description}`;
  };

  const addToCalendar = () => {
    // Create ICS file for Apple Calendar / generic calendar apps
    if (!event) {
      toast.error("Event data not available");
      return;
    }
    
    const title = event.name || 'Event';
    const location = event.venue_name || event.location || 'TBA';
    const description = event.description || `Your preferences are saved in your Phygital pass.`;
    
    // Format dates for ICS
    const startDate = formatICSDate(event.date, event.start_time);
    const endDate = formatICSDate(event.date, event.end_time);
    
    if (!startDate || !endDate) {
      toast.error("Event date/time not available");
      return;
    }
    
    // Escape special characters for ICS
    const escapeICS = (str) => str.replace(/[,;\\]/g, (match) => '\\' + match).replace(/\n/g, '\\n');
    
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Phygital//Event Calendar//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${escapeICS(title)}
LOCATION:${escapeICS(location)}
DESCRIPTION:${escapeICS(description)}
STATUS:CONFIRMED
SEQUENCE:0
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${event.slug || 'event'}.ics`;
    link.click();
    toast.success("Calendar event downloaded!");
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      // If this RSVP came from a host_invite link (Amayah-style), accept the
      // invite first so the slot is locked and a card is created with
      // source='host_invite'. Then continue the canonical RSVP submission so
      // the full preference card / intake / confirmation experience runs.
      const inviteToken = new URLSearchParams(window.location.search).get("invite_token");
      if (inviteToken && (selectedInviteSlot || requestAlternateTime)) {
        try {
          await fetch(`${API}/rsvp/invite/${inviteToken}/accept`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slot_id: selectedInviteSlot || "request_alternate",
              first_name: accountData.first_name,
              last_name: accountData.last_name,
              email: accountData.email,
              phone: accountData.phone,
              request_alternate: requestAlternateTime,
            }),
          });
        } catch (_e) {
          // non-fatal; continue with standard RSVP flow
        }
      }

      // Prepare account data with role_type (first selected role)
      const rsvpData = {
        ...accountData,
        role_type: accountData.roles[0] || null,  // Convert roles array to single role_type
        is_vip: isVipInvite,  // Set VIP flag if from VIP invite link
        label: isVipInvite ? "VIP" : "attendee",  // Set label
        vip_code: vipCode || null,  // Pass the VIP code for tracking
        source: inviteToken ? "host_invite" : (isVipInvite ? "vip_invite" : "direct")
      };
      const rsvpRes = await axios.post(`${API}/events/${event.event_id}/rsvp`, rsvpData, { withCredentials: true });
      
      // Store session token if returned (for new guest users)
      const newToken = rsvpRes.data.session_token;
      if (newToken) {
        localStorage.setItem('session_token', newToken);
        // Update axios defaults for subsequent requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
      
      // Get the token for preferences call (use new token or existing)
      const authToken = newToken || localStorage.getItem('session_token');
      
      // Submit preferences with explicit auth header
      await axios.post(`${API}/events/${event.event_id}/preferences`, preferences, { 
        withCredentials: true,
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      
      // Save to client's Phygital profile (bidirectional sync)
      try {
        await fetch(`${API}/client-profile/save-intake`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: accountData.email,
            name: `${accountData.first_name} ${accountData.last_name}`,
            event_id: event.event_id,
            inspo: preferences.inspo,
            defaults: preferences,
            accessibility: preferences.accessibility,
            linked_experiences: linkedExperiences,
            selected_service: selectedService ? { service_id: selectedService.service_id, name: selectedService.name } : null
          })
        });
      } catch (e) { console.log("Profile save:", e); }
      
      toast.success("You're all set!");
      
      // Trigger global sync so profile pages update with new RSVP
      triggerGlobalSync('all', event.event_id);
      
      // Redirect to dedicated confirmation page
      const rsvpId = rsvpRes.data.rsvp_id;

      // Iter 9.48d - persist service-tag intake answers (bridal/instructor/travel)
      try {
        if (selectedService?.service_id && Object.keys(tagAnswers || {}).length > 0) {
          await axios.post(
            `${API}/services/${selectedService.service_id}/tag-intake-responses`,
            { booking_id: rsvpId, email: accountData?.email, responses: tagAnswers },
            { withCredentials: true }
          );
        }
      } catch (tagErr) {
        console.warn("Tag intake save failed (non-blocking):", tagErr);
      }

      if (rsvpId) {
        navigate(`/confirmation/${rsvpId}`, { replace: true });
      } else {
        // Fallback to confirmation step if no rsvp_id (shouldn't happen)
        setCurrentStep(5);
        setTimeout(() => generateDigitalPass(), 500);
      }
    } catch (error) {
      console.error("RSVP error:", error);
      const errorDetail = error.response?.data?.detail || "";
      
      // Check if this is a VIP list capacity error
      if (errorDetail.includes("capacity") || errorDetail.includes("at capacity") || errorDetail.includes("maximum")) {
        // Extract list info from error if available
        setCapacityError({
          message: errorDetail,
          vipCode: vipCode,
          eventId: event.event_id
        });
        setShowCapacityModal(true);
      } else {
        toast.error(errorDetail || "Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Instructor Pilot Submit - handles the restructured 4-step flow
  const handleInstructorSubmit = async () => {
    setSaving(true);
    try {
      // Prepare account data for RSVP
      const rsvpData = {
        ...accountData,
        role_type: preferences.builder_role || null,
        is_vip: isVipInvite,
        label: "instructor",
        vip_code: vipCode || null,
        source: "instructor_intake"
      };
      const rsvpRes = await axios.post(`${API}/events/${event.event_id}/rsvp`, rsvpData, { withCredentials: true });
      
      // Store session token
      const newToken = rsvpRes.data.session_token;
      if (newToken) {
        localStorage.setItem('session_token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
      
      const authToken = newToken || localStorage.getItem('session_token');
      
      // Submit instructor-specific session data via the instructor-rsvp endpoint
      const instPayload = {
        first_name: accountData.first_name,
        last_name: accountData.last_name,
        email: accountData.email,
        phone: accountData.phone || "",
        title: accountData.title || "",
        company: accountData.company || "",
        bio: accountData.bio || "",
        headshot_url: accountData.headshot_url || "",
        linkedin: accountData.linkedin || "",
        instagram: accountData.instagram || "",
        twitter: accountData.tiktok || "",
        website: accountData.website || "",
        session_title: instructorData.session_title,
        session_description: instructorData.session_description,
        session_topics: (instructorData.session_topics || []).filter(t => t.trim()),
        session_format: instructorData.session_format,
        session_length: instructorData.session_length,
        key_learnings: instructorData.key_learnings,
        questions_for_audience: instructorData.questions_for_audience,
        resources: (instructorData.resources || []).filter(r => r.title || r.url),
        builder_current_build: preferences.builder_current_build || "",
        builder_stage: preferences.builder_stage || "",
        builder_project_url: preferences.builder_project_url || "",
        invite_code: instructorInviteCode || event.instructor_invite_code || "",
        city: accountData.city || "",
        location: accountData.location || ""
      };
      
      try {
        await axios.post(
          `${API}/events/${event.event_id}/instructor-rsvp?code=${instructorInviteCode || event.instructor_invite_code || ''}`, 
          instPayload, 
          { 
            withCredentials: true,
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          }
        );
      } catch (instErr) {
        console.error("Instructor RSVP additional data error:", instErr);
      }
      
      toast.success("Session submitted successfully!");
      
      const rsvpId = rsvpRes.data.rsvp_id;

      // Iter 9.48d - persist service-tag intake answers (bridal/instructor/travel)
      try {
        if (selectedService?.service_id && Object.keys(tagAnswers || {}).length > 0) {
          await axios.post(
            `${API}/services/${selectedService.service_id}/tag-intake-responses`,
            {
              booking_id: rsvpId,
              email: accountData?.email,
              responses: tagAnswers,
            },
            { withCredentials: true, headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
          );
        }
      } catch (tagErr) {
        console.warn("Tag intake save failed (non-blocking):", tagErr);
      }
      
      // Success popup
      const popup = document.createElement('div');
      popup.innerHTML = `
        <div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;animation:fadeIn 0.3s ease">
          <div style="background:white;border-radius:20px;padding:32px;max-width:380px;margin:16px;text-align:center;animation:slideUp 0.3s ease">
            <div style="width:64px;height:64px;background:#EFF6FF;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 16px">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" stroke-width="2"><polyline points="20,6 9,17 4,12"/></svg>
            </div>
            <h3 style="font-size:18px;font-weight:600;color:#111;margin-bottom:8px">Session Submitted!</h3>
            <p style="font-size:14px;color:#666;line-height:1.5;margin-bottom:4px">Your session details have been received. The host will share audience insights with you before the session.</p>
            <p style="font-size:12px;color:#999;margin-top:12px">Redirecting to your confirmation...</p>
          </div>
        </div>
      `;
      document.body.appendChild(popup);
      
      triggerGlobalSync('all', event.event_id);
      
      setTimeout(() => {
        popup.remove();
        if (rsvpId) {
          navigate(`/confirmation/${rsvpId}`, { replace: true });
        }
      }, 3000);
    } catch (error) {
      console.error("Instructor RSVP error:", error);
      const errorDetail = error.response?.data?.detail || "";
      if (errorDetail.includes("capacity") || errorDetail.includes("at capacity")) {
        setCapacityError({ message: errorDetail, vipCode, eventId: event.event_id });
        setShowCapacityModal(true);
      } else {
        toast.error(errorDetail || "Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Filtered view submission - saves only the specific section data
  const [filteredSubmitted, setFilteredSubmitted] = useState(false);
  const [filterSubmitting, setFilterSubmitting] = useState(false);
  
  const handleFilteredSubmit = async () => {
    setFilterSubmitting(true);
    try {
      // Prepare filtered preferences based on view type
      let filteredPrefs = {};
      const sectionTitles = {
        'dietary': 'Dietary Preferences',
        'accessibility': 'Accessibility Information',
        'interests': 'Interests & Experience'
      };
      
      switch (activeViewFilter) {
        case 'dietary':
          filteredPrefs = {
            dietary_restrictions: preferences.dietary_restrictions,
            meat_preferences: preferences.meat_preferences,
            allergies: preferences.allergies,
            dietary_notes: preferences.dietary_notes,
            transportation: preferences.transportation,
            transportation_notes: preferences.transportation_notes
          };
          break;
        case 'accessibility':
          filteredPrefs = {
            accessibility_needs: preferences.accessibility_needs,
            invisible_disabilities: preferences.invisible_disabilities,
            disability_notes: preferences.disability_notes
          };
          break;
        case 'interests':
          filteredPrefs = {
            experience_apps: preferences.experience_apps,
            interests: preferences.interests,
            vibe: preferences.vibe,
            additional_notes: preferences.additional_notes
          };
          break;
        default:
          filteredPrefs = preferences;
      }
      
      // Submit to a coordinator endpoint (or save anonymously for reports)
      await axios.post(`${API}/events/${event.event_id}/filtered-intake`, {
        view_type: activeViewFilter,
        preferences: filteredPrefs,
        submitted_at: new Date().toISOString()
      });
      
      toast.success(`${sectionTitles[activeViewFilter]} saved!`);
      setFilteredSubmitted(true);
    } catch (error) {
      console.error("Filtered submit error:", error);
      toast.error("Unable to save. Please try again.");
    } finally {
      setFilterSubmitting(false);
    }
  };

  // Request extra invites when VIP list is at capacity
  const handleRequestExtraInvites = async () => {
    if (!capacityError?.vipCode || !capacityError?.eventId) return;
    
    setRequestingExtraInvites(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // First, get the list_id from the VIP code
      const vipRes = await axios.get(`${API}/vip/claim/${capacityError.vipCode}`);
      const listId = vipRes.data?.list_id;
      
      if (!listId) {
        toast.error("Could not find VIP list information");
        return;
      }
      
      // Request extra invites
      await axios.post(`${API}/events/${capacityError.eventId}/vip-lists/${listId}/request-extra-invites`, {
        requester_name: accountData.name || `${accountData.first_name} ${accountData.last_name}`.trim(),
        requester_email: accountData.email,
        requested_count: 1,
        reason: "VIP list was at capacity during RSVP"
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success("Request sent! The host will be notified and can approve additional spots.");
      setShowCapacityModal(false);
      setCapacityError(null);
    } catch (err) {
      console.error("Error requesting extra invites:", err);
      toast.error("Could not send request. Please contact the host directly.");
    } finally {
      setRequestingExtraInvites(false);
    }
  };

  const toggleArrayItem = (field, item) => {
    setPreferences(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  // Handle inspo upload with source tracking
  const handleInspoUpload = async (file, category) => {
    if (!file) return;
    
    // Create local URL for preview
    const localUrl = URL.createObjectURL(file);
    
    // Add to preferences with source tracking
    const newInspo = {
      image_url: localUrl,
      url: localUrl,
      category: category,
      source: "rsvp_upload",
      source_detail: `Uploaded during RSVP`,
      file: file,  // Store file for later upload
      added_at: new Date().toISOString()
    };
    
    setPreferences(prev => ({
      ...prev,
      inspo: [...(prev.inspo || []), newInspo]
    }));
    
    toast.success("Inspiration added!");
  };
  
  // Add host-curated inspo to user's selection
  const selectHostInspo = (inspoItem) => {
    // Check if already selected
    const alreadySelected = (preferences.inspo || []).some(
      i => i.inspo_id === inspoItem.inspo_id || i.image_url === inspoItem.image_url
    );
    
    if (alreadySelected) {
      // Remove if already selected
      setPreferences(prev => ({
        ...prev,
        inspo: (prev.inspo || []).filter(
          i => i.inspo_id !== inspoItem.inspo_id && i.image_url !== inspoItem.image_url
        )
      }));
    } else {
      // Add with source tracking
      const selectedInspo = {
        ...inspoItem,
        source: "host_curated",
        source_detail: inspoItem.source_detail || "Curated by host",
        added_at: new Date().toISOString()
      };
      
      setPreferences(prev => ({
        ...prev,
        inspo: [...(prev.inspo || []), selectedInspo]
      }));
    }
  };
  
  // Remove inspo from selection
  const removeInspo = (idx) => {
    setPreferences(prev => ({
      ...prev,
      inspo: (prev.inspo || []).filter((_, i) => i !== idx)
    }));
  };

  // Envelope Animation - Classic envelope with logo sticker on chrome background
  // Shows envelope that opens to reveal the event
  // Wait for event data to load first, but show envelope immediately with chrome background
  // Use swrEvent directly to avoid race conditions with state updates
  const eventData = event || swrEvent;
  
  if (showEnvelope && envelopeEnabled) {
    // Show a simple envelope placeholder while loading
    if (!eventData) {
      // No default background - use solid color gradient instead of hardcoded image
      return (
        <div 
          className="min-h-screen flex items-center justify-center font-sans overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
          }}
        >
          <PageMeta 
            title="You're Invited"
            description="Open your invitation on Phygital"
          />
          {/* Simple loading envelope placeholder */}
          <div className="relative">
            <div className="w-80 h-52 bg-white rounded-lg shadow-2xl" />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg border border-gray-100">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Event loaded - show full envelope with logo.
    // Lookup chain: event-level logo -> dynamic branding -> organization logo
    // -> host profile photo -> initials fallback. This keeps Shelsea/Mia/Aria
    // covers consistent with the host dashboard cover and the /me planner.
    const logoPath =
      eventData?.logo_url ||
      eventData?.logo_image ||
      eventData?.logo ||
      eventData?.host_brand_logo ||
      dynamicBranding?.logo_url ||
      dynamicBranding?.organization_logo ||
      eventData?.organization?.logo_url ||
      eventData?.host_profile_image ||
      eventData?.host_photo_url;
    const logoImage = logoPath ? getImageUrl(logoPath) : null;
    const customTexts = eventData?.custom_texts || {};
    
    // Get initials for the sticker - use custom_texts.sticker_initials or calculate from event name
    const getEventInitials = () => {
      if (customTexts.sticker_initials) return customTexts.sticker_initials;
      if (customTexts.envelope_sticker_text) return customTexts.envelope_sticker_text;
      if (!eventData?.name) return "PH";
      const words = eventData.name.split(/\s+/);
      if (words.length >= 4) {
        return words.slice(0, 4).map(w => w[0]).join("").toUpperCase();
      } else if (words.length >= 2) {
        return words.map(w => w[0]).join("").toUpperCase().slice(0, 4);
      }
      return eventData.name.slice(0, 2).toUpperCase();
    };
    
    // Background - Use card_background as unified source (synced with all surfaces)
    const backgroundImage = eventData?.card_background || eventData?.background_theme_url || eventData?.envelope_background || dynamicBranding?.cover_image;
    const backgroundStyle = backgroundImage 
      ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' };
    
    return (
      <div 
        data-testid="envelope-animation"
        className="min-h-screen flex items-center justify-center font-sans overflow-hidden cursor-pointer"
        style={backgroundStyle}
        onClick={() => {
          setEnvelopeOpened(true);
          setTimeout(() => setShowEnvelope(false), 800);
        }}
      >
        <PageMeta 
          title={eventData?.name ? `You're Invited to ${eventData.name}` : "You're Invited"}
          description={eventData?.description || `Open your invitation on Phygital`}
          image={eventData?.cover_image || eventData?.og_image}
        />
        <div className={`transition-all duration-1000 ease-out ${envelopeOpened ? 'scale-110 opacity-0' : 'scale-100 opacity-100'}`}>
          {/* Envelope Container */}
          <div className="relative">
            {/* White Envelope Card */}
            <div className="w-80 h-52 bg-white rounded-lg shadow-2xl relative overflow-hidden">
              {/* Envelope flap (top triangle) */}
              <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-gray-100 to-white" 
                   style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)' }} />
              
              {/* Inner card visible through envelope */}
              <div className="absolute inset-4 top-8 bg-gray-50 rounded border border-gray-200" />
            </div>
            
            {/* Logo Sticker - Centered on envelope */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg overflow-hidden border border-gray-100">
                {logoImage ? (
                  <img 
                    src={logoImage}
                    alt={eventData?.name || "Event"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // If logo fails to load, hide img and show text fallback
                      e.target.style.display = 'none';
                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                  />
                ) : null}
                <div 
                  className="flex flex-col items-center justify-center text-gray-800 font-bold text-xs leading-none absolute inset-0"
                  style={{ display: logoImage ? 'none' : 'flex' }}
                >
                  {getEventInitials().split("").map((letter, i) => (
                    <span key={i}>{letter}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* "You're Invited" text below envelope */}
          <div className="text-center mt-8">
            <p className="text-white/90 text-xs tracking-[0.4em] uppercase mb-3 drop-shadow-lg">
              YOU'RE INVITED
            </p>
            <p className="text-white/70 text-xs drop-shadow-lg mb-4">
              Tap anywhere to continue
            </p>
            {/* Skip button for users who don't realize they need to tap */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEnvelopeOpened(true);
                setTimeout(() => setShowEnvelope(false), 800);
              }}
              className="text-white/50 text-xs underline hover:text-white/80 transition-colors"
            >
              Skip animation
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center font-sans">
        <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center font-sans">
        <div className="text-center p-8">
          <h1 className="text-2xl font-semibold mb-2">Event not found</h1>
          {loadError && (
            <p className="text-gray-500 mb-4 max-w-md">{loadError}</p>
          )}
          {swrError && (
            <p className="text-red-500 text-sm mb-4">Error: {swrError.message || "Failed to load event"}</p>
          )}
          <div className="flex gap-2 justify-center">
            <Button onClick={() => window.location.reload()} variant="outline">Refresh</Button>
            <Button onClick={() => navigate("/")} variant="outline">Go Home</Button>
          </div>
        </div>
      </div>
    );
  }

  // Show success message for filtered submissions
  if (filteredSubmitted) {
    const sectionNames = {
      'dietary': 'Dietary preferences',
      'accessibility': 'Accessibility information',
      'interests': 'Experience preferences'
    };
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4 font-sans">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your {sectionNames[activeViewFilter] || 'information'} has been saved for {event?.name || 'this event'}.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The event coordinators will use this information to help make your experience better.
          </p>
          <Button 
            onClick={() => window.close()} 
            variant="outline" 
            className="mr-2"
          >
            Close Window
          </Button>
          <Button 
            onClick={() => {
              setFilteredSubmitted(false);
              setCurrentStep(filteredStep);
            }}
          >
            Edit Response
          </Button>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    // ============================================================================
    // INSTRUCTOR PILOT FLOW - Restructured per Master Plan Section 15
    // Step 0: Welcome + Name/Email lock-in
    // Step 1: Builder Profile 
    // Step 2: Audience + Accessibility (combined)
    // Step 3: Topics + 3 Questions + Submit
    // ============================================================================
    if (isInstructorPilotFlow) {
      const isVirtualEvt = event?.event_type === 'url' || event?.event_type === 'virtual';
      
      switch (currentStep) {
        case 0: // Welcome + Name/Email Lock-in
          return (
            <div className="animate-fade-in flex flex-col justify-start pt-4 pb-24 relative" data-testid="instructor-step-0">
              {/* Host Navigation Bar */}
              {isEventHost && envelopeOpened && (
                <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-2">
                  <div className="max-w-md lg:max-w-2xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                      {(event?.logo_image || event?.cover_image) ? (
                        <img src={getImageUrl(event.logo_image || event.cover_image)} alt={event?.name || "Event"} className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <PhygitalLogo className="h-7" variant="dark" />
                      )}
                    </Link>
                    <button onClick={() => navigate('/me')} className="flex items-center gap-2">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border-2 border-gray-200" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-500" />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Action Buttons for Signed-in Users */}
              {isAuthenticated && (
                <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
                  {isEventHost && (
                    <button onClick={() => navigate(`/host/v2/${event.event_id}`)} className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 rounded-full text-sm text-white font-medium transition-colors" data-testid="event-dashboard-btn">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </button>
                  )}
                  {existingRsvp && existingRsvp.rsvp_id && (
                    <button onClick={() => navigate(`/confirmation/${existingRsvp.rsvp_id}`)} className="flex items-center gap-2 px-4 py-2 bg-black/10 hover:bg-black/20 backdrop-blur-sm rounded-full text-sm text-gray-800 font-medium transition-colors border border-black/5" data-testid="view-confirmation-btn">
                      <CheckCircle2 className="w-4 h-4" /> View Confirmation
                    </button>
                  )}
                  {existingRsvp && (
                    <button onClick={() => setShowPreferenceCard(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-full text-sm text-blue-700 font-medium transition-colors border border-blue-200" data-testid="view-preference-card-btn">
                      <Eye className="w-4 h-4" /> View Card
                    </button>
                  )}
                  {/* Re-RSVP button for cancelled/cant_make_it users */}
                  {cancelledRsvp?.has_cancelled_rsvp && !existingRsvp && (
                    <button onClick={handleReRsvp} className="flex items-center gap-2 px-4 py-2 bg-emerald-50 hover:bg-emerald-100 rounded-full text-sm text-emerald-700 font-medium transition-colors border border-emerald-200" data-testid="re-rsvp-btn">
                      <RotateCcw className="w-4 h-4" /> Re-RSVP
                    </button>
                  )}
                </div>
              )}
              
              {/* Event Logo */}
              <div className={`text-center mb-6 ${isEventHost && envelopeOpened ? 'pt-14' : 'pt-2'}`}>
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 overflow-hidden">
                  {(event.logo_image || event.cover_image) ? (
                    <img src={getImageUrl(event.logo_image || event.cover_image)} alt={event.name} className="w-full h-full object-cover" />
                  ) : (
                    <PhygitalIcon className="w-10 h-10" variant="dark" />
                  )}
                </div>
                <h1 className="text-xl font-semibold text-gray-900 mb-0.5">{event.name}</h1>
                {event.subtitle && <p className="text-sm text-gray-600 mb-1">{event.subtitle}</p>}
                
                {/* Date/Time/Location */}
                {event?.display_settings?.show_date_time !== false && (
                  <p className="text-[11px] sm:text-sm text-gray-600 flex items-center justify-center flex-wrap gap-x-1">
                    {(event.date_tba || event.date) && (
                    <span>
                      {event.date_tba ? "Date TBA" : (formatSafeDate(event.date) || "Date TBA")}
                    </span>
                    )}
                    {(event.date_tba || event.date) && <span>•</span>}
                    <span>
                      {event.time_tba ? "Time TBA" : (
                        <>
                          {formatTime12Hour(event.time || event.start_time, getTimezoneAbbr(event.timezone || event.timezone_display))}
                          {event.timezone && false && ` ${event.timezone}`}
                        </>
                      )}
                    </span>
                    <span>•</span>
                    <span>{isVirtualEvt ? "Virtual Session" : (event.location || "Location TBA")}</span>
                  </p>
                )}
                
                {/* Instructor Info */}
                {event?.instructor && (
                  <div className="mt-3 flex items-center justify-center gap-2">
                    {event.instructor.headshot_url && (
                      <img src={event.instructor.headshot_url} alt={event.instructor.name} className="w-8 h-8 rounded-full object-cover" />
                    )}
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{event.instructor.name}</p>
                      {event.instructor.linkedin && (
                        <a href={ensureAbsoluteUrl(event.instructor.linkedin)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">LinkedIn</a>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Description */}
                {event.description && (
                  <p className="text-xs text-gray-500 mt-3 max-w-sm mx-auto">{event.description}</p>
                )}
              </div>
              
              {/* Hosted By with Social Icons */}
              {partners.length > 0 && (
                <div className="text-center mb-4">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Hosted by</p>
                  <div className="flex items-center justify-center gap-3">
                    {partners.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        {p.logo_url && <img src={p.logo_url} alt={p.name} className="h-5 object-contain opacity-70" onError={e => e.target.style.display='none'} />}
                        <span className="text-xs text-gray-600">{p.name}</span>
                      </div>
                    ))}
                  </div>
                  {/* Social Icons */}
                  {event?.host_social_links && Object.values(event.host_social_links).some(v => v) && (
                    <div className="flex items-center justify-center gap-3 mt-2">
                      {event.host_social_links.instagram && (
                        <a href={event.host_social_links.instagram.startsWith('http') ? event.host_social_links.instagram : `https://instagram.com/${event.host_social_links.instagram}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition-colors" data-testid="host-social-ig">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                        </a>
                      )}
                      {event.host_social_links.linkedin && (
                        <a href={event.host_social_links.linkedin.startsWith('http') ? event.host_social_links.linkedin : `https://linkedin.com/company/${event.host_social_links.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors" data-testid="host-social-li">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z"/></svg>
                        </a>
                      )}
                      {event.host_social_links.twitter && (
                        <a href={event.host_social_links.twitter.startsWith('http') ? event.host_social_links.twitter : `https://x.com/${event.host_social_links.twitter}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700 transition-colors" data-testid="host-social-x">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </a>
                      )}
                      {event.host_social_links.discord && (
                        <a href={event.host_social_links.discord} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-indigo-500 transition-colors" data-testid="host-social-discord">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286z"/></svg>
                        </a>
                      )}
                      {event.host_social_links.website && (
                        <a href={event.host_social_links.website.startsWith('http') ? event.host_social_links.website : `https://${event.host_social_links.website}`} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-600 transition-colors" data-testid="host-social-web">
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Name + Email Lock-in Form */}
              {event?.rsvps_closed ? (
                <div className="text-center p-4 bg-gray-100 rounded-xl border border-gray-200">
                  <p className="font-medium text-gray-700 mb-1">RSVPs are closed</p>
                  <p className="text-sm text-gray-500">Registration for this event has ended.</p>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100" data-testid="instructor-rsvp-lockin">
                  <h3 className="text-base font-semibold text-gray-900 text-center mb-1">RSVP</h3>
                  <p className="text-xs text-gray-500 text-center mb-4">Lock in your spot - we'll personalize after</p>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-700">First Name *</Label>
                        <Input
                          value={accountData.first_name}
                          onChange={(e) => {
                            const firstName = e.target.value;
                            const fullName = `${firstName} ${accountData.last_name}`.trim();
                            setAccountData(prev => ({ ...prev, first_name: firstName, name: fullName }));
                          }}
                          placeholder="First name"
                          className="mt-1 rounded-xl border-gray-200 h-10 text-sm bg-white"
                          data-testid="instructor-first-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-700">Last Name *</Label>
                        <Input
                          value={accountData.last_name}
                          onChange={(e) => {
                            const lastName = e.target.value;
                            const fullName = `${accountData.first_name} ${lastName}`.trim();
                            setAccountData(prev => ({ ...prev, last_name: lastName, name: fullName }));
                          }}
                          placeholder="Last name"
                          className="mt-1 rounded-xl border-gray-200 h-10 text-sm bg-white"
                          data-testid="instructor-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-700">Email *</Label>
                      <Input
                        type="email"
                        value={accountData.email}
                        onChange={(e) => setAccountData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="you@example.com"
                        className="mt-1 rounded-xl border-gray-200 h-10 text-sm bg-white"
                        data-testid="instructor-email"
                      />
                    </div>
                    
                    {/* Location Dropdown - for instructor Welcome step too */}
                    {event?.intake_settings?.collect_location && (
                      <div data-testid="instructor-location-selector">
                        <label className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-blue-500" />
                          Where are you based?
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={locationSearch || accountData.city || ""}
                            onChange={(e) => {
                              setLocationSearch(e.target.value);
                              setShowLocationDropdown(true);
                              setAccountData(prev => ({ ...prev, city: e.target.value }));
                            }}
                            onFocus={() => setShowLocationDropdown(true)}
                            placeholder="Search or select your city..."
                            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all"
                            data-testid="instructor-location-input"
                          />
                          {showLocationDropdown && configuredLocations.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-40 overflow-y-auto">
                              {configuredLocations
                                .filter(loc => {
                                  const q = (locationSearch || "").toLowerCase();
                                  if (!q) return true;
                                  return loc.city.toLowerCase().includes(q) || loc.state?.toLowerCase().includes(q) || loc.country?.toLowerCase().includes(q);
                                })
                                .map((loc, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => {
                                      setAccountData(prev => ({ ...prev, city: loc.full_label || loc.label }));
                                      setLocationSearch(loc.full_label || loc.label);
                                      setShowLocationDropdown(false);
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 transition-colors first:rounded-t-xl last:rounded-b-xl"
                                  >
                                    <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <span>{loc.label}</span>
                                    {loc.country !== "USA" && <span className="text-[10px] text-gray-400 ml-auto">{loc.country}</span>}
                                  </button>
                                ))
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => {
                        if (!accountData.first_name || !accountData.last_name || !accountData.email) {
                          toast.error("Please fill in your name and email");
                          return;
                        }
                        setCurrentStep(1);
                      }}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 text-sm rounded-full"
                      data-testid="instructor-begin-btn"
                    >
                      Continue
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  
                  <p className="text-[10px] text-gray-400 text-center mt-3">
                    Your profile stays with you on Phygital
                  </p>
                </div>
              )}
            </div>
          );
          
        case 1: // Builder Profile
          return (
            <div className="animate-fade-in" data-testid="instructor-step-1">
              <EventHeader event={event} showDescription={false} />
              <StepHeader step={1} total={3} title="About You" subtitle="Tell us about yourself" event={event} />
              
              {/* Headshot Upload */}
              <div className="flex items-center gap-4 mb-5">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center overflow-hidden border-2 border-white shadow-lg">
                    {accountData.headshot_url ? (
                      <img src={accountData.headshot_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl font-bold text-blue-400">{accountData.first_name?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-50 border">
                    <Camera className="w-4 h-4 text-gray-600" />
                    <input type="file" accept="image/*" className="hidden" data-testid="instructor-headshot-upload"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => setAccountData(prev => ({ ...prev, headshot_url: ev.target.result }));
                        reader.readAsDataURL(file);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const token = localStorage.getItem("session_token");
                          const res = await fetch(`${API}/upload/headshot`, {
                            method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', body: formData
                          });
                          if (res.ok) { const data = await res.json(); setAccountData(prev => ({ ...prev, headshot_url: data.url })); }
                        } catch (err) { console.error("Upload error:", err); }
                      }}
                    />
                  </label>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Profile Photo</p>
                  <p className="text-xs text-gray-500">Add a photo so attendees can recognize you</p>
                </div>
              </div>
              
              {/* Title & Company */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div>
                  <Label className="text-xs text-gray-700">Title / Role</Label>
                  <Input value={accountData.title || ''} onChange={e => setAccountData(prev => ({...prev, title: e.target.value}))} placeholder="e.g. CEO, Engineer, Designer" className="mt-1 rounded-xl border-gray-200 text-sm h-9" data-testid="instructor-title" />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">Company</Label>
                  <Input value={accountData.company} onChange={e => setAccountData(prev => ({...prev, company: e.target.value}))} placeholder="Your company" className="mt-1 rounded-xl border-gray-200 text-sm h-9" data-testid="instructor-company" />
                </div>
              </div>
              
              {/* Bio */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700">Bio</Label>
                <Textarea value={accountData.bio || ''} onChange={e => setAccountData(prev => ({...prev, bio: e.target.value}))} placeholder="A brief bio about you and your expertise..." className="mt-1 rounded-xl border-gray-200 text-sm min-h-[80px]" rows={3} data-testid="instructor-bio" />
              </div>
              
              {/* Social Links */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div>
                  <Label className="text-xs text-gray-700 text-blue-600">LinkedIn</Label>
                  <Input value={accountData.linkedin} onChange={e => {
                    let val = e.target.value;
                    if (val.includes('linkedin.com/in/')) val = val.split('linkedin.com/in/')[1]?.split('/')[0]?.split('?')[0] || val;
                    setAccountData(prev => ({...prev, linkedin: val}));
                  }} placeholder="handle or URL" className="mt-1 rounded-xl border-gray-200 text-xs h-9" data-testid="instructor-linkedin" />
                </div>
                <div>
                  <Label className="text-xs text-gray-700 text-pink-500">Instagram</Label>
                  <Input value={accountData.instagram} onChange={e => setAccountData(prev => ({...prev, instagram: e.target.value}))} placeholder="@handle" className="mt-1 rounded-xl border-gray-200 text-xs h-9" data-testid="instructor-instagram" />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">X / Twitter</Label>
                  <Input value={accountData.tiktok} onChange={e => setAccountData(prev => ({...prev, tiktok: e.target.value}))} placeholder="@handle" className="mt-1 rounded-xl border-gray-200 text-xs h-9" data-testid="instructor-twitter" />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">Website</Label>
                  <Input value={accountData.website || ''} onChange={e => setAccountData(prev => ({...prev, website: e.target.value}))} placeholder="https://..." className="mt-1 rounded-xl border-gray-200 text-xs h-9" data-testid="instructor-website" />
                </div>
              </div>
              
              {/* Builder Details */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-3 border border-indigo-100 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4 text-indigo-600" />
                  <h3 className="text-xs font-semibold text-gray-900">What You're Building</h3>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-600 mb-1 block">What are you building?</label>
                  <Input value={preferences.builder_current_build} onChange={e => setPreferences(prev => ({...prev, builder_current_build: e.target.value}))}
                    placeholder="Describe your project" className="rounded-lg border-gray-200 text-xs bg-white" data-testid="instructor-builder-project" />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Stage</label>
                    <div className="flex flex-wrap gap-1.5">
                      {["Idea", "MVP", "Growth", "Scaling"].map(stage => (
                        <button key={stage} onClick={() => setPreferences(prev => ({...prev, builder_stage: stage}))}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${preferences.builder_stage === stage ? "bg-purple-600 text-white" : "bg-white text-gray-700 border border-gray-200 hover:border-purple-300"}`}
                          data-testid={`builder-stage-${stage.toLowerCase()}`}
                        >{stage}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1"><Link2 className="w-3 h-3" /> Project Link</label>
                    <Input type="url" value={preferences.builder_project_url} onChange={e => setPreferences(prev => ({...prev, builder_project_url: e.target.value}))}
                      placeholder="https://..." className="rounded-lg border-gray-200 text-xs bg-white" data-testid="instructor-builder-url" />
                  </div>
                </div>
              </div>
              
              <StepNav onBack={() => setCurrentStep(0)} onNext={() => setCurrentStep(2)} />
            </div>
          );
          
        case 2: // Audience + Accessibility (combined)
          const digitalAccessOpts = isVirtualEvt ? DIGITAL_ACCESSIBILITY_OPTIONS : (event?.accessibility_options || ACCESSIBILITY_OPTIONS);
          const intakeSet = event?.intake_settings || {};
          
          return (
            <div className="animate-fade-in" data-testid="instructor-step-2">
              <EventHeader event={event} showDescription={false} />
              <StepHeader step={2} total={3} title="Session Content" subtitle="Tell us about your session" event={event} />
              
              {/* Session Title */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700 font-medium">Session Title *</Label>
                <Input value={instructorData.session_title} onChange={e => setInstructorData(prev => ({...prev, session_title: e.target.value}))} placeholder="e.g. Building AI Agents from Scratch" className="mt-1 rounded-xl border-gray-200 text-sm" data-testid="session-title-input" />
              </div>
              
              {/* Session Description */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700 font-medium">Session Description *</Label>
                <Textarea value={instructorData.session_description} onChange={e => setInstructorData(prev => ({...prev, session_description: e.target.value}))} placeholder="Describe what this session covers, who it's for, and what attendees will learn..." className="mt-1 rounded-xl border-gray-200 text-sm min-h-[100px]" rows={4} data-testid="session-description-input" />
              </div>
              
              {/* Session Format & Length */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <Label className="text-xs text-gray-700 font-medium">Format</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {["Workshop", "Q&A", "Demo", "Fireside Chat", "Panel"].map(fmt => (
                      <button key={fmt} onClick={() => setInstructorData(prev => ({...prev, session_format: fmt}))}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${instructorData.session_format === fmt ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        data-testid={`session-format-${fmt.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}
                      >{fmt}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-700 font-medium">Session Length</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(event?.session_length_options || ["1hr", "2hr"]).map(len => (
                      <button key={len} onClick={() => setInstructorData(prev => ({...prev, session_length: len}))}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${instructorData.session_length === len ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                        data-testid={`session-length-${len}`}
                      >{len}</button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Topics / Proposals (multiple) */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700 font-medium">Topics / Proposals</Label>
                <p className="text-[10px] text-gray-500 mb-2">Add topics or proposals you'd like to cover. You can add multiple.</p>
                <div className="space-y-2">
                  {(instructorData.session_topics || []).map((topic, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input value={topic} onChange={e => {
                        const newTopics = [...instructorData.session_topics];
                        newTopics[idx] = e.target.value;
                        setInstructorData(prev => ({...prev, session_topics: newTopics}));
                      }} placeholder={`Topic ${idx + 1}`} className="rounded-lg border-gray-200 text-sm flex-1" data-testid={`session-topic-${idx}`} />
                      <button onClick={() => {
                        const newTopics = instructorData.session_topics.filter((_, i) => i !== idx);
                        setInstructorData(prev => ({...prev, session_topics: newTopics}));
                      }} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <button onClick={() => setInstructorData(prev => ({...prev, session_topics: [...(prev.session_topics || []), ""]}))}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium" data-testid="add-topic-btn">
                    <Plus className="w-3.5 h-3.5" /> Add Topic
                  </button>
                </div>
              </div>
              
              {/* Key Learnings */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700 font-medium">Key Takeaways</Label>
                <Textarea value={instructorData.key_learnings} onChange={e => setInstructorData(prev => ({...prev, key_learnings: e.target.value}))} placeholder="What will attendees take away from this session? List key learnings..." className="mt-1 rounded-xl border-gray-200 text-sm min-h-[70px]" rows={3} data-testid="key-learnings-input" />
              </div>
              
              {/* Questions for Audience */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700 font-medium">Questions for Your Audience</Label>
                <Textarea value={instructorData.questions_for_audience} onChange={e => setInstructorData(prev => ({...prev, questions_for_audience: e.target.value}))} placeholder="Any questions you want to ask attendees before or during the session?" className="mt-1 rounded-xl border-gray-200 text-sm min-h-[60px]" rows={2} data-testid="questions-for-audience-input" />
              </div>
              
              {/* Resources (multiple) */}
              <div className="mb-4">
                <Label className="text-xs text-gray-700 font-medium">Resources to Share</Label>
                <p className="text-[10px] text-gray-500 mb-2">Slides, docs, links  - will be shared with attendees after the session.</p>
                <div className="space-y-2">
                  {(instructorData.resources || []).map((res, idx) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded-lg border border-gray-200 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Input value={res.title || ''} onChange={e => {
                          const newRes = [...instructorData.resources];
                          newRes[idx] = {...newRes[idx], title: e.target.value};
                          setInstructorData(prev => ({...prev, resources: newRes}));
                        }} placeholder="Resource title" className="rounded-lg border-gray-200 text-xs flex-1 h-8" data-testid={`resource-title-${idx}`} />
                        <button onClick={() => {
                          const newRes = instructorData.resources.filter((_, i) => i !== idx);
                          setInstructorData(prev => ({...prev, resources: newRes}));
                        }} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <Input value={res.url || ''} onChange={e => {
                        const newRes = [...instructorData.resources];
                        newRes[idx] = {...newRes[idx], url: e.target.value};
                        setInstructorData(prev => ({...prev, resources: newRes}));
                      }} placeholder="https://..." className="rounded-lg border-gray-200 text-xs h-8" data-testid={`resource-url-${idx}`} />
                    </div>
                  ))}
                  <button onClick={() => setInstructorData(prev => ({...prev, resources: [...(prev.resources || []), {title: "", url: "", description: ""}]}))}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium" data-testid="add-resource-btn">
                    <Plus className="w-3.5 h-3.5" /> Add Resource
                  </button>
                </div>
              </div>
              
              <StepNav onBack={() => setCurrentStep(1)} onNext={() => setCurrentStep(3)} />
            </div>
          );
          
        case 3: // Confirm + Consent + Submit (Instructor)
          return (
            <div className="animate-fade-in" data-testid="instructor-step-3">
              <EventHeader event={event} showDescription={false} />
              <StepHeader step={3} total={3} title="Confirm" subtitle="Review and submit your session details" event={event} />
              
              {/* Summary Card */}
              <div className="space-y-3 mb-5">
                {instructorData.session_title && (
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-[10px] text-blue-500 font-medium uppercase tracking-wide">Session</p>
                    <p className="text-sm font-semibold text-gray-900">{instructorData.session_title}</p>
                    {instructorData.session_format && <span className="text-xs text-blue-600">{instructorData.session_format} &middot; {instructorData.session_length || '2hr'}</span>}
                  </div>
                )}
                {instructorData.session_topics?.filter(t => t.trim()).length > 0 && (
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-1">Topics</p>
                    <div className="flex flex-wrap gap-1">
                      {instructorData.session_topics.filter(t => t.trim()).map((t, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white text-xs rounded-full border text-gray-700">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {instructorData.resources?.filter(r => r.title || r.url).length > 0 && (
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-1">Resources ({instructorData.resources.filter(r => r.title || r.url).length})</p>
                    {instructorData.resources.filter(r => r.title || r.url).map((r, i) => (
                      <p key={i} className="text-xs text-gray-700">{r.title || r.url}</p>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Legal Consent */}
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={accountData.beehiiv_consent || false}
                    onChange={(e) => setAccountData(prev => ({...prev, beehiiv_consent: e.target.checked}))}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500" data-testid="instructor-consent" />
                  <p className="text-[10px] text-gray-600 leading-snug">
                    I agree to the <a href="/terms" target="_blank" className="underline text-gray-700">Terms of Service</a> & <a href="/privacy" target="_blank" className="underline text-gray-700">Privacy Policy</a>. I understand my session data will be shared with the event host.
                  </p>
                </label>
              </div>
              
              {/* Submit */}
              <div className="flex gap-2 mb-4">
                <Button variant="outline" onClick={() => setCurrentStep(2)} className="flex-1 h-11 rounded-full text-sm border-gray-200">Back</Button>
                <Button
                  onClick={handleInstructorSubmit}
                  disabled={!accountData.beehiiv_consent || saving || !instructorData.session_title}
                  className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm"
                  data-testid="instructor-submit-btn"
                >
                  {saving ? "Submitting..." : "Submit Session"}
                </Button>
              </div>
            </div>
          );
          
        default:
          return null;
      }
    }
    
    // ============================================================================
    // STANDARD FLOW (non-instructor events)
    // ============================================================================
    switch (currentStep) {
      case 0: // Welcome with preview
        return (
          <div className="animate-fade-in flex flex-col justify-start pt-4 pb-24 relative">
            {/* Host Navigation Bar - Only for hosts/cohosts, appears after envelope */}
            {isEventHost && envelopeOpened && (
              <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-4 py-2">
                <div className="max-w-md lg:max-w-2xl mx-auto flex items-center justify-between">
                  <Link to="/" className="flex items-center gap-2">
                    {(event?.logo_image || event?.cover_image) ? (
                      <img src={getImageUrl(event.logo_image || event.cover_image)} alt={event?.name || "Event"} className="h-7 w-7 rounded-full object-cover" />
                    ) : (
                      <PhygitalLogo className="h-7" variant="dark" />
                    )}
                  </Link>
                  <button
                    onClick={() => navigate('/me')}
                    className="flex items-center gap-2"
                  >
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <Users className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* Action Buttons for Signed-in Users */}
            {isAuthenticated && (
              <div className="mb-4 flex flex-wrap items-center justify-center gap-2">
                {/* Dashboard Button - Only for hosts/cohosts */}
                {isEventHost && (
                  <button
                    onClick={() => navigate(`/host/v2/${event.event_id}`)}
                    className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 rounded-full text-sm text-white font-medium transition-colors"
                    data-testid="event-dashboard-btn"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </button>
                )}
                
                {/* View Confirmation Button - If user has existing RSVP */}
                {existingRsvp && existingRsvp.rsvp_id && (
                  <button
                    onClick={() => navigate(`/confirmation/${existingRsvp.rsvp_id}`)}
                    className="flex items-center gap-2 px-4 py-2 bg-black/10 hover:bg-black/20 backdrop-blur-sm rounded-full text-sm text-gray-800 font-medium transition-colors border border-black/5"
                    data-testid="view-confirmation-btn"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    View Confirmation
                  </button>
                )}
                
                {/* Claim Event - Shows for super_admin when event is unclaimed */}
                {!isEventHost && user?.role === 'super_admin' && 
                  ['', 'admin_default', 'system', 'admin', null].includes(event?.host_user_id) && (
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("session_token");
                        await axios.post(`${API}/events/${event.event_id}/claim`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success("Event claimed! You can now manage it.");
                        const res = await axios.get(`${API}/events/${eventSlug}`);
                        setEvent(res.data);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Failed to claim event");
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 rounded-full text-sm text-white font-medium transition-opacity"
                    data-testid="claim-event-btn"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Claim Event
                  </button>
                )}
                
                {/* Re-RSVP button for cancelled/cant_make_it users */}
                {cancelledRsvp?.has_cancelled_rsvp && !existingRsvp && (
                  <button
                    onClick={handleReRsvp}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 hover:bg-emerald-100 rounded-full text-sm text-emerald-700 font-medium transition-colors border border-emerald-200"
                    data-testid="re-rsvp-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Re-RSVP
                  </button>
                )}
              </div>
            )}
            
            {/* Large Logo for Welcome - syncs to host override -> category
                default. Never falls back to the Phygital wordmark. The light
                category color forms the rounded background; the canonical
                category icon is the cover image. */}
            <div className={`text-center mb-6 ${isEventHost && envelopeOpened ? 'pt-14' : 'pt-2'}`}>
              {(() => {
                const catId = event?.category_id || (event?.pilot_category ? `cat_${event.pilot_category}` : null);
                const catBranding = (window.__categoryBrandingCache || {})[catId] || null;
                const lightBg = catBranding?.light_hex || "#f3f4f6";
                const iconSrc = event.logo_image || event.cover_image || catBranding?.icon_url;
                return (
                  <div
                    className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3 overflow-hidden"
                    style={{ backgroundColor: lightBg }}
                  >
                    {iconSrc ? (
                      <img
                        src={getImageUrl(iconSrc)}
                        alt={event.name}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <span className="text-xl font-bold" style={{ color: catBranding?.dark_hex || "#0a0a0a" }}>
                        {(event?.name || "").trim().slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                );
              })()}
              <h1 className="text-xl font-semibold text-gray-900 mb-0.5">{event.name}</h1>
              {event.subtitle && <p className="text-sm text-gray-600 mb-1">{event.subtitle}</p>}
              
              {/* Date/Time/Location - skip the whole row for invite flows
                  where the scheduled time is captured by the slot picker
                  below, and skip when no location/date/time exist at all. */}
              {event?.display_settings?.show_date_time !== false && !hostInvite && (() => {
                const hasDate = !!(event.date_tba || event.date);
                const hasTime = !!(event.time_tba || event.time || event.start_time);
                const hasAddress = !!(event.location || event.city);
                const revealAfterConfirm = event?.location_reveal_after_confirm !== false;
                const showLocation = event?.display_settings?.show_location !== false
                  && serviceSettings.location_visibility !== "hidden"
                  && (hasAddress || !revealAfterConfirm);
                if (!hasDate && !hasTime && !showLocation) return null;
                return (
                  <p className="text-[11px] sm:text-sm text-gray-600 flex items-center justify-center flex-wrap gap-x-1">
                    {hasDate && (
                      <span>
                        {event.date_tba ? "Date TBA" : (formatSafeDate(event.date) || "Date TBA")}
                      </span>
                    )}
                    {hasDate && hasTime && <span>•</span>}
                    {hasTime && (
                      <span>
                        {event.time_tba ? "Time TBA" : (
                          <>
                            {formatTime12Hour(event.time || event.start_time, getTimezoneAbbr(event.timezone || event.timezone_display))}
                            {event.end_time && ` - ${formatTime12Hour(event.end_time)}`}
                          </>
                        )}
                      </span>
                    )}
                    {showLocation && (
                      <>
                        {(hasDate || hasTime) && <span>•</span>}
                        <span className="inline-flex items-center gap-1">
                          {hasAddress ? (event.location || event.city) : "Location TBA"}
                        {/* Info icon with tooltip if location will be sent week of event */}
                        {event.location_after_rsvp && (
                    <span 
                      className="relative group cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const tooltip = e.currentTarget.querySelector('.tooltip-content');
                        if (tooltip) {
                          tooltip.classList.toggle('opacity-0');
                          tooltip.classList.toggle('opacity-100');
                          setTimeout(() => {
                            tooltip.classList.add('opacity-0');
                            tooltip.classList.remove('opacity-100');
                          }, 3000);
                        }
                      }}
                    >
                      <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 transition-colors" />
                      <span className="tooltip-content absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-[10px] rounded-lg whitespace-nowrap opacity-0 transition-opacity duration-200 pointer-events-none z-50 shadow-lg">
                        Location sent week of event
                        <span className="absolute top-full right-2 border-4 border-transparent border-t-gray-900"></span>
                      </span>
                    </span>
                  )}
                </span>
                    </>
                  )}
                </p>
                );
              })()}
              
              {/* Description - controlled by display_settings */}
              {event?.display_settings?.show_description !== false && event.description && (
                <p 
                  className="text-gray-600 mt-2 max-w-xs mx-auto leading-relaxed"
                  style={{ fontSize: `${parseInt(event?.display_settings?.description_font_size) || 12}px` }}
                >
                  {event.description}
                </p>
              )}
            </div>

            {/* Artist credit (Iter 9.47.t) - surfaces the artist's image, bio,
                and social handles right below the event header. Plants the
                creator-economy hook for hosts who are also influencers. */}
            {event?.artist?.artist_user_id && (
              <div
                className="flex items-center gap-3 max-w-md mx-auto mb-4 p-3 rounded-2xl bg-stone-50 border border-stone-100"
                data-testid="event-artist-credit"
              >
                <img
                  src={getImageUrl(event.artist.artist_image)}
                  alt={event.artist.artist_name}
                  className="w-12 h-12 rounded-full object-cover shrink-0 ring-2 ring-white"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-stone-500">Your makeup artist</p>
                  <p className="text-sm font-semibold text-stone-900 truncate" data-testid="artist-name">{event.artist.artist_name}</p>
                  {event.artist.artist_bio && (
                    <p className="text-[11px] text-stone-600 line-clamp-2 mt-0.5">{event.artist.artist_bio}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    {event.artist.artist_instagram && (
                      <a
                        href={`https://instagram.com/${String(event.artist.artist_instagram).replace(/^@/, "")}`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] text-stone-500 hover:text-purple-600"
                        data-testid="artist-ig-link"
                      >
                        IG @{String(event.artist.artist_instagram).replace(/^@/, "")}
                      </a>
                    )}
                    {event.artist.artist_tiktok && (
                      <a
                        href={`https://tiktok.com/@${String(event.artist.artist_tiktok).replace(/^@/, "")}`}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] text-stone-500 hover:text-purple-600"
                        data-testid="artist-tt-link"
                      >
                        TikTok @{String(event.artist.artist_tiktok).replace(/^@/, "")}
                      </a>
                    )}
                    {event.artist.artist_website && (
                      <a
                        href={event.artist.artist_website}
                        target="_blank" rel="noreferrer"
                        className="text-[10px] text-stone-500 hover:text-purple-600"
                        data-testid="artist-web-link"
                      >
                        Website
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Wedding-intake collapsible (Iter 9.47.v) - one collapsible section
                with the 12 wedding-specific bridal logistics fields. Only renders
                when the host event has wedding_specific_intake=true (or a wedding
                parent dashboard). Standard intake still flows separately above
                this block. */}
            {weddingIntake.applies && weddingIntake.fields.length > 0 && (
              <div
                className="max-w-md mx-auto mb-4 rounded-2xl border border-stone-200 bg-white overflow-hidden"
                data-testid="wedding-intake-collapsible"
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50 transition"
                  onClick={() => setWeddingExpanded((v) => !v)}
                  data-testid="wedding-intake-toggle"
                >
                  <span>
                    <p className="text-sm font-semibold text-stone-900">Wedding Details</p>
                    <p className="text-[11px] text-stone-500">A few quick logistics so the day goes smoothly.</p>
                  </span>
                  <span className="text-stone-400 text-lg leading-none">{weddingExpanded ? "−" : "+"}</span>
                </button>
                {weddingExpanded && (
                  <div className="px-4 pb-4 pt-1 space-y-3 border-t border-stone-100" data-testid="wedding-intake-fields">
                    {weddingIntake.fields.map((f) => {
                      const v = weddingAnswers[f.field_id] ?? "";
                      const onChange = (val) => setWeddingAnswers((p) => ({ ...p, [f.field_id]: val }));
                      // Respect cascade visibility (e.g. trial_preferred_date only when trial_requested=true)
                      const cascade = f.shown_when || {};
                      const cascadeBlocked = Object.entries(cascade).some(([k, expected]) => {
                        if (k.startsWith("field.")) {
                          const dep = k.slice(6);
                          return weddingAnswers[dep] !== expected;
                        }
                        return false;
                      });
                      if (cascadeBlocked) return null;
                      return (
                        <div key={f.field_id} data-testid={`wedding-field-${f.field_id}`}>
                          <label className="block text-[11px] font-semibold text-stone-700 mb-1">
                            {f.label}{f.default_required && <span className="text-rose-500"> *</span>}
                          </label>
                          {f.help_text && <p className="text-[10px] text-stone-400 mb-1">{f.help_text}</p>}
                          {f.type === "select" && (
                            <select
                              className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2 bg-white"
                              value={v}
                              onChange={(e) => onChange(e.target.value)}
                            >
                              <option value="">Select…</option>
                              {(f.options || []).map((o) => (
                                <option key={o.id} value={o.id}>{o.label}</option>
                              ))}
                            </select>
                          )}
                          {f.type === "boolean" && (
                            <label className="inline-flex items-center gap-2 text-xs">
                              <input
                                type="checkbox"
                                checked={v === true}
                                onChange={(e) => onChange(e.target.checked)}
                              />
                              Yes
                            </label>
                          )}
                          {f.type === "number" && (
                            <input
                              type="number"
                              className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2"
                              value={v}
                              onChange={(e) => onChange(e.target.value)}
                            />
                          )}
                          {(f.type === "text" || f.type === "time" || f.type === "date") && (
                            <input
                              type={f.type === "text" ? "text" : f.type}
                              className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2"
                              value={v}
                              onChange={(e) => onChange(e.target.value)}
                            />
                          )}
                          {f.type === "textarea" && (
                            <textarea
                              rows={2}
                              className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2"
                              value={v}
                              onChange={(e) => onChange(e.target.value)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {/* Iter 9.48d - Service-tag conditional intake     */}
            {/* (bridal / instructor / travel)                  */}
            {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
            {tagIntake.applies && tagIntake.groups.length > 0 && (
              <div className="mb-4 space-y-3" data-testid="tag-intake-section">
                {tagIntake.groups.map((g) => (
                  <div
                    key={g.tag}
                    className={`rounded-xl border overflow-hidden ${
                      g.tag === 'bridal' ? 'border-rose-200 bg-rose-50/30' :
                      g.tag === 'instructor' ? 'border-indigo-200 bg-indigo-50/30' :
                      g.tag === 'travel' ? 'border-sky-200 bg-sky-50/30' :
                      'border-stone-200 bg-stone-50/30'
                    }`}
                    data-testid={`tag-intake-${g.tag}`}
                  >
                    <div className="px-4 py-3 border-b border-stone-100/60 flex items-center justify-between">
                      <h3 className="text-xs font-semibold text-stone-700 uppercase tracking-wide">{g.label}</h3>
                      <span className={`text-[10px] capitalize px-2 py-0.5 rounded-full ${
                        g.tag === 'bridal' ? 'bg-rose-100 text-rose-700' :
                        g.tag === 'instructor' ? 'bg-indigo-100 text-indigo-700' :
                        g.tag === 'travel' ? 'bg-sky-100 text-sky-700' :
                        'bg-stone-100 text-stone-700'
                      }`}>{g.tag}</span>
                    </div>
                    <div className="p-4 space-y-3">
                      {g.fields.map((f) => {
                        const ans = (tagAnswers[g.tag] || {})[f.key] ?? "";
                        const setAns = (val) =>
                          setTagAnswers((p) => ({ ...p, [g.tag]: { ...(p[g.tag] || {}), [f.key]: val } }));
                        return (
                          <div key={f.key} data-testid={`tag-field-${g.tag}-${f.key}`}>
                            <label className="block text-[11px] font-semibold text-stone-700 mb-1">
                              {f.label}{f.required && <span className="text-rose-500"> *</span>}
                            </label>
                            {f.type === "single" && (
                              <select
                                className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2 bg-white"
                                value={ans}
                                onChange={(e) => setAns(e.target.value)}
                              >
                                <option value="">Select…</option>
                                {(f.options || []).map((o) => (<option key={o} value={o}>{o}</option>))}
                              </select>
                            )}
                            {f.type === "textarea" && (
                              <textarea
                                rows={2}
                                className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2 bg-white"
                                value={ans}
                                onChange={(e) => setAns(e.target.value)}
                              />
                            )}
                            {(f.type === "text" || f.type === "number" || f.type === "date" || f.type === "time") && (
                              <input
                                type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                                className="w-full text-xs border border-stone-300 rounded-lg px-2 py-2 bg-white"
                                value={ans}
                                onChange={(e) => setAns(e.target.value)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Ways to make your experience, yours - controlled by intake_settings from pilot config */}
            {event?.display_settings?.show_experience_section !== false && (
            <div className="mb-3">
              <h2 className="text-center text-sm font-medium text-gray-900 mb-3">
                Ways to make your experience, yours
              </h2>
              
              <div className="flex justify-center gap-4 flex-wrap">
                {/* Show icons based on pilot config - requested_defaults and intake settings */}
                
                {/* Face Default - for makeup/beauty providers */}
                {(event.intake_settings?.requested_defaults?.includes('face')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-pink-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Face</span>
                  </div>
                )}
                
                {/* Hair Default - for hair providers */}
                {(event.intake_settings?.requested_defaults?.includes('hair')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                      <Scissors className="w-5 h-5 text-pink-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Hair</span>
                  </div>
                )}
                
                {/* Nails Default */}
                {(event.intake_settings?.requested_defaults?.includes('nails')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                      <Paintbrush className="w-5 h-5 text-rose-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Nails</span>
                  </div>
                )}
                
                {/* Lash Default */}
                {(event.intake_settings?.requested_defaults?.includes('lash')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                      <Eye className="w-5 h-5 text-violet-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Lashes</span>
                  </div>
                )}
                
                {/* Wellness Default */}
                {(event.intake_settings?.requested_defaults?.includes('wellness')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-teal-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Wellness</span>
                  </div>
                )}
                
                {/* Travel Default */}
                {(event.intake_settings?.requested_defaults?.includes('travel')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-sky-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Travel</span>
                  </div>
                )}
                
                {/* Style/Retail Default */}
                {(event.intake_settings?.requested_defaults?.includes('style')) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Style</span>
                  </div>
                )}
                
                {/* Dietary - only if collect_dietary is true */}
                {(event.intake_settings?.collect_dietary === true) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Dietary</span>
                  </div>
                )}
                
                {/* Accessibility - only if collect_accessibility is true */}
                {(event.intake_settings?.collect_accessibility === true) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Accessibility className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Access</span>
                  </div>
                )}
                
                {/* Interests - only for event dashboard types */}
                {(event.intake_settings?.collect_interests === true) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-pink-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Interests</span>
                  </div>
                )}
                
                {/* Vibe - only for event dashboard types */}
                {(event.intake_settings?.collect_vibe === true) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Vibe</span>
                  </div>
                )}
                
                {/* Inspo - show if inspo_folders exist */}
                {(event.intake_settings?.inspo_folders?.length > 0) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-violet-600" />
                    </div>
                    <span className="text-[10px] text-gray-500">Inspo</span>
                  </div>
                )}
                
                {/* Tap In - Uses actual logo from database - Shows when tapin_enabled OR playlist_link exists */}
                {(event.intake_settings?.tapin_enabled || event.tap_in_invite_link || event.playlist_link) && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_syncnow/artifacts/dv6os836_tap%20in%20app%20cover.png" 
                        alt="Tap In" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-[10px] text-gray-500">Tap In</span>
                  </div>
                )}
              </div>
            </div>
            )}
            
            {/* Info text - controlled by display_settings */}
            {event?.display_settings?.show_experience_section !== false && (
              <>
                <p className="text-center text-xs text-gray-600 mb-4 px-2">
                  Share your preferences once, use them for any event.
                </p>
                <p className="text-center text-[10px] text-gray-500 mb-4 -mt-2">
                  Your profile stays with you on Phygital.
                </p>
              </>
            )}
            
            {/* Partners Preview on Welcome - Controlled by show_hosted_by OR display_settings */}
            {(event?.show_hosted_by !== false && event?.display_settings?.show_hosted_by !== false) && partners.length > 0 && (
              <div className="mb-4">
                <p className="text-center text-[10px] text-gray-500 uppercase tracking-wider mb-2">Hosted By</p>
                <div className="flex justify-center items-center gap-4 flex-wrap">
                  {partners.filter(p => p.featured && p.show_in_hosted_by !== false && !p.name?.toLowerCase().includes('hilton') && !p.name?.toLowerCase().includes('ocean')).slice(0, 3).map((partner) => {
                    const isColdWater = partner.name?.toLowerCase().includes('cold water');
                    const isDataDreaming = partner.name?.toLowerCase().includes('data dreaming');
                    const logoHeight = isColdWater || isDataDreaming ? 'h-10' : 'h-7';
                    const linkUrl = ensureAbsoluteUrl(partner.cta_url || partner.website);
                    
                    const content = (
                      <div className="flex flex-col items-center">
                        {partner.logo_url ? (
                          <img 
                            src={partner.logo_url} 
                            alt={partner.name} 
                            className={`${logoHeight} object-contain opacity-70`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                            }}
                          />
                        ) : null}
                        <span 
                          className={`text-sm font-medium text-gray-600 ${partner.logo_url ? 'hidden' : ''} ${linkUrl ? 'hover:text-purple-600 transition-colors' : ''}`}
                          style={{ display: partner.logo_url ? 'none' : 'block' }}
                        >
                          {partner.name}
                        </span>
                      </div>
                    );
                    
                    return linkUrl ? (
                      <a 
                        key={partner.partner_id} 
                        href={linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:opacity-80 transition-opacity"
                      >
                        {content}
                      </a>
                    ) : (
                      <div key={partner.partner_id}>
                        {content}
                      </div>
                    );
                  })}
                  {partners.filter(p => !p.featured && p.show_in_hosted_by !== false && !p.name?.toLowerCase().includes('hilton') && !p.name?.toLowerCase().includes('ocean')).length > 0 && partners.filter(p => p.featured && p.show_in_hosted_by !== false).length === 0 && (
                    partners.filter(p => p.show_in_hosted_by !== false && !p.name?.toLowerCase().includes('hilton') && !p.name?.toLowerCase().includes('ocean')).slice(0, 3).map((partner) => {
                      const isColdWater = partner.name?.toLowerCase().includes('cold water');
                      const isDataDreaming = partner.name?.toLowerCase().includes('data dreaming');
                      const logoHeight = isColdWater || isDataDreaming ? 'h-10' : 'h-7';
                      
                      return (
                        <div key={partner.partner_id} className="flex flex-col items-center">
                          {partner.logo_url ? (
                            <img 
                              src={partner.logo_url} 
                              alt={partner.name} 
                              className={`${logoHeight} object-contain opacity-70`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                          ) : null}
                          <span 
                            className={`text-sm font-medium text-gray-600 ${partner.logo_url ? 'hidden' : ''}`}
                            style={{ display: partner.logo_url ? 'none' : 'block' }}
                          >
                            {partner.name}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            
            {/* External Website Link - controlled by display_settings */}
            {event?.display_settings?.show_learn_more !== false && event?.external_website_url && (
              <div className="mb-4 text-center">
                <a 
                  href={ensureAbsoluteUrl(event.external_website_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-700 underline underline-offset-4"
                >
                  <ExternalLink className="w-3 h-3" />
                  {event?.learn_more_text || 'Learn more about this event'}
                </a>
              </div>
            )}
            
            {/* Check event status - Cancelled, Rescheduled, Pre-invite mode, RSVPs closed, or normal */}
            {event?.status === 'cancelled' ? (
              <div className="text-center p-6 bg-red-50 rounded-xl border border-red-200" data-testid="event-cancelled-banner">
                <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="font-semibold text-red-700 mb-1">Event Cancelled</p>
                <p className="text-sm text-red-500">This event has been cancelled. We apologize for any inconvenience.</p>
              </div>
            ) : event?.status === 'rescheduled' ? (
              <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-200" data-testid="event-rescheduled-banner">
                <Calendar className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="font-semibold text-blue-700 mb-1">Event Rescheduled</p>
                <p className="text-sm text-blue-500">
                  New date: {event?.rescheduled_date ? new Date(event.rescheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'TBA'}
                </p>
                {!event?.rsvps_closed && (
                  <Button 
                    onClick={() => setCurrentStep(getNextStep(0))}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"
                    data-testid="rsvp-rescheduled-btn"
                  >
                    {slotText(eventPageSlots, 'rescheduled_cta', 'RSVP for New Date')}
                  </Button>
                )}
              </div>
            ) : (event?.status === 'pre_invite' || event?.pre_invite_mode?.enabled) ? (
              <PreInviteForm event={event} user={user} />
            ) : event?.rsvps_closed ? (
              <div className="text-center p-4 bg-gray-100 rounded-xl border border-gray-200" data-testid="rsvps-closed-banner">
                <p className="font-medium text-gray-700 mb-1" data-testid="rsvps-closed-title">{slotText(eventPageSlots, 'rsvps_closed_title', 'RSVPs are closed')}</p>
                <p className="text-sm text-gray-500" data-testid="rsvps-closed-body">{slotText(eventPageSlots, 'rsvps_closed_body', 'Registration for this experience has ended.')}</p>
              </div>
            ) : (
              <>
                {/* Acuity-prefilled invite: read-only confirmation block.
                    The slot is locked in Acuity, so we don't render a slot
                    picker. Client just confirms what they booked and continues
                    to the preferences intake. The envelope still renders for
                    everyone (handled above by showEnvelope). */}
                {rsvpPrefill && rsvpPrefill.invite_source === "acuity_share_preferences" && (
                  <div
                    className="mb-4 p-4 bg-emerald-50 rounded-2xl border border-emerald-200"
                    data-testid="acuity-prefill-confirmation-block"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-medium uppercase tracking-wide" data-testid="acuity-confirmed-label">
                        {slotText(eventPageSlots, 'acuity_confirmed_label', 'Confirmed via Acuity')}
                      </span>
                    </div>
                    <p className="text-sm text-stone-900 mb-2" data-testid="acuity-confirmed-body">
                      {slotText(eventPageSlots, 'acuity_confirmed_body', 'Your appointment is already booked. Take 60 seconds to share your preferences so we can prep for you.')}
                    </p>
                    <div className="space-y-1.5 mt-3">
                      {rsvpPrefill.service_name && (
                        <p className="text-xs text-stone-700" data-testid="acuity-prefill-service">
                          <span className="text-stone-500">Service:</span>{" "}
                          <span className="text-stone-900 font-medium">{rsvpPrefill.service_name}</span>
                        </p>
                      )}
                      {rsvpPrefill.appointment_date && (
                        <p className="text-xs text-stone-700" data-testid="acuity-prefill-date">
                          <span className="text-stone-500">When:</span>{" "}
                          <span className="text-stone-900 font-medium">
                            {(() => {
                              try {
                                const d = new Date(rsvpPrefill.appointment_date);
                                return d.toLocaleDateString(undefined, { weekday: "short", month: "long", day: "numeric", year: "numeric" })
                                  + " at " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                              } catch (_e) { return rsvpPrefill.appointment_date; }
                            })()}
                          </span>
                        </p>
                      )}
                      {rsvpPrefill.organization?.display_name && (
                        <p className="text-xs text-stone-700">
                          <span className="text-stone-500">With:</span>{" "}
                          <span className="text-stone-900 font-medium">{rsvpPrefill.organization.display_name}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Amayah-style native invite slot picker. Only renders when
                    ?invite_token=... is present. Inherits the canonical
                    envelope -> welcome -> intake -> confirmation flow; this
                    just adds the host's pre-offered availability + a way to
                    request an alternate time. */}
                {hostInvite && (
                  <div className="mb-4 p-4 bg-stone-50 rounded-2xl border border-stone-200" data-testid="invite-welcome-block">
                    {hostInvite.note && (
                      <p className="text-sm text-stone-700 italic mb-3">"{hostInvite.note}"</p>
                    )}
                    {hostInvite.service_label && (
                      <p className="text-xs text-stone-500 mb-2">
                        Service: <span className="text-stone-900 font-medium">{hostInvite.service_label}</span>
                      </p>
                    )}
                    <p className="text-sm font-medium text-stone-900 mb-2">Pick a time that works</p>
                    <div className="space-y-2">
                      {(hostInvite.slots || []).map((slot) => {
                        const selected = selectedInviteSlot === slot.slot_id;
                        let label = slot.label;
                        if (!label && slot.start_iso) {
                          try {
                            const s = new Date(slot.start_iso);
                            const e = slot.end_iso ? new Date(slot.end_iso) : null;
                            const day = s.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
                            const st = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                            const en = e ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : null;
                            label = en ? `${day}, ${st} to ${en}` : `${day}, ${st}`;
                          } catch (_e) {
                            label = slot.start_iso;
                          }
                        }
                        return (
                          <button
                            key={slot.slot_id}
                            type="button"
                            onClick={() => { setSelectedInviteSlot(slot.slot_id); setRequestAlternateTime(false); }}
                            className={`w-full text-left text-sm px-3 py-2 rounded-xl border transition-colors ${selected ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-900 border-stone-200 hover:border-stone-400"}`}
                            data-testid={`invite-slot-${slot.slot_id}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => { setRequestAlternateTime(!requestAlternateTime); setSelectedInviteSlot(null); }}
                        className={`w-full text-left text-sm px-3 py-2 rounded-xl border-dashed border transition-colors ${requestAlternateTime ? "bg-stone-100 border-stone-400 text-stone-900" : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"}`}
                        data-testid="invite-request-alternate"
                      >
                        None of these work, request another time
                      </button>
                    </div>
                  </div>
                )}
                <Button
                  onClick={() => setCurrentStep(getNextStep(0))}
                  disabled={hostInvite && !selectedInviteSlot && !requestAlternateTime}
                  className="w-auto mx-auto px-8 bg-gray-900 hover:bg-gray-800 text-white py-2.5 text-sm rounded-full shadow-md flex items-center gap-2 disabled:opacity-40"
                  data-testid="begin-button"
                >
                  Begin
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        );

      case 1: // Service Selection (providers) or Accessibility (events)
        if (isProviderFlow) {
          // Check if service is pre-selected from Acuity booking
          const urlParams = new URLSearchParams(window.location.search);
          const acuityServiceId = urlParams.get('acuity_service');
          const isAcuityLocked = !!acuityServiceId;
          
          // If Acuity-locked and not yet selected, auto-select
          if (isAcuityLocked && !selectedService) {
            const acuitySvc = availableServices.find(s => s.service_id === acuityServiceId || s.acuity_id === acuityServiceId);
            if (acuitySvc) setSelectedService(acuitySvc);
          }
          
          // SERVICE SELECTION STEP
          return (
            <div className="animate-fade-in" data-testid="step-service-selection">
              <EventHeader event={event} showDescription={false} />
              <StepHeader 
                step={getStepProgress(1).current} 
                total={getStepProgress(1).total} 
                title={isAcuityLocked ? "Your Booked Service" : "Select Your Service"} 
                subtitle={isAcuityLocked ? "Booked via Acuity, add optional extras below" : "Choose the experience you'd like to book"}
                event={event}
                selectedService={selectedService}
              />
              
              {isAcuityLocked && (
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                  <p className="text-[10px] text-blue-600 font-medium flex items-center gap-1">
                    <Check className="w-3 h-3" /> Booked via Acuity Scheduling
                  </p>
                </div>
              )}
              
              {availableServices.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No services available yet. Contact your provider directly.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableServices.filter(s => s.is_active !== false).map((svc) => {
                    const isLocked = isAcuityLocked && selectedService?.service_id === svc.service_id;
                    return (
                    <button
                      key={svc.service_id}
                      onClick={() => !isAcuityLocked && setSelectedService(svc)}
                      className={`w-full p-4 rounded-xl border text-left transition-all ${
                        selectedService?.service_id === svc.service_id
                          ? "border-purple-500 bg-purple-50 shadow-sm"
                          : isAcuityLocked ? "border-gray-100 bg-gray-50 opacity-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      data-testid={`service-option-${svc.service_id}`}
                      disabled={isAcuityLocked && selectedService?.service_id !== svc.service_id}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-gray-900">
                            {svc.name}
                            {svc.acuity_synced && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-blue-50 text-blue-600 border border-blue-100">
                                Acuity
                              </span>
                            )}
                            {isLocked && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-green-50 text-green-600 border border-green-100">
                                Booked
                              </span>
                            )}
                          </p>
                          {svc.description && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{svc.description}</p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">
                            {svc.duration_minutes && `${svc.duration_minutes} min`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {serviceSettings.show_price && svc.price > 0 && (
                            <span className="text-sm font-semibold text-gray-900">${svc.price}</span>
                          )}
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedService?.service_id === svc.service_id 
                              ? "border-purple-500 bg-purple-500" 
                              : "border-gray-300"
                          }`}>
                            {selectedService?.service_id === svc.service_id && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                    );
                  })}
                </div>
              )}
              
              <StepNav 
                onBack={() => setCurrentStep(getPrevStep(1))} 
                onNext={selectedService ? () => setCurrentStep(getNextStep(1)) : null}
                nextLabel="Next"
              />
            </div>
          );
        }
        
        // EVENT FLOW: Accessibility and Invisible Disabilities
        const isVirtualEvent = event?.event_type === 'url' || event?.event_type === 'virtual';
        const accessibilityOptions = isVirtualEvent ? DIGITAL_ACCESSIBILITY_OPTIONS : (event?.accessibility_options || ACCESSIBILITY_OPTIONS);
        
        return (
          <div className="animate-fade-in">
            <EventHeader event={event} showDescription={false} />
            <StepHeader 
              step={getStepProgress(1).current} 
              total={getStepProgress(1).total} 
              title="Accessibility" 
              subtitle={isVirtualEvent ? "Digital accessibility needs?" : "How can we accommodate you?"}
              event={event}
            />
            
            {/* Skip hint */}
            <p className="text-xs text-gray-500 mb-3 text-center">
              No accommodations needed? Just tap "Next" to continue
            </p>
            
            {/* Accessibility Options - Physical or Digital based on event type */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">
                {isVirtualEvent ? "Digital accommodations" : "Physical accommodations"}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {accessibilityOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = preferences.accessibility_needs.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleArrayItem("accessibility_needs", option.id)}
                      className={`p-2.5 rounded-xl border text-left transition-all relative ${
                        isSelected
                          ? "border-gray-900 bg-gray-50 shadow-sm"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-xs text-gray-900 leading-tight">{option.label}</p>
                          <p className="text-xs text-gray-600 mt-0.5 leading-tight">{option.desc}</p>
                        </div>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-green-500 absolute top-2 right-2" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Invisible Disabilities - with Heart icon */}
            <div className="mb-4">
              <button
                onClick={() => setShowAccessibilityExpanded(!showAccessibilityExpanded)}
                className="flex items-center gap-2 w-full text-left mb-2"
              >
                <div className="w-5 h-5 rounded-full bg-pink-100 flex items-center justify-center">
                  <Heart className="w-2.5 h-2.5 text-pink-500" fill="currentColor" />
                </div>
                <span className="text-xs font-medium text-gray-700">Invisible Disabilities</span>
                <span className="text-[10px] text-gray-500">(optional)</span>
                <ChevronRight className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${showAccessibilityExpanded ? 'rotate-90' : ''}`} />
              </button>
              
              {showAccessibilityExpanded && (
                <div className="space-y-2 animate-fade-in pl-2">
                  <p className="text-[10px] text-gray-600 mb-2">
                    Share what helps us support you best. This info stays private with event hosts.
                  </p>
                  {INVISIBLE_DISABILITIES.map((option) => {
                    const Icon = option.icon;
                    const isSelected = preferences.invisible_disabilities.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleArrayItem("invisible_disabilities", option.id)}
                        className={`w-full p-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? "border-gray-900 bg-gray-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-xs text-gray-900">{option.label}</p>
                              {isSelected && <Check className="w-3 h-3 text-green-500" />}
                            </div>
                            <p className="text-[10px] text-gray-600 mt-0.5">{option.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  
                  <Textarea
                    placeholder="Anything else we should know?"
                    value={preferences.disability_notes}
                    onChange={(e) => setPreferences({ ...preferences, disability_notes: e.target.value })}
                    className="mt-2 border-gray-200 rounded-xl text-xs"
                    rows={2}
                  />
                </div>
              )}
            </div>
            
            <StepNav 
              onBack={isFilteredView ? null : () => setCurrentStep(getPrevStep(1))} 
              onNext={isFilteredView ? handleFilteredSubmit : () => setCurrentStep(getNextStep(1))}
              nextLabel={isFilteredView ? (filterSubmitting ? "Saving..." : "Submit") : "Next"}
            />
          </div>
        );

      case 2: // Inspo + Full Picture (providers) or Dietary (events)
        if (isProviderFlow) {
          // INSPO + FULL PICTURE STEP
          const EXP_CATS = [
            { id: "hair", label: "Hair" },
            { id: "nails", label: "Nails" },
            { id: "outfit", label: "Outfit / Style" },
            { id: "lash", label: "Lashes" },
            { id: "skincare", label: "Skincare" },
            { id: "event", label: "Event / Occasion" },
            { id: "other", label: "Other" },
          ];
          
          return (
            <div className="animate-fade-in" data-testid="step-inspo-full-picture">
              <EventHeader event={event} showDescription={false} />
              <StepHeader 
                step={getStepProgress(2).current} 
                total={getStepProgress(2).total} 
                title="Inspiration" 
                subtitle={`Browse ${event?.title || event?.name || "your provider"}'s looks & link your full vision`}
                event={event}
                selectedService={selectedService}
              />
              
              {/* Host's Curated Looks - filtered by selected service */}
              {hostCuratedInspo.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">
                    {event?.title || event?.name}'s Portfolio
                    {selectedService && <span className="text-purple-500 ml-1">- {selectedService.name}</span>}
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {hostCuratedInspo
                      .filter(l => !selectedService || !l.service_tags?.length || l.service_tags.includes(selectedService.name))
                      .map((look) => {
                      const isSelected = (preferences.inspo || []).some(i => i.look_id === look.look_id);
                      return (
                        <button key={look.look_id}
                          onClick={() => {
                            setPreferences(prev => ({
                              ...prev,
                              inspo: isSelected 
                                ? (prev.inspo || []).filter(i => i.look_id !== look.look_id)
                                : [...(prev.inspo || []), { look_id: look.look_id, image_url: look.image_url, title: look.title, source: "host_portfolio" }]
                            }));
                          }}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${isSelected ? "border-purple-500 ring-2 ring-purple-200" : "border-transparent hover:border-gray-300"}`}
                          data-testid={`host-look-${look.look_id}`}
                        >
                          {look.image_url ? (
                            <img src={look.image_url} alt={look.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Heart className="w-6 h-6 text-gray-300" /></div>
                          )}
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                          )}
                          {/* Save to My Inspo button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const saveToInspo = async () => {
                                try {
                                  await axios.post(`${API}/user/inspirations/save-from-host`, {
                                    look_id: look.look_id,
                                    event_id: event?.event_id,
                                    image_url: look.image_url,
                                    category: event?.pilot_category || event?.category || "custom",
                                    tags: look.service_tags || [],
                                    title: look.title || "",
                                    host_id: event?.host_user_id || event?.created_by,
                                  }, { withCredentials: true });
                                  toast.success("Saved to your Inspo!");
                                } catch {
                                  toast.error("Login to save inspo");
                                }
                              };
                              saveToInspo();
                            }}
                            className="absolute top-1 left-1 w-5 h-5 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white shadow-sm"
                            title="Save to My Inspo"
                            data-testid={`save-look-${look.look_id}`}
                          >
                            <Bookmark className="w-3 h-3 text-gray-600" />
                          </button>
                          {look.title && (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                              <p className="text-[9px] text-white truncate">{look.title}</p>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Acuity-bound Private Host Inspo (tier-2): only renders when rsvp email matches an Acuity client */}
              {acuityBoundInspo.length > 0 && (
                <div className="mb-4 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-3" data-testid="acuity-bound-inspo">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Lock className="w-3.5 h-3.5 text-purple-600" />
                    <p className="text-xs font-semibold text-purple-800">
                      {isAcuityMatch ? "Private picks for you" : "Private picks from your provider"}
                    </p>
                    {isAcuityMatch && (
                      <span className="text-[9px] uppercase tracking-wide bg-purple-600 text-white px-1.5 py-0.5 rounded-full">Acuity matched</span>
                    )}
                  </div>
                  <p className="text-[10px] text-purple-700 mb-2">
                    Tap to add to your selection. Visible only because your email matches your provider's records.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {acuityBoundInspo.map((it) => {
                      const isSelected = (preferences.inspo || []).some(i => i.inspo_id === it.inspo_id || i.image_url === it.image_url);
                      return (
                        <button
                          key={it.inspo_id || it.image_url}
                          onClick={() => {
                            setPreferences(prev => ({
                              ...prev,
                              inspo: isSelected
                                ? (prev.inspo || []).filter(i => i.inspo_id !== it.inspo_id && i.image_url !== it.image_url)
                                : [...(prev.inspo || []), { inspo_id: it.inspo_id, image_url: it.image_url, source: "host_private", source_detail: "Curated privately" }]
                            }));
                          }}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${isSelected ? "border-purple-500 ring-2 ring-purple-200" : "border-transparent hover:border-purple-300"}`}
                          data-testid={`acuity-inspo-${it.inspo_id || ''}`}
                        >
                          <img src={it.image_url} alt="" className="w-full h-full object-cover" />
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                          )}
                          {(it.tagged_products || []).length > 0 && (
                            <div className="absolute top-1 left-1 flex flex-wrap gap-0.5">
                              {(it.tagged_products || []).slice(0, 1).map(tp => (
                                <span key={tp} className="text-[8px] bg-white/85 text-gray-700 rounded px-1 py-px">{tp}</span>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Conditional Instructor section - renders when service is tagged as a class/instructor/workshop */}
              {(() => {
                const cat = (event?.pilot_category || '').toLowerCase();
                const isClassFlow = cat === 'instructor' || event?.event_type === 'instructor_pilot' ||
                  (selectedService?.tags || []).some(t => ['class', 'workshop', 'lesson', 'instructor'].includes((t || '').toLowerCase()));
                if (!isClassFlow) return null;
                return (
                  <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3" data-testid="instructor-section">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <GraduationCap className="w-3.5 h-3.5 text-indigo-600" />
                      <p className="text-xs font-semibold text-indigo-900">A few notes for your instructor</p>
                    </div>
                    <p className="text-[10px] text-indigo-700 mb-2">Helps your instructor tailor the class.</p>
                    <textarea
                      value={preferences.instructor_notes || ""}
                      onChange={(e) => setPreferences(prev => ({ ...prev, instructor_notes: e.target.value }))}
                      placeholder="Skill level, goals, anything you want them to know..."
                      className="w-full text-xs border border-indigo-200 rounded-lg p-2 bg-white"
                      rows={2}
                      data-testid="instructor-notes-input"
                    />
                  </div>
                );
              })()}
              
              {/* Upload your own */}
              <div className="mb-4">
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-purple-300 transition-colors cursor-pointer">
                  <Upload className="w-5 h-5 mx-auto text-gray-300 mb-1" />
                  <p className="text-xs text-gray-400">Upload your own inspiration</p>
                  <p className="text-[10px] text-purple-400 mt-0.5">Saved to your Phygital profile</p>
                </div>
              </div>
              
              {/* THE FULL PICTURE - Embedded grid */}
              <div className="border-t border-gray-100 pt-4 mb-4" data-testid="full-picture-section">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <p className="text-xs font-semibold text-gray-800">The Full Picture</p>
                </div>
                <p className="text-[10px] text-gray-500 mb-3">
                  Link other upcoming experiences so your provider sees your complete vision
                </p>
                
                {/* Linked experiences already added */}
                {linkedExperiences.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {linkedExperiences.map((exp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded-lg border border-purple-100 bg-purple-50/50">
                        <div>
                          <p className="text-xs font-medium text-gray-900">{exp.category}</p>
                          {exp.provider_name && <p className="text-[10px] text-gray-500">{exp.provider_name}</p>}
                          {exp.notes && <p className="text-[10px] text-gray-400 italic">{exp.notes}</p>}
                        </div>
                        <button onClick={() => setLinkedExperiences(prev => prev.filter((_, i) => i !== idx))} className="p-1"><X className="w-3 h-3 text-gray-400" /></button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Category grid with dropdowns */}
                <div className="grid grid-cols-4 gap-1.5">
                  {EXP_CATS.map((cat) => {
                    const isLinked = linkedExperiences.some(e => e.category === cat.label);
                    const isExpanded = expandedCategory === cat.id;
                    return (
                      <div key={cat.id} className={`${isExpanded ? "col-span-4" : ""}`}>
                        <button
                          onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
                          className={`w-full p-2 rounded-lg border text-center transition-all text-[10px] font-medium ${
                            isExpanded ? "border-purple-500 bg-purple-50 text-purple-700"
                            : isLinked ? "border-purple-200 bg-purple-50/30 text-purple-600"
                            : "border-gray-200 hover:border-gray-300 text-gray-600"
                          }`}
                          data-testid={`exp-cat-${cat.id}`}
                        >
                          {cat.label} {isLinked && <Check className="w-3 h-3 inline text-purple-500" />}
                        </button>
                        {isExpanded && (
                          <div className="mt-2 p-3 border border-gray-200 rounded-xl space-y-2">
                            <Input value={newLinkedExp.provider_name} onChange={(e) => setNewLinkedExp({ ...newLinkedExp, provider_name: e.target.value, category: cat.label })} placeholder="Provider / salon name" className="text-xs h-8" />
                            <Input value={newLinkedExp.notes} onChange={(e) => setNewLinkedExp({ ...newLinkedExp, notes: e.target.value })} placeholder="Notes (e.g. 'blowout at 2pm')" className="text-xs h-8" />
                            <Button size="sm" onClick={() => {
                              setLinkedExperiences(prev => [...prev, { category: cat.label, provider_name: newLinkedExp.provider_name, notes: newLinkedExp.notes }]);
                              setNewLinkedExp({ category: "", provider_name: "", notes: "" });
                              setExpandedCategory(null);
                            }} className="w-full rounded-full text-xs h-8">Link {cat.label}</Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <StepNav 
                onBack={() => setCurrentStep(getPrevStep(2))} 
                onNext={() => setCurrentStep(getNextStep(2))}
                nextLabel="Next"
              />
            </div>
          );
        }
        
        // EVENT FLOW: Dietary - Styled cards matching DefaultsTab
        return (
          <div className="animate-fade-in">
            <EventHeader event={event} />
            <StepHeader 
              step={getStepProgress(2).current} 
              total={getStepProgress(2).total} 
              title="Dietary" 
              subtitle="Any dietary needs?"
              event={event}
            />
            
            {/* Skip hint */}
            <p className="text-xs text-gray-500 mb-3 text-center">
              No dietary restrictions? Just tap "Next" to continue
            </p>
            
            {/* Dietary preferences - 2 column grid with icons */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">Dietary preferences</p>
              <div className="grid grid-cols-2 gap-2">
                {DIETARY_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = preferences.dietary_restrictions.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleArrayItem("dietary_restrictions", option.id)}
                      className={`p-2.5 rounded-xl border text-left transition-all relative ${
                        isSelected
                          ? "border-gray-900 bg-gray-50 shadow-sm"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`dietary-option-${option.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-xs text-gray-900 leading-tight">{option.label}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-tight">{option.desc}</p>
                        </div>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-green-500 absolute top-2 right-2" />}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Meat Preferences - Multi-select */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">Meat preferences <span className="text-gray-400">(what you eat)</span></p>
              <div className="flex flex-wrap gap-2">
                {MEAT_OPTIONS.map((option) => {
                  const isSelected = preferences.meat_preferences.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleArrayItem("meat_preferences", option.id)}
                      className={`px-3 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        isSelected
                          ? "bg-gray-900 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                      data-testid={`meat-option-${option.id}`}
                    >
                      {option.label}
                      {isSelected && <Check className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Allergies - Expandable */}
            <div className="mb-4">
              <button
                onClick={() => setShowAccessibilityExpanded(!showAccessibilityExpanded)}
                className="flex items-center gap-2 w-full text-left mb-2"
              >
                <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-2.5 h-2.5 text-red-500" />
                </div>
                <span className="text-xs font-medium text-gray-700">Allergies</span>
                <span className="text-[10px] text-gray-500">(important for safety)</span>
                <ChevronRight className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${showAccessibilityExpanded ? 'rotate-90' : ''}`} />
              </button>
              
              {showAccessibilityExpanded && (
                <div className="grid grid-cols-2 gap-2 animate-fade-in pl-2">
                  {ALLERGY_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = preferences.allergies.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleArrayItem("allergies", option.id)}
                        className={`p-2 rounded-xl border text-left transition-all ${
                          isSelected
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid={`allergy-option-${option.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-3 h-3" />
                          </div>
                          <p className="font-medium text-xs text-gray-900">{option.label}</p>
                          {isSelected && <Check className="w-3 h-3 text-red-500 ml-auto" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Additional dietary notes */}
            <div className="mb-6">
              <Label className="text-xs text-gray-700 mb-2 block">Additional dietary notes</Label>
              <Textarea
                placeholder="Any other dietary needs or preferences..."
                value={preferences.dietary_notes || ""}
                onChange={(e) => setPreferences({ ...preferences, dietary_notes: e.target.value })}
                className="border-gray-200 rounded-xl text-sm"
                rows={2}
                data-testid="dietary-notes"
              />
            </div>
            
            {/* Transportation - Only show if host enabled it */}
            {event.intake_settings?.collect_transportation && (
              <div className="mb-6 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-700 mb-3">How are you getting there?</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: "driving", label: "Driving", icon: Car, desc: "I'll drive myself" },
                    { id: "dropoff", label: "Drop Off", icon: Car, desc: "Rideshare, driver, etc." }
                  ].map(option => {
                    const isSelected = preferences.transportation === option.id;
                    const IconComp = option.icon;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setPreferences({ ...preferences, transportation: option.id })}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          isSelected 
                            ? "border-purple-500 bg-purple-50" 
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid={`transportation-${option.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isSelected ? "bg-purple-100" : "bg-gray-100"
                          }`}>
                            <IconComp className={`w-4 h-4 ${isSelected ? "text-purple-600" : "text-gray-500"}`} />
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${isSelected ? "text-purple-700" : "text-gray-700"}`}>
                              {option.label}
                            </p>
                            <p className="text-xs text-gray-500">{option.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {preferences.transportation && (
                  <Textarea
                    placeholder="Any notes about transportation? (parking needs, pickup location, etc.)"
                    value={preferences.transportation_notes || ""}
                    onChange={(e) => setPreferences({ ...preferences, transportation_notes: e.target.value })}
                    className="mt-3 border-gray-200 rounded-xl text-sm"
                    rows={2}
                    data-testid="transportation-notes"
                  />
                )}
              </div>
            )}
            
            <StepNav 
              onBack={isFilteredView ? null : () => setCurrentStep(getPrevStep(2))} 
              onNext={isFilteredView ? handleFilteredSubmit : () => setCurrentStep(getNextStep(2))}
              nextLabel={isFilteredView ? (filterSubmitting ? "Saving..." : "Submit") : "Next"}
            />
          </div>
        );

      case 3: // Tap In and Experience Apps
        // Determine what to show based on pilot type
        const pilotCategory = event?.pilot_category?.toLowerCase();
        const isEventsPilot = pilotCategory === 'events' || pilotCategory === 'event';
        const isInstructorPilot = pilotCategory === 'instructor' || pilotCategory === 'instructors' || pilotCategory === 'fitness';
        const isProviderPilot = ['makeup', 'hair', 'nails', 'nail', 'lash', 'culinary', 'wellness', 'style', 'travel', 'dispensary', 'style'].includes(pilotCategory);
        
        // Get what the admin has configured
        const intakeSettings = event?.intake_settings || {};
        const showTapIn = intakeSettings.tapin_enabled || intakeSettings.collect_music_preferences;
        const showVIPIntros = intakeSettings.collect_interests && isEventsPilot;
        const showInterests = intakeSettings.collect_interests;
        const showVibe = intakeSettings.collect_vibe !== false && isEventsPilot;
        // For provider pilots, always show inspo section (core feature)
        const showInspo = isProviderPilot || intakeSettings.inspo_folders?.length > 0 || intakeSettings.collect_inspo_images;
        const inspoCategory = intakeSettings.inspo_folders?.[0] || pilotCategory;
        
        // Provider-specific settings
        const showSkinSection = pilotCategory === 'makeup' || pilotCategory === 'hair';
        const showCuisineSection = pilotCategory === 'culinary';
        
        return (
          <div className="animate-fade-in">
            <EventHeader event={event} />
            <StepHeader 
              step={getStepProgress(3).current} 
              total={getStepProgress(3).total} 
              title={isProviderFlow ? "Preferences" : (isProviderPilot ? "Your Preferences" : (isInstructorPilot ? "Personalize Your Session" : "Your Experience"))}
              subtitle={isProviderFlow ? "Help your provider prepare with your defaults" : (isProviderPilot ? "Help your provider prepare" : (isInstructorPilot ? "Help us tailor this session for you" : "Ways to make it yours"))}
              event={event}
              selectedService={selectedService}
            />
            
            {/* PROVIDER PILOTS: Show inspo and provider-specific questions */}
            {isProviderPilot && (
              <div className="space-y-6 mb-6">
                {/* Inspo Upload Section - SKIP for provider flow (inspo is in Step 2) */}
                {showInspo && !isProviderFlow && (
                  <div className="mb-6" data-testid="inspo-section">
                    <h3 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Heart className="w-4 h-4 text-pink-500" />
                      {inspoCategory === 'face' || pilotCategory === 'makeup' ? 'Makeup Inspiration' : 
                       inspoCategory === 'culinary' || pilotCategory === 'culinary' ? 'Design Inspiration' :
                       inspoCategory === 'hair' || pilotCategory === 'hair' ? 'Hair Inspiration' :
                       inspoCategory === 'nail' || inspoCategory === 'nails' || pilotCategory === 'nails' ? 'Nail Inspiration' :
                       'Your Inspiration'}
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                      {inspoCategory === 'face' || pilotCategory === 'makeup' ? 'Share looks you love so your MUA understands your style' : 
                       inspoCategory === 'culinary' || pilotCategory === 'culinary' ? 'Share images of cakes, presentations, or styles you like' :
                       inspoCategory === 'hair' || pilotCategory === 'hair' ? 'Share styles you want to try or have done' :
                       'Share images that inspire you'}
                    </p>
                    
                    {/* Your Selected Inspo */}
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2">Your Selected Inspiration</p>
                      <div className="grid grid-cols-3 gap-2">
                        {/* Selected inspo images with source badge */}
                        {(preferences.inspo || []).map((img, idx) => {
                          const imgUrl = typeof img === 'string' ? img : img.image_url || img.url;
                          const source = img.source || 'upload';
                          return (
                            <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-gray-100 relative group">
                              <img src={imgUrl} alt="" className="w-full h-full object-cover" />
                              {/* Source badge */}
                              <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                source === 'host_curated' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-white'
                              }`}>
                                {source === 'host_curated' ? 'From Host' : 'Uploaded'}
                              </div>
                              <button
                                onClick={() => removeInspo(idx)}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <span className="text-xs">×</span>
                              </button>
                            </div>
                          );
                        })}
                        
                        {/* Add inspo button */}
                        {(preferences.inspo || []).length < 6 && (
                          <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer flex flex-col items-center justify-center text-gray-400 hover:text-gray-500 transition-colors">
                            <Upload className="w-5 h-5 mb-1" />
                            <span className="text-xs">Upload</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              data-testid="inspo-upload-input"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleInspoUpload(file, inspoCategory || pilotCategory);
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                    
                    {/* Host Curated Inspo - Show if host has added any */}
                    {hostCuratedInspo.length > 0 && (
                      <div className="mb-4 p-3 bg-purple-50 rounded-xl border border-purple-100">
                        <p className="text-xs font-medium text-purple-700 mb-2 flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Suggestions from your {pilotCategory === 'makeup' ? 'MUA' : pilotCategory === 'culinary' ? 'baker' : pilotCategory === 'hair' ? 'stylist' : 'provider'}
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          {hostCuratedInspo.map((inspo) => {
                            const isSelected = (preferences.inspo || []).some(
                              i => i.inspo_id === inspo.inspo_id || i.image_url === inspo.image_url
                            );
                            return (
                              <button
                                key={inspo.inspo_id}
                                onClick={() => selectHostInspo(inspo)}
                                className={`aspect-square rounded-lg overflow-hidden relative border-2 transition-all ${
                                  isSelected ? 'border-purple-500 ring-2 ring-purple-200' : 'border-transparent hover:border-purple-300'
                                }`}
                              >
                                <img src={inspo.image_url} alt="" className="w-full h-full object-cover" />
                                {isSelected && (
                                  <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                                    <Check className="w-6 h-6 text-white drop-shadow-lg" />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {hostCuratedInspo[0]?.notes && (
                          <p className="text-xs text-purple-600 mt-2 italic">"{hostCuratedInspo[0].notes}"</p>
                        )}
                      </div>
                    )}
                    
                    {/* Loading state for host inspo */}
                    {loadingHostInspo && (
                      <div className="text-xs text-gray-500 text-center py-2">
                        Loading suggestions from your provider...
                      </div>
                    )}
                    
                    <p className="text-[10px] text-gray-400 mt-2">
                      Your inspiration will be saved to your profile for future appointments
                    </p>
                  </div>
                )}
                
                {/* Skin/Beauty Section - for Makeup pilot */}
                {showSkinSection && pilotCategory === 'makeup' && (
                  <div className="mb-6" data-testid="skin-section">
                    <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-pink-500" />
                      Skin & Makeup Preferences
                    </h3>
                    
                    {/* Skin Type */}
                    <div className="mb-4">
                      <label className="text-xs text-gray-600 mb-1 block">Skin Type</label>
                      <div className="flex flex-wrap gap-2">
                        {['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'].map((type) => (
                          <button
                            key={type}
                            onClick={() => setPreferences(prev => ({ ...prev, skin_type: type }))}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                              preferences.skin_type === type
                                ? "bg-pink-500 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Skin Concerns */}
                    <div className="mb-4">
                      <label className="text-xs text-gray-600 mb-1 block">Any skin concerns?</label>
                      <div className="flex flex-wrap gap-2">
                        {['Acne', 'Redness', 'Dark spots', 'Fine lines', 'Texture', 'None'].map((concern) => (
                          <button
                            key={concern}
                            onClick={() => {
                              setPreferences(prev => ({
                                ...prev,
                                skin_concerns: prev.skin_concerns?.includes(concern)
                                  ? prev.skin_concerns.filter(c => c !== concern)
                                  : [...(prev.skin_concerns || []), concern]
                              }));
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                              preferences.skin_concerns?.includes(concern)
                                ? "bg-pink-500 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {concern}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Allergies / Sensitivities - multi-chip from admin
                        category_preference_schemas (loaded into
                        mergedQuestions). Free-text removed per spec. */}
                    <div className="mb-4">
                      <label className="text-xs text-gray-600 mb-2 block">Product allergies or sensitivities?</label>
                      <div className="flex flex-wrap gap-2" data-testid="makeup-allergies-chips">
                        {(() => {
                          const allergyOpts =
                            (dynamicContent?.intake?.allergy_options) ||
                            (dynamicContent?.allergy_options) ||
                            ["Latex","Fragrance","Mineral oil","Talc","Parabens","Sulfates","Formaldehyde","Nickel","Nuts (almond/coconut)","Beeswax","Lanolin","Silicones","Alcohol","Essential oils","None"];
                          const current = Array.isArray(preferences.makeup_allergies)
                            ? preferences.makeup_allergies
                            : (preferences.makeup_allergies ? [preferences.makeup_allergies] : []);
                          return allergyOpts.map((opt) => {
                            const active = current.includes(opt);
                            return (
                              <button
                                key={opt}
                                type="button"
                                data-testid={`makeup-allergy-chip-${opt.replace(/\s+/g,'-').toLowerCase()}`}
                                onClick={() => {
                                  setPreferences(prev => {
                                    const list = Array.isArray(prev.makeup_allergies)
                                      ? prev.makeup_allergies
                                      : (prev.makeup_allergies ? [prev.makeup_allergies] : []);
                                    const next = active ? list.filter(v => v !== opt) : [...list, opt];
                                    return { ...prev, makeup_allergies: next };
                                  });
                                }}
                                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                                  active
                                    ? "bg-rose-500 text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                {opt}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    
                    {/* Special Requests */}
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Special requests or notes for your MUA</label>
                      <Textarea
                        placeholder="Anything else you'd like your makeup artist to know..."
                        value={preferences.special_requests || ''}
                        onChange={(e) => setPreferences(prev => ({ ...prev, special_requests: e.target.value }))}
                        className="min-h-[60px] resize-none text-sm"
                      />
                    </div>
                  </div>
                )}
                
                {/* Culinary Section - for Culinary pilot */}
                {showCuisineSection && (
                  <div className="mb-6" data-testid="culinary-section">
                    <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <ChefHat className="w-4 h-4 text-orange-500" />
                      Order Details
                    </h3>
                    
                    {/* Event Info */}
                    <div className="mb-4">
                      <label className="text-xs text-gray-600 mb-1 block">What's the occasion?</label>
                      <div className="flex flex-wrap gap-2">
                        {['Birthday', 'Wedding', 'Anniversary', 'Baby Shower', 'Corporate', 'Holiday', 'Other'].map((occasion) => (
                          <button
                            key={occasion}
                            onClick={() => setPreferences(prev => ({ ...prev, occasion: occasion }))}
                            className={`px-3 py-1.5 rounded-full text-sm ${
                              preferences.occasion === occasion
                                ? "bg-orange-500 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {occasion}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Guest Count */}
                    <div className="mb-4">
                      <label className="text-xs text-gray-600 mb-1 block">How many guests?</label>
                      <Input
                        type="number"
                        placeholder="Estimated number of servings"
                        value={preferences.guest_count || ''}
                        onChange={(e) => setPreferences(prev => ({ ...prev, guest_count: e.target.value }))}
                        className="text-sm w-32"
                      />
                    </div>
                    
                    {/* Special Requests */}
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Special requests or notes</label>
                      <Textarea
                        placeholder="Flavor preferences, design ideas, specific requests..."
                        value={preferences.special_requests || ''}
                        onChange={(e) => setPreferences(prev => ({ ...prev, special_requests: e.target.value }))}
                        className="min-h-[60px] resize-none text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* EVENTS/INSTRUCTOR PILOTS: Show Tap In, VIP Intros, Interests, Vibe */}
            {(isEventsPilot || isInstructorPilot) && (
              <>
                <div className="space-y-3 mb-4">
                  {EXPERIENCE_APPS.filter(app => {
                    // Only show Tap In if enabled in intake_settings
                    if (app.id === 'tap-in' && !showTapIn && !isEventsPilot) return false;
                    // Only show VIP Intros if enabled (defaults to true for events, controlled by intake_settings)
                    if (app.id === 'vip_intro') {
                      // Check if explicitly disabled in intake_settings
                      if (intakeSettings?.show_vip_intros === false) return false;
                      // Default: only show for events pilot
                      if (!isEventsPilot) return false;
                    }
                    return true;
                  }).map((app) => {
                    const isSelected = preferences.experience_apps.includes(app.id);
                    const IconComponent = app.iconComponent;
                    
                    return (
                      <button
                        key={app.id}
                        onClick={() => toggleArrayItem("experience_apps", app.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all border ${
                          isSelected ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {/* App Icon - Compact */}
                        <div className={`w-10 h-10 rounded-lg ${app.icon ? 'overflow-hidden' : `bg-gradient-to-br ${app.color}`} flex items-center justify-center flex-shrink-0`}>
                          {app.icon ? (
                            <img src={app.icon} alt={app.name} className="w-full h-full object-cover" />
                          ) : (
                            <IconComponent className="w-5 h-5 text-white" />
                          )}
                        </div>
                        
                        {/* App Info - Compact */}
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-medium text-sm text-gray-900">{app.name}</p>
                          <p className="text-xs text-gray-500 truncate">{app.desc}</p>
                        </div>
                        
                        {/* Selected indicator */}
                        {isSelected ? (
                          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {/* Interests Section - for Events/Instructor */}
                {showInterests && (
                  <div className="mb-3">
                    <h3 className="text-xs font-medium text-gray-700 mb-2">What brings you here?</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {/* Use dynamic interests based on pilot_category - per Master Plan Section 15 */}
                      {getInterestOptions(event).map((interest) => (
                        <button
                          key={interest}
                          onClick={() => toggleArrayItem("interests", interest)}
                          className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                            preferences.interests.includes(interest)
                              ? "bg-gray-900 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Vibe Section - only for Events */}
                {showVibe && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-2">Your vibe tonight</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {VIBE_OPTIONS.map((option) => {
                        const Icon = option.icon;
                        const isSelected = preferences.vibe === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => setPreferences(prev => ({ ...prev, vibe: option.id }))}
                            className={`p-3 rounded-xl border text-left transition-all ${
                              isSelected
                                ? "border-gray-900 bg-gray-50 shadow-sm"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg ${option.color} flex items-center justify-center flex-shrink-0`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900">{option.label}</p>
                                <p className="text-xs text-gray-600">{option.desc}</p>
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* INSTRUCTOR PILOT: Builder Profile Section (per Master Plan Section 25) */}
            {isInstructorPilot && intakeSettings.collect_builder_profile === true && (
              <div className="mb-4" data-testid="builder-profile-section">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-3 border border-indigo-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-3 h-3 text-indigo-600" />
                    </div>
                    <h3 className="text-xs font-semibold text-gray-900">Your Builder Profile</h3>
                  </div>
                  
                  {/* Role - Button Style */}
                  <div className="mb-3">
                    <label className="text-xs text-gray-600 mb-1.5 block">What describes you best?</label>
                    <div className="flex flex-wrap gap-1.5">
                      {(intakeSettings.builder_profile_options?.roles || [
                        "Founder/CEO", "Engineer", "Designer", "Product",
                        "Marketing", "Operations", "Investor", "Student", "Curious"
                      ]).map(role => (
                        <button
                          key={role}
                          onClick={() => setPreferences(prev => ({ ...prev, builder_role: role }))}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                            preferences.builder_role === role
                              ? "bg-indigo-600 text-white"
                              : "bg-white text-gray-700 border border-gray-200 hover:border-indigo-300"
                          }`}
                          data-testid={`builder-role-${role.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Stage - Button Style */}
                  <div className="mb-3">
                    <label className="text-xs text-gray-600 mb-1.5 block">Where in your journey?</label>
                    <div className="flex flex-wrap gap-1.5">
                      {(intakeSettings.builder_profile_options?.stages || [
                        "Exploring", "Have idea", "Building MVP", "Launched", "Scaling", "Not building"
                      ]).map(stage => (
                        <button
                          key={stage}
                          onClick={() => setPreferences(prev => ({ ...prev, builder_stage: stage }))}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                            preferences.builder_stage === stage
                              ? "bg-purple-600 text-white"
                              : "bg-white text-gray-700 border border-gray-200 hover:border-purple-300"
                          }`}
                          data-testid={`builder-stage-${stage.replace(/[^a-zA-Z]/g, '').toLowerCase()}`}
                        >
                          {stage}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* What they're building + Project Link - Compact */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Working on?</label>
                      <input
                        type="text"
                        value={preferences.builder_current_build}
                        onChange={(e) => setPreferences(prev => ({ ...prev, builder_current_build: e.target.value }))}
                        placeholder="Your project"
                        className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                        data-testid="builder-current-build-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        Link (optional)
                      </label>
                      <input
                        type="url"
                        value={preferences.builder_project_url}
                        onChange={(e) => setPreferences(prev => ({ ...prev, builder_project_url: e.target.value }))}
                        placeholder="https://..."
                        className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                        data-testid="builder-project-url-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* INSTRUCTOR PILOT: Audience Preferences Section (per Master Plan Default 11: AUDIENCE) */}
            {isInstructorPilot && intakeSettings.collect_audience_preferences !== false && (
              <div className="mb-6 space-y-4" data-testid="audience-prefs-section">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Your Learning Preferences</h3>
                <p className="text-xs text-gray-500 mb-3">Help the instructor tailor the content to you</p>
                
                {/* Experience Level */}
                <div>
                  <label className="text-xs text-gray-700 mb-2 block">Experience level with this topic</label>
                  <div className="flex flex-wrap gap-2">
                    {(intakeSettings.audience_options?.experience_levels || ["Beginner", "Intermediate", "Advanced"]).map(level => (
                      <button
                        key={level}
                        onClick={() => setPreferences(prev => ({ ...prev, audience_experience_level: level }))}
                        className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                          preferences.audience_experience_level === level
                            ? "bg-blue-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        data-testid={`experience-level-${level.toLowerCase()}`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Learning Style */}
                <div>
                  <label className="text-xs text-gray-700 mb-2 block">How do you learn best?</label>
                  <div className="flex flex-wrap gap-2">
                    {(intakeSettings.audience_options?.learning_styles || [
                      "Visual - Show me", "Auditory - Tell me", "Hands-on - Let me try", "Reading - Give me resources"
                    ]).map(style => (
                      <button
                        key={style}
                        onClick={() => setPreferences(prev => ({ ...prev, audience_learning_style: style }))}
                        className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                          preferences.audience_learning_style === style
                            ? "bg-blue-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        data-testid={`learning-style-${style.split(' ')[0].toLowerCase()}`}
                      >
                        {style}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Pace Preference */}
                <div>
                  <label className="text-xs text-gray-700 mb-2 block">Preferred pace</label>
                  <div className="flex flex-wrap gap-2">
                    {(intakeSettings.audience_options?.pace_preferences || [
                      "Fast-paced", "Moderate", "Slow and thorough"
                    ]).map(pace => (
                      <button
                        key={pace}
                        onClick={() => setPreferences(prev => ({ ...prev, audience_pace_preference: pace }))}
                        className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                          preferences.audience_pace_preference === pace
                            ? "bg-blue-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        data-testid={`pace-pref-${pace.split(' ')[0].toLowerCase()}`}
                      >
                        {pace}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Interaction Preference */}
                <div>
                  <label className="text-xs text-gray-700 mb-2 block">How do you like to participate?</label>
                  <div className="flex flex-wrap gap-2">
                    {(intakeSettings.audience_options?.interaction_preferences || [
                      "Love asking questions", "Prefer to listen", "Better in small groups", "Chat participation"
                    ]).map(pref => (
                      <button
                        key={pref}
                        onClick={() => setPreferences(prev => ({ ...prev, audience_interaction_pref: pref }))}
                        className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                          preferences.audience_interaction_pref === pref
                            ? "bg-blue-600 text-white shadow-md"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        data-testid={`interaction-pref-${pref.split(' ')[0].toLowerCase()}`}
                      >
                        {pref}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Question Bank - For Instructor Pilots Only */}
            {isInstructorPilot && (
              intakeSettings.question_bank_enabled || dynamicContent?.intake?.question_bank_enabled
            ) && (
              <div className="mb-4" data-testid="question-bank-section">
                <h3 className="text-xs font-medium text-gray-700 mb-2">
                  {intakeSettings.question_bank_label || 
                   dynamicContent?.intake?.question_bank_label || 
                   "Have a question?"}
                </h3>
                <Textarea
                  placeholder="What would you like to learn or discuss in this session?"
                  value={preferences.question_for_instructor}
                  onChange={(e) => setPreferences(prev => ({ ...prev, question_for_instructor: e.target.value }))}
                  className="min-h-[60px] resize-none text-sm"
                  data-testid="question-for-instructor-input"
                />
                {/* Question Recipient Selection */}
                {preferences.question_for_instructor && preferences.question_for_instructor.trim().length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1.5">Who should see this question?</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'instructor', label: 'Instructor', desc: 'Ask during session' },
                        { id: 'emergent', label: 'Emergent Team', desc: 'For the hosts' },
                        { id: 'both', label: 'Both', desc: 'Share with everyone' }
                      ].map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setPreferences(prev => ({ ...prev, question_recipient: option.id }))}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                            preferences.question_recipient === option.id
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'
                          }`}
                          data-testid={`question-recipient-${option.id}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <StepNav 
              onBack={isFilteredView ? null : () => setCurrentStep(getPrevStep(3))} 
              onNext={isFilteredView ? handleFilteredSubmit : () => setCurrentStep(getNextStep(3))}
              nextLabel={isFilteredView ? (filterSubmitting ? "Saving..." : "Submit") : "Next"}
            />
          </div>
        );

      case 4: // Account Creation (previously step 6)
        return (
          <div className="animate-fade-in">
            <EventHeader event={event} />
            
            {/* Pre-fill source indicator */}
            {prefillSource && (
              <div className={`mb-3 px-3 py-2 rounded-xl border text-xs flex items-center gap-2 ${
                prefillSource === 'acuity' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
              }`} data-testid={`prefill-badge-${prefillSource}`}>
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                <span>
                  {prefillSource === 'acuity' 
                    ? 'Pre-filled from Acuity booking: name, email & phone synced automatically'
                    : 'Pre-filled by host: name, email & city provided ahead of time'}
                </span>
              </div>
            )}
            
            <StepHeader 
              step={getStepProgress(4).current} 
              total={getStepProgress(4).total} 
              title={dynamicContent?.intake?.title || "Your Info"} 
              subtitle={dynamicContent?.intake?.subtitle || "Save preferences for any experience"}
              event={event}
            />
            
            <div className="space-y-3 mb-4">
              {/* Headshot/Profile Photo Upload - Per Master Plan */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center overflow-hidden border-2 border-white shadow-lg">
                    {accountData.headshot_url ? (
                      <img 
                        src={accountData.headshot_url} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-purple-400">
                        {accountData.first_name?.charAt(0) || accountData.name?.charAt(0) || '?'}
                      </span>
                    )}
                  </div>
                  <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-50 border">
                    <Camera className="w-4 h-4 text-gray-600" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Show preview immediately
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setAccountData(prev => ({ ...prev, headshot_url: ev.target.result }));
                        };
                        reader.readAsDataURL(file);
                        
                        // Upload to server
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const token = localStorage.getItem("session_token");
                          const res = await fetch(`${API}/upload/headshot`, {
                            method: 'POST',
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                            credentials: 'include',
                            body: formData
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setAccountData(prev => ({ ...prev, headshot_url: data.url }));
                          }
                        } catch (err) {
                          console.error("Upload error:", err);
                        }
                      }}
                      data-testid="headshot-upload"
                    />
                  </label>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Profile Photo</p>
                  <p className="text-xs text-gray-500">Add a photo so your provider can recognize you</p>
                </div>
              </div>
              
              {/* Required Fields - First/Last Name */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-700">First Name *</Label>
                  <Input
                    value={accountData.first_name}
                    onChange={(e) => {
                      const firstName = e.target.value;
                      const fullName = `${firstName} ${accountData.last_name}`.trim();
                      setAccountData({ ...accountData, first_name: firstName, name: fullName });
                    }}
                    placeholder="First name"
                    className="mt-1 rounded-xl border-gray-200 h-10 text-sm"
                    data-testid="account-first-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">Last Name *</Label>
                  <Input
                    value={accountData.last_name}
                    onChange={(e) => {
                      const lastName = e.target.value;
                      const fullName = `${accountData.first_name} ${lastName}`.trim();
                      setAccountData({ ...accountData, last_name: lastName, name: fullName });
                    }}
                    placeholder="Last name"
                    className="mt-1 rounded-xl border-gray-200 h-10 text-sm"
                    data-testid="account-last-name"
                  />
                </div>
              </div>
              
              {/* Email */}
              <div>
                <Label className="text-xs text-gray-700">Email *</Label>
                <Input
                  type="email"
                  value={accountData.email}
                  onChange={(e) => {
                    setAccountData({ ...accountData, email: e.target.value });
                    setEmailManuallyChanged(true); // Mark email as manually changed
                  }}
                  placeholder="you@example.com"
                  className="mt-1 rounded-xl border-gray-200 h-10 text-sm"
                  data-testid="account-email"
                />
              </div>
              
              {/* Company and Title - Hidden for service provider flows except instructor and event */}
              {(!isProviderFlow || isInstructorPilotFlow || event?.pilot_category?.toLowerCase() === 'event') && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-gray-700">Company</Label>
                  <Input
                    value={accountData.company}
                    onChange={(e) => setAccountData({ ...accountData, company: e.target.value })}
                    placeholder="Your company"
                    className="mt-1 rounded-xl border-gray-200 text-sm h-9"
                    data-testid="account-company"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">Title</Label>
                  <Input
                    value={accountData.title}
                    onChange={(e) => setAccountData({ ...accountData, title: e.target.value })}
                    placeholder="Your role"
                    className="mt-1 rounded-xl border-gray-200 text-sm h-9"
                    data-testid="account-title"
                  />
                </div>
              </div>
              )}
              
              {/* City - REQUIRED with location dropdown if collect_location is enabled */}
              <div className="relative" data-testid="account-city-wrap">
                <Label className="text-xs text-gray-700">City <span className="text-red-500">*</span></Label>
                {event?.intake_settings?.collect_location && configuredLocations.length > 0 ? (
                  <div className="relative">
                    <input
                      type="text"
                      value={locationSearch || accountData.city || ""}
                      onChange={(e) => {
                        setLocationSearch(e.target.value);
                        setAccountData({ ...accountData, city: e.target.value });
                        setShowLocationDropdown(true);
                      }}
                      onFocus={() => setShowLocationDropdown(true)}
                      placeholder="Search your city..."
                      className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-gray-100 focus:border-gray-300 h-9"
                      data-testid="account-city"
                    />
                    {showLocationDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-40 overflow-y-auto" data-testid="welcome-location-selector">
                        {configuredLocations
                          .filter(loc => {
                            const q = (locationSearch || "").toLowerCase();
                            if (!q) return true;
                            return loc.city.toLowerCase().includes(q) || loc.state?.toLowerCase().includes(q) || loc.country?.toLowerCase().includes(q);
                          })
                          .sort((a, b) => {
                            if (!locationSearch) {
                              if (a.status === 'active' && b.status !== 'active') return -1;
                              if (a.status !== 'active' && b.status === 'active') return 1;
                            }
                            return 0;
                          })
                          .map((loc, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                const val = loc.country !== "USA" ? loc.full_label : loc.label;
                                setAccountData({ ...accountData, city: val });
                                setLocationSearch(val);
                                setShowLocationDropdown(false);
                              }}
                              className={`w-full px-3 py-1.5 text-left text-sm hover:bg-blue-50 flex items-center gap-2 ${loc.status === 'active' && !locationSearch ? 'bg-blue-50/50' : ''}`}
                            >
                              <MapPin className={`w-3 h-3 flex-shrink-0 ${loc.status === 'active' ? 'text-blue-500' : 'text-gray-400'}`} />
                              <span>{loc.label}</span>
                              {loc.country !== "USA" && <span className="text-[10px] text-gray-400 ml-auto">{loc.country}</span>}
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </div>
                ) : (
                  <Input
                    value={accountData.city}
                    onChange={(e) => setAccountData({ ...accountData, city: e.target.value })}
                    placeholder="Los Angeles, CA"
                    className="mt-1 rounded-xl border-gray-200 text-sm h-9"
                    data-testid="account-city"
                  />
                )}
              </div>
              
              {/* Phone and Social Handles - Compact Row */}
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs text-gray-700">Phone</Label>
                  <div className="relative mt-1">
                    {/* Country code prefix - auto-derives from city; user can edit */}
                    <span
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-stone-500 font-medium select-none pointer-events-none"
                      data-testid="account-phone-cc"
                    >
                      {(() => {
                        const c = (accountData.city || "").toLowerCase();
                        if (/london|uk|united kingdom|england/.test(c)) return "+44";
                        if (/paris|france/.test(c)) return "+33";
                        if (/toronto|vancouver|montreal|canada/.test(c)) return "+1";
                        if (/dubai|abu dhabi|uae/.test(c)) return "+971";
                        if (/sydney|melbourne|australia/.test(c)) return "+61";
                        if (/mumbai|delhi|bangalore|india/.test(c)) return "+91";
                        if (/mexico|cdmx/.test(c)) return "+52";
                        return "+1";
                      })()}
                    </span>
                    <Input
                      type="tel"
                      value={accountData.phone}
                      onChange={(e) => setAccountData({ ...accountData, phone: e.target.value })}
                      placeholder="555-1234"
                      className="rounded-xl border-gray-200 text-xs h-9 pl-9"
                      data-testid="account-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-700 text-pink-500">IG</Label>
                  <Input
                    value={accountData.instagram}
                    onChange={(e) => setAccountData({ ...accountData, instagram: e.target.value })}
                    placeholder="@handle"
                    className="mt-1 rounded-xl border-gray-200 text-xs h-9"
                    data-testid="account-instagram"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-700">TikTok</Label>
                  <Input
                    value={accountData.tiktok}
                    onChange={(e) => setAccountData({ ...accountData, tiktok: e.target.value })}
                    placeholder="@handle"
                    className="mt-1 rounded-xl border-gray-200 text-xs h-9"
                    data-testid="account-tiktok"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-700 text-blue-600">LinkedIn</Label>
                  <Input
                    value={accountData.linkedin}
                    onChange={(e) => {
                      // Normalize LinkedIn input - accept URL, /in/handle, or just handle
                      let value = e.target.value;
                      if (value.includes('linkedin.com/in/')) {
                        value = value.split('linkedin.com/in/')[1]?.split('/')[0]?.split('?')[0] || value;
                      } else if (value.startsWith('/in/')) {
                        value = value.substring(4);
                      }
                      setAccountData({ ...accountData, linkedin: value });
                    }}
                    placeholder="handle"
                    className="mt-1 rounded-xl border-gray-200 text-xs h-9"
                    data-testid="account-linkedin"
                  />
                </div>
              </div>
              
              {/* Inner Circle Option - Gated by category public_features.
                  For Makeup pilots right now, public_features=["rsvp"] only,
                  so this block is hidden until admin opts the category in. */}
              {event?.inner_circle?.enabled !== false && categoryPublicFeatures.includes("newsletter") && (
                <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={accountData.add_to_list}
                      onChange={(e) => setAccountData({ ...accountData, add_to_list: e.target.checked })}
                      className="mt-0.5 w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                      data-testid="add-to-list-checkbox"
                    />
                    <div>
                      <span className="font-medium text-xs text-purple-900">
                        Add me to the {event?.inner_circle?.name || `${event?.organization_name || event?.host_name || event?.name || 'the'} inner circle`}
                      </span>
                      <p className="text-[10px] text-purple-700 mt-0.5">
                        {event?.inner_circle?.benefits || "Get early access to future events, exclusive invites, and industry connections"}
                      </p>
                    </div>
                  </label>
                </div>
              )}
              
              {/* Legal Consent - Required */}
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accountData.beehiiv_consent || false}
                    onChange={(e) => setAccountData({ ...accountData, beehiiv_consent: e.target.checked })}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                    data-testid="beehiiv-consent-checkbox"
                  />
                  <p className="text-[10px] text-gray-600 leading-snug">
                    I agree to the{" "}
                    <a href="/terms" target="_blank" className="underline text-gray-700">Terms of Service</a> &{" "}
                    <a href="/privacy" target="_blank" className="underline text-gray-700">Privacy Policy</a>
                  </p>
                </label>
              </div>
            </div>
            
            {/* Preview Card Button */}
            <div className="mb-4">
              <button
                onClick={() => setShowPreSubmitPreview(true)}
                className="w-full flex items-center justify-center gap-2 p-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 transition-colors"
                data-testid="preview-card-btn"
              >
                <Eye className="w-4 h-4" />
                Preview your preference card
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mb-4">
              <Button 
                variant="outline" 
                onClick={() => setCurrentStep(getPrevStep(4))}
                className="flex-1 h-11 rounded-full text-sm border-gray-200"
              >
                Back
              </Button>
              <Button 
                onClick={handleComplete}
                disabled={!accountData.first_name || !accountData.last_name || !accountData.email || (!isProviderFlow && !accountData.beehiiv_consent) || saving}
                className="flex-1 h-11 bg-gray-900 hover:bg-gray-800 text-white rounded-full text-sm"
                data-testid="complete-button"
              >
                {saving ? "Saving..." : "Confirm RSVP"}
              </Button>
            </div>

            {/* Pre-submit Preview Modal */}
            {showPreSubmitPreview && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
                  {/* Card Preview */}
                  <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-900 p-4 text-white rounded-t-2xl">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] px-2 py-0.5 bg-purple-800/50 rounded-full font-medium" data-testid="card-label">
                        {event?.display_settings?.card_label || event?.inherited_display_settings?.card_label || "PREFERENCE CARD"}
                      </span>
                      <button onClick={() => setShowPreSubmitPreview(false)} className="text-white/60 hover:text-white">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <p className="text-[10px] text-white/70 mb-1">{event?.name}</p>
                    
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold">
                        {(accountData.first_name || accountData.name)?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-semibold">{accountData.first_name && accountData.last_name ? `${accountData.first_name} ${accountData.last_name}` : accountData.name || "Your Name"}</p>
                        {accountData.roles.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-pink-500/30 rounded text-white/90">
                            {ROLE_OPTIONS.find(r => r.id === accountData.roles[0])?.label}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {(preferences.accessibility_needs.length > 0 || preferences.disability_notes) && (
                      <div className="bg-purple-800/40 rounded-lg p-2 mb-2">
                        <p className="text-[9px] text-white/60 font-medium mb-1">ACCESSIBILITY</p>
                        {preferences.accessibility_needs.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {preferences.accessibility_needs.map(needId => {
                              const need = ACCESSIBILITY_OPTIONS.find(a => a.id === needId);
                              return need && <span key={needId} className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded">{need.label}</span>;
                            })}
                          </div>
                        )}
                        {preferences.disability_notes && (
                          <p className="text-[9px] text-white/70 mt-1.5 italic">"{preferences.disability_notes}"</p>
                        )}
                      </div>
                    )}
                    
                    {preferences.invisible_disabilities?.length > 0 && (
                      <div className="bg-purple-800/40 rounded-lg p-2 mb-2">
                        <p className="text-[9px] text-white/60 font-medium mb-1">INVISIBLE DISABILITIES</p>
                        <div className="flex flex-wrap gap-1">
                          {preferences.invisible_disabilities.map(d => {
                            const labels = {
                              vocal_tics: "Vocal Tics",
                              motor_tics: "Motor Tics",
                              tourettes: "Tourette's",
                              anxiety: "Anxiety",
                              chronic_pain: "Chronic Pain",
                              chronic_fatigue: "Chronic Fatigue",
                              adhd: "ADHD",
                              autism: "Autism Spectrum"
                            };
                            return <span key={d} className="text-[10px] px-1.5 py-0.5 bg-purple-500/30 rounded">{labels[d] || d}</span>;
                          })}
                        </div>
                      </div>
                    )}
                    
                    {(preferences.dietary_restrictions.length > 0 || preferences.meat_preferences?.length > 0 || preferences.allergies?.length > 0) && (
                      <div className="bg-purple-800/40 rounded-lg p-2 mb-2">
                        <p className="text-[9px] text-white/60 font-medium mb-1">DIETARY</p>
                        <div className="flex flex-wrap gap-1">
                          {preferences.dietary_restrictions.map(diet => {
                            const option = DIETARY_OPTIONS.find(d => d.id === diet);
                            return (
                              <span key={diet} className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded flex items-center gap-1">
                                {option?.label || diet}
                              </span>
                            );
                          })}
                          {preferences.meat_preferences?.map(meat => {
                            const option = MEAT_OPTIONS.find(m => m.id === meat);
                            return (
                              <span key={meat} className="text-[10px] px-1.5 py-0.5 bg-green-500/30 rounded">
                                {option?.label || meat}
                              </span>
                            );
                          })}
                        </div>
                        {preferences.allergies?.length > 0 && (
                          <div className="mt-1.5 pt-1.5 border-t border-white/10">
                            <p className="text-[8px] text-red-300/80 font-medium mb-1">ALLERGIES</p>
                            <div className="flex flex-wrap gap-1">
                              {preferences.allergies.map(allergy => {
                                const option = ALLERGY_OPTIONS.find(a => a.id === allergy);
                                return (
                                  <span key={allergy} className="text-[10px] px-1.5 py-0.5 bg-red-500/30 rounded">
                                    {option?.label || allergy}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {preferences.dietary_notes && (
                          <p className="text-[9px] text-white/70 mt-1.5 italic">"{preferences.dietary_notes}"</p>
                        )}
                      </div>
                    )}
                    
                    {preferences.interests.length > 0 && (
                      <div className="mb-2">
                        <p className="text-[9px] text-white/60 font-medium mb-1">INTERESTS</p>
                        <div className="flex flex-wrap gap-1">
                          {preferences.interests.slice(0, 4).map(interest => (
                            <span key={interest} className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded">{interest}</span>
                          ))}
                          {preferences.interests.length > 4 && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded">+{preferences.interests.length - 4}</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {preferences.vibe && (
                      <div>
                        <p className="text-[9px] text-white/60 font-medium mb-1">VIBE</p>
                        <p className="text-xs">{VIBE_OPTIONS.find(v => v.id === preferences.vibe)?.label}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <p className="text-[10px] text-gray-500 text-center mb-3">
                      This card will be shared with the event host
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowPreSubmitPreview(false);
                          setCurrentStep(1);
                        }}
                        className="flex-1 h-10 rounded-full text-xs"
                      >
                        <Edit3 className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowPreSubmitPreview(false);
                          handleComplete();
                        }}
                        disabled={!accountData.first_name || !accountData.last_name || !accountData.email || !accountData.beehiiv_consent || saving}
                        className="flex-1 h-10 bg-purple-600 hover:bg-purple-700 rounded-full text-xs"
                      >
                        {saving ? "Saving..." : "Looks Good"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 6: // PAYMENT (Iter 9.40 Slice I.g - only inserted into the flow when host has Acuity connected)
        return (
          <div className="animate-fade-in pb-24" data-testid="step-payment">
            <EventHeader event={event} />
            <div className="max-w-md mx-auto px-4">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Reserve your spot</h2>
                <p className="text-sm text-gray-500 mt-1">Pick a time and pay your deposit through Acuity. We'll come back to your preferences right after.</p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden mb-6">
                {selectedService ? (
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-gray-900 truncate">{selectedService?.name || selectedService?.service_name || "Service"}</p>
                      <p className="text-xs text-gray-500">
                        {selectedService?.duration_min ? `${selectedService.duration_min} min` : ""}
                        {selectedService?.price ? ` · $${selectedService.price}` : ""}
                      </p>
                    </div>
                  </div>
                ) : null}
                {(() => {
                  // Iter 9.40 Pass I.g (2026-05-07) - real Acuity embed instead
                  // of opening a new tab. Resolves scheduling URL from:
                  //   1. Per-service override: selectedService.acuity_scheduling_url
                  //   2. Event-level:          event.acuity_scheduling_url / acuity_booking_url / acuity_url
                  //   3. Org-level fallback:   event.tech_stack.acuity.scheduling_url
                  const baseUrl =
                    selectedService?.acuity_scheduling_url ||
                    selectedService?.acuity_appointment_type_url ||
                    event?.acuity_scheduling_url ||
                    event?.acuity_booking_url ||
                    event?.acuity_url ||
                    event?.tech_stack?.acuity?.scheduling_url ||
                    null;

                  // Iter 9.50+ R11 - Acuity iframe prefill. Acuity scheduling
                  // URLs accept firstName/lastName/email/phone via query string.
                  // Pre-filling from the RSVP form removes a friction step.
                  let schedulingUrl = baseUrl;
                  if (baseUrl) {
                    try {
                      const u = new URL(baseUrl);
                      const first = (accountData?.first_name || "").trim();
                      const last = (accountData?.last_name || "").trim();
                      const email = (accountData?.email || "").trim();
                      const phone = (accountData?.phone || "").trim();
                      if (first) u.searchParams.set("firstName", first);
                      if (last) u.searchParams.set("lastName", last);
                      if (email) u.searchParams.set("email", email);
                      if (phone) u.searchParams.set("phone", phone);
                      schedulingUrl = u.toString();
                    } catch (_) { schedulingUrl = baseUrl; }
                  }

                  if (schedulingUrl) {
                    return (
                      <div>
                        <div className="px-5 pt-4 pb-3 text-center">
                          <p className="text-sm text-gray-700">Pick a time + pay your deposit. Your preferences load right after.</p>
                        </div>
                        <iframe
                          src={schedulingUrl}
                          title="Acuity Scheduling"
                          width="100%"
                          height="760"
                          frameBorder="0"
                          style={{ border: 0, minHeight: 760, width: "100%" }}
                          data-testid="step-payment-acuity-iframe"
                        />
                        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                          <button
                            onClick={() => setCurrentStep(getNextStep(6))}
                            className="text-xs text-gray-500 hover:text-gray-900 underline"
                            data-testid="step-payment-continue-after-book"
                          >
                            I've booked - continue to preferences
                          </button>
                          <button
                            onClick={() => setCurrentStep(getNextStep(6))}
                            className="text-xs text-gray-400 hover:text-gray-700 underline"
                            data-testid="step-payment-skip"
                          >
                            Skip - pay later
                          </button>
                        </div>
                      </div>
                    );
                  }

                  // Fallback when no Acuity URL is configured
                  return (
                    <div className="px-5 py-6 text-center">
                      <p className="text-sm text-gray-700 mb-3">Acuity isn't linked for this service yet. You can keep going and the host will follow up with payment.</p>
                      <button
                        onClick={() => setCurrentStep(getNextStep(6))}
                        className="text-xs text-gray-500 hover:text-gray-900 underline"
                        data-testid="step-payment-skip"
                      >
                        Continue to preferences
                      </button>
                    </div>
                  );
                })()}
              </div>
              <StepNav
                onBack={() => setCurrentStep(getPrevStep(6))}
                onNext={() => setCurrentStep(getNextStep(6))}
              />
            </div>
          </div>
        );

      case 5: // Confirmation - Clean Apple-Style Design
        // Iter 9.40 Slice I.h - modern provider confirmation (no Apple Wallet)
        if (isProviderFlow) {
          const conf = event?.confirmation_settings || {};
          const heroBg = event?.cover_image || event?.confirmation_background_url || conf.background_url;
          const logo = event?.logo_image || event?.host_logo_url;
          const linkedProducts = (selectedService?.linked_products || []).slice(0, 6);
          return (
            <div className="animate-fade-in pb-12" data-testid="step-confirmation-provider">
              {/* Hero header - background image + logo synced from Welcome */}
              <div
                className="relative w-full h-56 rounded-3xl overflow-hidden mb-6 shadow-lg"
                style={{
                  background: heroBg
                    ? `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%), url(${getImageUrl(heroBg)}) center/cover no-repeat`
                    : "linear-gradient(135deg,#1a1a1a 0%,#3a3a3a 100%)",
                }}
                data-testid="confirm-hero"
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  {logo ? (
                    <img src={getImageUrl(logo)} alt={event?.name} className="w-16 h-16 rounded-full border-2 border-white/80 object-cover mb-3 shadow-lg" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center mb-3">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                  )}
                  <h1 className="text-2xl font-bold text-white tracking-tight">{event?.name || "You're confirmed"}</h1>
                  <p className="text-sm text-white/85 mt-1">
                    {selectedService?.name || "Booking confirmed"}
                  </p>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="text-[10px] bg-emerald-500/95 text-white px-2.5 py-1 rounded-full font-semibold tracking-wide" data-testid="confirm-badge">
                    Confirmed
                  </span>
                </div>
              </div>

              {/* Booking summary card */}
              <div className="rounded-2xl border border-stone-200 bg-white shadow-sm p-5 mb-5" data-testid="confirm-booking-summary">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-medium mb-0.5">Guest</p>
                    <p className="font-medium text-stone-900">
                      {accountData.first_name && accountData.last_name ? `${accountData.first_name} ${accountData.last_name}` : accountData.name || "Guest"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-medium mb-0.5">Service</p>
                    <p className="font-medium text-stone-900 text-xs">{selectedService?.name || "TBD"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-medium mb-0.5">Duration</p>
                    <p className="font-medium text-stone-900 text-xs">
                      {selectedService?.duration_min ? `${selectedService.duration_min} min` : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-stone-400 font-medium mb-0.5">Total</p>
                    <p className="font-medium text-stone-900 text-xs">
                      {selectedService?.price ? `$${selectedService.price}` : "-"}
                    </p>
                  </div>
                </div>
              </div>

              {/* PRE-appointment block (host-set) - Iter 9.50+ R14
                  Now supports either a plain `pre_text` field OR a structured
                  `pre_links` array of {image_url,title,url,type} so the host
                  can attach polished link cards (prep guide, what to wear,
                  inspo board, deposit link, etc) with image + title + arrow. */}
              {(conf.pre_text || (conf.pre_links || []).length > 0) && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50 p-5 mb-5" data-testid="confirm-pre-block">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-2.5">Before your appointment</p>
                  {conf.pre_text && <p className="text-sm text-stone-700 whitespace-pre-line mb-3">{conf.pre_text}</p>}
                  {(conf.pre_links || []).length > 0 && (
                    <div className="space-y-2" data-testid="confirm-pre-links">
                      {(conf.pre_links || []).map((lnk, i) => (
                        <a
                          key={i}
                          href={lnk.url || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-all"
                          data-testid={`confirm-pre-link-${i}`}
                        >
                          <div className="w-12 h-12 rounded-lg bg-amber-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                            {lnk.image_url ? <img src={lnk.image_url} alt={lnk.title} className="w-full h-full object-cover" /> : <ChevronRight className="w-5 h-5 text-amber-500" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-stone-900 truncate">{lnk.title || "Open link"}</p>
                            {lnk.subtitle && <p className="text-[11px] text-stone-500 truncate">{lnk.subtitle}</p>}
                          </div>
                          <ExternalLink className="w-4 h-4 text-stone-400 flex-shrink-0" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* DURING-appointment block */}
              {conf.during_text && (
                <div className="rounded-2xl border border-purple-100 bg-purple-50 p-5 mb-5" data-testid="confirm-during-block">
                  <p className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold mb-1.5">During your appointment</p>
                  <p className="text-sm text-stone-700 whitespace-pre-line">{conf.during_text}</p>
                </div>
              )}

              {/* Product picker - linked products with click tracking */}
              {linkedProducts.length > 0 && (
                <div className="rounded-2xl border border-stone-200 bg-white p-5 mb-5" data-testid="confirm-products">
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-3">Products used in this look</p>
                  <div className="space-y-2.5">
                    {linkedProducts.map((p, i) => (
                      <a
                        key={i}
                        href={p.purchase_url || p.link || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/product-click`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              product_id: p.product_id || p.master_product_id || p.id,
                              host_id: event?.host_user_id,
                              source: "confirmation_page",
                            }),
                          }).catch(() => {});
                        }}
                        className="flex items-center gap-3 p-3 rounded-xl border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-all"
                        data-testid={`confirm-product-${i}`}
                      >
                        <div className="w-12 h-12 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                          {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-stone-900 truncate">{p.name || "Product"}</p>
                          <p className="text-xs text-stone-500 truncate">{p.brand}{p.shade ? ` · ${p.shade}` : ""}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-stone-300" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* POST-appointment block + Add Final Look - Iter 9.50+ R14
                  Also supports a structured `post_links` array (aftercare guide,
                  rebook link, leave a review, share your look, etc.). */}
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5 mb-5" data-testid="confirm-post-block">
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-2.5">After your appointment</p>
                {conf.post_text ? (
                  <p className="text-sm text-stone-700 whitespace-pre-line mb-3">{conf.post_text}</p>
                ) : (
                  <p className="text-sm text-stone-700 mb-3">Loved it? Drop a final-look photo so it lives in your Phygital makeup folder forever.</p>
                )}
                {(conf.post_links || []).length > 0 && (
                  <div className="space-y-2 mb-3" data-testid="confirm-post-links">
                    {(conf.post_links || []).map((lnk, i) => (
                      <a
                        key={i}
                        href={lnk.url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-300 hover:shadow-sm transition-all"
                        data-testid={`confirm-post-link-${i}`}
                      >
                        <div className="w-12 h-12 rounded-lg bg-emerald-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {lnk.image_url ? <img src={lnk.image_url} alt={lnk.title} className="w-full h-full object-cover" /> : <ChevronRight className="w-5 h-5 text-emerald-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-stone-900 truncate">{lnk.title || "Open link"}</p>
                          {lnk.subtitle && <p className="text-[11px] text-stone-500 truncate">{lnk.subtitle}</p>}
                        </div>
                        <ExternalLink className="w-4 h-4 text-stone-400 flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                )}
                <label className="block">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="final-look-upload"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("file", file);
                      fd.append("event_id", event?.event_id || "");
                      fd.append("user_id", user?.user_id || "");
                      fd.append("category", event?.pilot_category || "makeup");
                      fd.append("subfolder", "experiences");
                      try {
                        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/inspo/upload-final-look`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
                          body: fd,
                        });
                        if (!r.ok) throw new Error("upload failed");
                        toast.success("Final look saved · in your /me makeup → Experiences folder");
                      } catch (err) {
                        toast.error("Couldn't upload - try again?");
                      }
                    }}
                  />
                  <div className="cursor-pointer flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-emerald-200 text-emerald-700 font-medium hover:bg-emerald-100 transition-colors">
                    <Upload className="w-4 h-4" />
                    Add final look photo
                  </div>
                </label>
              </div>

              {/* Footer - minimal Phygital branding (no Apple Wallet button per spec) */}
              <div className="text-center mt-8 mb-2">
                <button
                  onClick={() => navigate("/me")}
                  className="text-sm text-stone-500 hover:text-stone-900 underline underline-offset-4"
                  data-testid="confirm-go-to-me"
                >
                  See this in your Phygital
                </button>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-stone-300">
                <PhygitalIcon className="w-3.5 h-3.5 opacity-60" />
                <span className="text-[10px]">Powered by Phygital</span>
              </div>
            </div>
          );
        }
        // Legacy event confirmation (Apple Wallet pass + QR) below - unchanged for non-provider flow.
        return (
          <div className="animate-fade-in">
            <EventHeader event={event} />
            
            {/* Success Message */}
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-4 shadow-sm border border-green-200">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-1">You're confirmed</h2>
              <p className="text-sm text-gray-500">
                {event?.date ? (() => {
                  const f = formatSafeDate(event.date, { weekday: "long", month: "long", day: "numeric" });
                  return f ? `See you ${f}` : "See you soon";
                })() : "See you soon"}
              </p>
            </div>
            
            {/* Digital Pass - Clean Card */}
            {digitalPass ? (
              <div className="mb-6">
                <div 
                  id="event-pass-card"
                  className="bg-white rounded-3xl p-6 mb-4 shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Pass Header */}
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Event Pass</p>
                      <p className="text-gray-900 font-semibold">{event?.name || "Event"}</p>
                    </div>
                    <span className="text-[10px] bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">
                      Confirmed
                    </span>
                  </div>
                  
                  {/* Event Info Grid */}
                  <div className="bg-gray-50 rounded-2xl p-4 mb-5">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Guest</p>
                        <p className="text-gray-900 font-medium">{accountData.first_name && accountData.last_name ? `${accountData.first_name} ${accountData.last_name}` : accountData.name || "Guest"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Date</p>
                        <p className="text-gray-900 font-medium">
                          {formatSafeDate(event?.date) || "TBA"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Time</p>
                        <p className="text-gray-900 font-medium">
                          {event?.start_time ? formatTime12Hour(event.start_time, getTimezoneAbbr(event?.timezone || event?.timezone_display)) : event?.time ? formatTime12Hour(event.time, getTimezoneAbbr(event?.timezone || event?.timezone_display)) : "TBA"}
                          {event?.end_time && ` - ${formatTime12Hour(event.end_time)}`}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Venue</p>
                        <p className="text-gray-900 font-medium text-xs">
                          {serviceSettings.location_visibility === "hidden" 
                            ? "Released upon confirmation" 
                            : (event?.venue_name || event?.location || "TBA")}
                        </p>
                      </div>
                      {selectedService && (
                        <div>
                          <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Service</p>
                          <p className="text-gray-900 font-medium text-xs">{selectedService.name}</p>
                        </div>
                      )}
                      {linkedExperiences.length > 0 && (
                        <div className="col-span-2">
                          <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Linked Experiences</p>
                          <p className="text-gray-900 font-medium text-xs">
                            {linkedExperiences.map(e => e.category).join(", ")}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* QR Code - Clean */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-3 rounded-xl border border-gray-100">
                      <img 
                        src={digitalPass.qr_code} 
                        alt="Event Pass QR Code" 
                        className="w-32 h-32"
                      />
                    </div>
                  </div>
                  <p className="text-gray-400 font-mono text-[10px] text-center mb-3">{digitalPass.pass_token}</p>
                  
                  {/* Phygital Branding */}
                  <div className="flex items-center justify-center gap-1.5 text-gray-300">
                    <PhygitalIcon className="w-3.5 h-3.5 opacity-60" />
                    <span className="text-[10px]">Phygital</span>
                  </div>
                </div>
                
                <p className="text-xs text-gray-400 text-center mb-5">
                  Show this pass at entry
                </p>
                
                {/* Action Buttons - Clean Grid */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <Button 
                    onClick={() => {
                      const passCard = document.getElementById('event-pass-card');
                      if (passCard) {
                        import('html2canvas').then(({ default: html2canvas }) => {
                          html2canvas(passCard, { backgroundColor: '#fff', scale: 2 }).then(canvas => {
                            const link = document.createElement('a');
                            link.href = canvas.toDataURL('image/png');
                            link.download = `${event?.slug || 'event'}-pass.png`;
                            link.click();
                          });
                        }).catch(() => {
                          const link = document.createElement('a');
                          link.href = digitalPass.qr_code;
                          link.download = `pass-${digitalPass.pass_token}.png`;
                          link.click();
                        });
                      }
                    }}
                    variant="outline"
                    className="text-xs py-2.5 rounded-xl border-gray-200 hover:bg-gray-50"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Save Pass
                  </Button>
                  <Button 
                    onClick={copyPassLink}
                    variant="outline"
                    className="text-xs py-2.5 rounded-xl border-gray-200 hover:bg-gray-50"
                  >
                    {linkCopied ? <Check className="w-3.5 h-3.5 mr-1.5 text-green-600" /> : <ArrowUp className="w-3.5 h-3.5 mr-1.5" />}
                    {linkCopied ? "Copied!" : "Share Link"}
                  </Button>
                </div>
                
                {/* Calendar */}
                <div className="mb-6">
                  <Button 
                    onClick={() => setShowCalendarOptions(true)}
                    variant="outline"
                    className="w-full border-gray-200 py-3 rounded-xl text-xs hover:bg-gray-50"
                    data-testid="add-to-calendar-btn"
                  >
                    <CalendarPlus className="w-4 h-4 mr-1.5" />
                    Add to Calendar
                  </Button>
                </div>
              </div>
            ) : generatingPass ? (
              <div className="bg-gray-50 rounded-2xl p-8 mb-6 text-center">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">Generating your pass...</p>
              </div>
            ) : (
              <Button 
                onClick={generateDigitalPass}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white mb-6 py-4 rounded-xl"
                data-testid="get-pass-btn"
              >
                <QrCode className="w-4 h-4 mr-2" />
                Get Your Digital Pass
              </Button>
            )}
            
            {/* Tap In Playlist  - Always shown on confirmation. Opting in during RSVP tags Beehiiv for reminders. */}
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-4 mb-4 border border-violet-100" data-testid="confirmation-tapin-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                  <img src={TAP_IN_COVER} alt="Tap In" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 text-sm mb-0.5">Tap In</h3>
                  <p className="text-xs text-gray-500">Add your song to the session playlist</p>
                  {event?.tap_in_invite_link || event?.playlist_link ? (
                    <a
                      href={event.tap_in_invite_link || event.playlist_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-violet-600 text-white text-xs font-medium rounded-full hover:bg-violet-700 transition-colors"
                      data-testid="tapin-playlist-link"
                    >
                      <Music className="w-3 h-3" />
                      Open Playlist
                    </a>
                  ) : (
                    <div className="flex items-center gap-2 mt-2 text-xs text-violet-600">
                      <Mail className="w-3 h-3" />
                      <span>Playlist link coming soon</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* ============ EVENT RESOURCES ============ */}
            {publicResources.length > 0 && (
              <div className="mb-6 mt-6" data-testid="confirmation-resources">
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 text-center">
                  Event Resources
                </h3>
                <div className="space-y-2">
                  {publicResources.map((resource) => {
                    const catLabel = resource.category === "pre_event" ? "Pre-Event" : resource.category === "check_in" ? "Check-In" : "Post-Survey";
                    const catColor = resource.category === "pre_event" ? "blue" : resource.category === "check_in" ? "green" : "purple";
                    return (
                      <a
                        key={resource.resource_id}
                        href={resource.link || "#"}
                        target={resource.link ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-colors group"
                        data-testid={`resource-${resource.resource_id}`}
                      >
                        {resource.image_url ? (
                          <img
                            src={resource.image_url.startsWith('/') ? `${API.replace('/api','')}${resource.image_url}` : resource.image_url}
                            alt=""
                            className="w-14 h-10 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                          />
                        ) : (
                          <div className={`w-14 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-${catColor}-50 border border-${catColor}-100`}>
                            <Link2 className={`w-4 h-4 text-${catColor}-500`} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h4 className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600 transition-colors">{resource.title}</h4>
                            <span className={`px-1.5 py-0.5 text-[8px] font-medium rounded-full bg-${catColor}-50 text-${catColor}-600`}>{catLabel}</span>
                          </div>
                          {resource.description && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{resource.description}</p>
                          )}
                        </div>
                        {resource.link && <ExternalLink className="w-3.5 h-3.5 text-gray-300 flex-shrink-0 mt-0.5" />}
                      </a>
                    );
                  })}
                </div>
                
                {/* Post-Survey Gate Message */}
                {resourceAvailability.post_survey && !publicResources.some(r => r.category === 'post_survey') && (
                  <div className="mt-3 p-3 rounded-xl bg-purple-50 border border-purple-100 text-center">
                    <p className="text-xs text-purple-700 font-medium">More resources available after completing the post-event survey</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Event Partners / Hosts Section - Dynamic from event.partners, controlled by settings */}
            {partners.length > 0 && (
              <>
                {/* Hosted By - Partners marked as is_host - Controlled by show_hosted_by */}
                {event?.show_hosted_by !== false && partners.filter(p => (p.is_host || p.type === 'host') && p.show_in_hosted_by !== false).length > 0 && (
                  <div className="mb-6 mt-8 pt-6 border-t border-gray-100">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3 text-center">Hosted By</h3>
                    <div className="flex flex-wrap justify-center items-center gap-2">
                      {partners.filter(p => (p.is_host || p.type === 'host') && p.show_in_hosted_by !== false).map((partner, idx, arr) => (
                        <span key={partner.partner_id || partner.id} className="inline-flex items-center">
                          {partner.website || partner.cta_url ? (
                            <a 
                              href={ensureAbsoluteUrl(partner.website || partner.cta_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={async (e) => {
                                const partnerId = partner.partner_id || partner.org_id || partner.id;
                                const destUrl = partner.website || partner.cta_url;
                                if (partnerId && destUrl) {
                                  try {
                                    await fetch(`${API}/events/${event?.event_id}/partners/${partnerId}/cta-click`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ destination_url: destUrl, cta_type: 'host' })
                                    });
                                  } catch (err) { console.log('CTA tracking:', err); }
                                }
                              }}
                              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors underline"
                            >
                              {partner.name}
                            </a>
                          ) : (
                            <span className="text-sm font-medium text-gray-700">{partner.name}</span>
                          )}
                          {idx < arr.length - 1 && <span className="text-gray-400 px-2">&</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Event Partners - Non-host partners - Controlled by show_partners */}
                {event?.show_partners !== false && partners.filter(p => !p.is_host && p.type !== 'host' && p.show_in_hosted_by !== false).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Event Partners</h3>
                    <div className="space-y-2">
                      {partners.filter(p => !p.is_host && p.type !== 'host' && p.show_in_hosted_by !== false).map((partner) => (
                        <a 
                          key={partner.partner_id || partner.id}
                          href={ensureAbsoluteUrl(partner.cta_url || partner.website) || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={async (e) => {
                            // Track partner CTA click
                            const partnerId = partner.partner_id || partner.org_id || partner.id;
                            const destUrl = partner.cta_url || partner.website;
                            if (partnerId && destUrl) {
                              try {
                                await fetch(`${API}/events/${event?.event_id}/partners/${partnerId}/cta-click`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ destination_url: destUrl, cta_type: 'partner' })
                                });
                              } catch (err) { console.log('CTA tracking:', err); }
                            }
                          }}
                          className={`flex items-center gap-3 rounded-xl p-4 border hover:shadow-sm transition-all group ${
                            partner.type === 'venue' 
                              ? 'bg-gradient-to-r from-sky-50 to-indigo-50 border-sky-100 hover:border-sky-200' 
                              : 'bg-white border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          {partner.logo || partner.logo_url ? (
                            <img 
                              src={partner.logo || partner.logo_url} 
                              alt={partner.name} 
                              className="w-11 h-11 rounded-xl object-contain bg-white"
                            />
                          ) : (
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              partner.type === 'venue' 
                                ? 'bg-gradient-to-br from-sky-400 to-indigo-500' 
                                : partner.type === 'sponsor'
                                  ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                                  : 'bg-gray-200'
                            }`}>
                              {partner.type === 'venue' ? (
                                <Building2 className="w-5 h-5 text-white" />
                              ) : (
                                <span className="text-white font-bold text-sm">{partner.name?.charAt(0)?.toUpperCase()}</span>
                              )}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm">{partner.name}</p>
                            {partner.description && <p className="text-xs text-gray-500">{partner.description}</p>}
                          </div>
                          {partner.button_text ? (
                            <span className={`text-xs font-medium ${partner.type === 'venue' ? 'text-sky-600 group-hover:text-sky-700' : 'text-gray-600 group-hover:text-gray-700'}`}>
                              {partner.button_text}
                            </span>
                          ) : (
                            <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            
            {/* Dynamic Hosted By - No hardcoded fallbacks, orgs come from event team data */}
            {/* Partners section above already handles displaying hosts from team */}
            
            {/* Event Website Link */}
            {event?.external_website_url && (
              <div className="text-center mb-6">
                <a 
                  href={ensureAbsoluteUrl(event.external_website_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  {event?.learn_more_text || 'View full event details'}
                </a>
              </div>
            )}
            
            {/* Extra padding at bottom */}
            <div className="pb-8" />
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 font-sans pb-20">
      {/* Desktop: Centered card with max-width, Mobile: Full width */}
      <div className="max-w-md mx-auto px-6 py-8 pt-12 md:max-w-xl md:py-16 md:pt-20 lg:max-w-2xl xl:max-w-3xl">
        {/* Progress indicator - Iter 9.40 Slice I.g (now also shows step 6 = Payment) */}
        {(currentStep >= 1 && currentStep <= 5) || currentStep === 6 ? (
          <div className="flex gap-1 mb-6 md:mb-8" data-testid="step-progress">
            {(() => {
              const enabled = (typeof getEnabledSteps === "function") ? getEnabledSteps() : [1, 2, 3, 4, 5];
              const showSteps = enabled.filter((s) => s !== 0); // hide welcome from progress
              const idx = showSteps.indexOf(currentStep);
              return showSteps.map((s, i) => (
                <div
                  key={s}
                  className={`flex-1 h-1 md:h-1.5 rounded-full transition-all ${
                    i <= idx ? 'bg-gray-900' : 'bg-gray-200'
                  }`}
                />
              ));
            })()}
          </div>
        ) : null}
        
        {renderStep()}
      </div>
      
      {/* Tap In Playlist Preview Modal */}
      {showTapInPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowTapInPreview(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            <div className="text-center mb-6">
              <div className="w-24 h-24 rounded-2xl overflow-hidden mx-auto mb-3 shadow-lg">
                <img 
                  src={TAP_IN_COVER} 
                  alt="Tap In" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-semibold">{event?.custom_texts?.playlist_title || `${event?.name || 'Event'} Playlist`}</h3>
              <p className="text-sm text-gray-600">Powered by Tap In</p>
            </div>
            
            {/* Mock playlist tracks */}
            <div className="space-y-3 mb-6">
              {["Opening Set", "Runway Mix", "Networking Hour", "After Party"].map((track, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                    <Play className="w-4 h-4 text-white" fill="currentColor" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{track}</p>
                    <p className="text-xs text-gray-600">{event?.custom_texts?.playlist_subtitle || `Curated by ${event?.host_name || 'the host'}`}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Button className="w-full bg-gray-900 text-white py-5 rounded-full">
              Download Tap In App
            </Button>
            <button 
              onClick={() => setShowTapInPreview(false)}
              className="w-full text-center text-gray-600 text-sm mt-3"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
      
      {/* Venue Preview Modal - Shows what venue sees with detailed preferences */}
      {showVenuePreview && digitalPass && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowVenuePreview(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-slide-up max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Your Attendee Card</h3>
                <p className="text-xs text-gray-600">What the venue will see</p>
              </div>
            </div>
            
            {/* Attendee Card Preview - Purple Gradient Style */}
            <div className="relative rounded-2xl overflow-hidden mb-4">
              <div 
                className="p-5 text-white"
                style={{ 
                  background: 'linear-gradient(135deg, rgb(139, 92, 246), rgb(147, 51, 234))'
                }}
              >
                {/* Event header with badge */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-black shadow-sm flex-shrink-0">
                    {event?.cover_image ? (
                      <img 
                        src={event.cover_image.startsWith('/') ? `${API}${event.cover_image}` : event.cover_image} 
                        alt={event?.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full bg-white/20 flex items-center justify-center">
                        <PartyPopper className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-white/20 text-white text-[9px] mb-1" data-testid="card-label">
                      {event?.display_settings?.card_label || event?.inherited_display_settings?.card_label || "PREFERENCE CARD"}
                    </Badge>
                    <h3 className="font-bold text-sm truncate">{event?.name || 'Event'}</h3>
                  </div>
                </div>
                
                {/* User info with photo */}
                <div className="bg-white/10 rounded-xl p-3 mb-3">
                  <div className="flex items-center gap-3">
                    {user?.profile_photo_url ? (
                      <img src={user.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white/30" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-semibold">
                        {accountData.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div>
                      <span className="font-semibold text-sm">{accountData.name || user?.name || 'Guest'}</span>
                      {event?.host_user_id === user?.user_id && (
                        <Badge className="ml-2 bg-violet-400/30 text-white text-[8px]">Host</Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Preferences Summary - Human Readable */}
                <div className="space-y-2">
                  {/* Dietary */}
                  {preferences.dietary_restrictions?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/60 uppercase mb-1">Dietary</p>
                      <div className="flex flex-wrap gap-1">
                        {preferences.dietary_restrictions.map((d, i) => (
                          <span key={i} className="text-xs bg-green-400/30 px-2 py-0.5 rounded-full">
                            {d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Invisible Disabilities / Support Needs */}
                  {preferences.invisible_disabilities?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/60 uppercase mb-1">Invisible Disabilities</p>
                      <div className="space-y-1">
                        {preferences.invisible_disabilities.map((d, i) => {
                          const labels = {
                            vocal_tics: { label: "Vocal Tics", desc: "May have involuntary vocal expressions" },
                            motor_tics: { label: "Motor Tics", desc: "May have involuntary motor movements" },
                            tourettes: { label: "Tourette's Syndrome", desc: "Has Tourette's Syndrome" },
                            anxiety: { label: "Anxiety", desc: "Experiences anxiety in certain situations" },
                            chronic_pain: { label: "Chronic Pain", desc: "Lives with chronic pain conditions" },
                            chronic_fatigue: { label: "Chronic Fatigue", desc: "Experiences chronic fatigue" },
                            adhd: { label: "ADHD", desc: "Has attention deficit hyperactivity disorder" },
                            autism: { label: "Autism Spectrum", desc: "On the autism spectrum" }
                          };
                          const info = labels[d] || { label: d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), desc: '' };
                          return (
                            <div key={i} className="bg-white/10 rounded-lg px-2 py-1.5">
                              <span className="text-xs font-medium">{info.label}</span>
                              {info.desc && <p className="text-[10px] text-white/60">{info.desc}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Interests */}
                  {preferences.interests?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/60 uppercase mb-1">Interests</p>
                      <div className="flex flex-wrap gap-1">
                        {preferences.interests.map((interest, i) => (
                          <span key={i} className="text-xs bg-blue-400/30 px-2 py-0.5 rounded-full">
                            {interest}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Seating */}
                  {preferences.seating_preference && (
                    <div>
                      <p className="text-[10px] text-white/60 uppercase mb-1">Seating</p>
                      <span className="text-xs bg-amber-400/30 px-2 py-0.5 rounded-full">
                        {preferences.seating_preference.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  )}
                  
                  {/* Accessibility */}
                  {preferences.accessibility_needs?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/60 uppercase mb-1">Accessibility</p>
                      <div className="flex flex-wrap gap-1">
                        {preferences.accessibility_needs.map((a, i) => (
                          <span key={i} className="text-xs bg-teal-400/30 px-2 py-0.5 rounded-full">
                            {a.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center mb-4">
              This is what the venue sees
            </p>
            
            {/* Action buttons */}
            <div className="flex gap-2 mb-2">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowVenuePreview(false);
                  setCurrentStep(1);
                }}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit Preferences
              </Button>
              <Button 
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => {
                  // TODO: Cancel RSVP flow
                  toast.info("Cancel RSVP feature coming soon");
                }}
              >
                Can&apos;t Make It
              </Button>
            </div>
            
            <Button 
              onClick={() => setShowVenuePreview(false)}
              className="w-full bg-gray-900 text-white py-4 rounded-full"
            >
              Done
            </Button>
          </div>
        </div>
      )}
      
      {/* Calendar Options Modal */}
      {showCalendarOptions && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowCalendarOptions(false)}>
          <div className="bg-white rounded-t-3xl w-full max-w-md p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
            
            <h3 className="text-lg font-semibold text-center mb-4">Add to Calendar</h3>
            
            <div className="space-y-2">
              {/* Google Calendar */}
              <button
                onClick={() => {
                  const googleUrl = getGoogleCalendarUrl();
                  if (googleUrl) {
                    window.open(googleUrl, '_blank');
                  } else {
                    toast.error("Event date/time not available");
                  }
                  setShowCalendarOptions(false);
                }}
                className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                data-testid="calendar-google"
              >
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Google Calendar</p>
                  <p className="text-xs text-gray-600">Add to your Google Calendar</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </button>
              
              {/* Outlook/Microsoft */}
              <button
                onClick={() => {
                  const outlookUrl = getOutlookCalendarUrl();
                  if (outlookUrl) {
                    window.open(outlookUrl, '_blank');
                  } else {
                    toast.error("Event date/time not available");
                  }
                  setShowCalendarOptions(false);
                }}
                className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                data-testid="calendar-outlook"
              >
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#0078D4" d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Microsoft Outlook</p>
                  <p className="text-xs text-gray-600">Add to your Outlook calendar</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </button>
              
              {/* Apple Calendar (iCal download) */}
              <button
                onClick={() => {
                  addToCalendar();  // Downloads .ics file
                  setShowCalendarOptions(false);
                }}
                className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                data-testid="calendar-apple"
              >
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#000" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-gray-900">Apple Calendar</p>
                  <p className="text-xs text-gray-600">Download .ics file</p>
                </div>
                <Download className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            
            <button
              onClick={() => setShowCalendarOptions(false)}
              className="w-full text-center text-gray-600 text-sm mt-4 py-2 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Footer - with better visibility, subtle login link, and no overlap */}
      <footer className="fixed bottom-0 left-0 right-0 py-4 text-center bg-white/95 backdrop-blur-sm border-t border-gray-100">
        <div className="flex items-center justify-center gap-4">
          <a 
            href="https://phygital.systems" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <PhygitalIcon className="w-4 h-4" />
            <span className="text-xs">Powered by Phygital</span>
          </a>
          <span className="text-gray-300">•</span>
          <span className="text-xs text-gray-500">phygital.systems</span>
        </div>
      </footer>
      
      {/* VIP List Capacity Modal */}
      <Dialog open={showCapacityModal} onOpenChange={setShowCapacityModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              VIP List is Full
            </DialogTitle>
            <DialogDescription>
              This VIP list has reached its maximum capacity. You can request additional spots from the host.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              {capacityError?.message || "The VIP list you're trying to join is currently at capacity."}
            </p>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>What happens next?</strong> If you request additional spots, the host will be notified and can approve your request. You'll receive an email when a spot becomes available.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCapacityModal(false);
                setCapacityError(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRequestExtraInvites}
              disabled={requestingExtraInvites}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {requestingExtraInvites ? "Sending..." : "Request Spot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preference Card Modal - parity with ConfirmationPage */}
      {existingRsvp && (
        <UnifiedPreferenceCard
          person={{
            ...existingRsvp,
            profile_image: user?.profile_picture || user?.profile_image || user?.picture || existingRsvp?.profile_image,
            picture: user?.profile_picture || user?.profile_image || user?.picture || existingRsvp?.profile_image,
            dietary_restrictions: preferences?.dietary_restrictions || [],
            meat_preferences: preferences?.meat_preferences || [],
            allergies: preferences?.allergies || [],
            dietary_notes: preferences?.dietary_notes,
            accessibility_needs: preferences?.accessibility_needs || [],
            accessibility_notes: preferences?.accessibility_notes,
            invisible_disabilities: preferences?.invisible_disabilities || [],
            preferences: {
              ...preferences,
              meat_preferences: preferences?.meat_preferences || [],
              allergies: preferences?.allergies || [],
              interest_preferences: preferences?.interests || [],
              vibe: preferences?.vibe
            }
          }}
          event={{
            ...event,
            cover_image: event?.cover_image || event?.logo
          }}
          open={showPreferenceCard}
          onOpenChange={setShowPreferenceCard}
          eventId={event?.event_id}
        />
      )}
    </div>
  );
};

// Step Header Component - shows host identity + selected service on every step
const StepHeader = ({ step, total, title, subtitle, event, selectedService }) => (
  <div className="mb-3">
    {/* Host identity bar */}
    {event && (event.name || event.logo_image) && (
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
          {event.logo_image ? (
            <img src={event.logo_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-xs font-bold text-gray-400">{((event.title || event.name || "P"))[0]}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-700 truncate">{event.title || event.name}</p>
          {selectedService && (
            <p className="text-[10px] text-purple-600 truncate">{selectedService.name}</p>
          )}
        </div>
      </div>
    )}
    <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Step {step} of {total}</p>
    <h2 className="text-lg font-semibold text-gray-900 mb-0">{title}</h2>
    <p className="text-xs text-gray-600">{subtitle}</p>
    {event && (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-gray-400">
        {formatSafeDate(event.date) && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatSafeDate(event.date)}
          </span>
        )}
        {event.start_time && (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}
          </span>
        )}
      </div>
    )}
  </div>
);

// Step Navigation Component
const StepNav = ({ onBack, onNext, nextLabel = "Next" }) => {
  const handleNext = () => {
    if (!onNext) return;
    // Scroll to top when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onNext();
  };
  
  const handleBack = () => {
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onBack();
  };
  
  return (
    <div className="flex gap-3 mt-4">
      {onBack && (
        <Button 
          variant="outline" 
          onClick={handleBack}
          className="flex-1 py-4 rounded-full border-gray-200"
          data-testid="step-back"
        >
          Back
        </Button>
      )}
      <Button 
        onClick={handleNext}
        disabled={!onNext}
        className="flex-1 bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-full disabled:opacity-50"
        data-testid="step-next"
      >
        {nextLabel}
        <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
};

// Add animations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fade-in {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slide-up {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    .animate-fade-in {
      animation: fade-in 0.3s ease-out;
    }
    .animate-slide-up {
      animation: slide-up 0.3s ease-out;
    }
  `;
  if (!document.head.querySelector('[data-phygital-animations]')) {
    style.setAttribute('data-phygital-animations', 'true');
    document.head.appendChild(style);
  }
}

export default EventPageElegant;
